import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import type {
  ClassificationOutput,
  CodeAssignment,
  DimensionsReport,
  DoorWallsOutput,
  ExtractedTextLabel,
  HistorySnapshot,
  MappeCrossPlanData,
  MultiagentRunMeta,
  MultiagentRunWohneinheit,
  PerRoomInlineTextLite,
  PlanAnnotationsLite,
  PolygonCollection,
  PolygonFeature,
  PrimitivesOutput,
  QuantitiesOutput,
  QuantitiesV2Output,
  RoomsOutput,
  RunIndex,
  UserWall,
} from "../multiagent-types";
import type {
  CodeAssignmentSource,
  CodeAssignmentStatus,
  Fachplaner,
  NachweisStatus,
  CatalogEntry,
  CodeAssignment as CodeAssignmentType,
} from "@bidi-takeoff/shared/gewerk-catalog";
import {
  DEFAULT_CATALOG,
  getCatalogEntryByCode,
} from "@bidi-takeoff/shared/gewerk-catalog";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { ProRaumUebersichtSection } from "../components/ProRaumUebersicht";
import {
  mergePolygons,
  splitPolygon,
  suggestSplitFromAnchors,
  type Ring,
} from "../multiagent-split";
import ScaleSetter from "../components/ScaleSetter";

// Sprint Atelier (2026-05-02) — Inline-Icons für die Topbar / Sub-Bar.
// Klein gehalten weil wir keine Icon-Bibliothek einziehen wollen.
function FolderSm({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    </svg>
  );
}
function ChevRSm({
  size = 12,
  style,
}: {
  size?: number;
  style?: React.CSSProperties;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
function CheckSm({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12l4 4 10-10" />
    </svg>
  );
}

// Atelier-Style icon-Button für die Polygon-Aktionen (Teilen / Mergen /
// Vertices / Löschen). Tönen die Hover-/Background-Farbe leicht in eine
// semantische Richtung (amber/violet/blue/red), bleiben aber zurückhaltend.
function atelierIconBtn(
  tone: "amber" | "violet" | "blue" | "red"
): React.CSSProperties {
  const colorMap: Record<string, [string, string]> = {
    amber: [
      "color-mix(in oklab, oklch(0.72 0.15 60) 16%, transparent)",
      "oklch(0.50 0.16 50)",
    ],
    violet: [
      "color-mix(in oklab, oklch(0.62 0.18 290) 14%, transparent)",
      "oklch(0.45 0.18 290)",
    ],
    blue: [
      "color-mix(in oklab, oklch(0.65 0.16 240) 14%, transparent)",
      "oklch(0.45 0.18 240)",
    ],
    red: [
      "color-mix(in oklab, oklch(0.65 0.18 25) 14%, transparent)",
      "oklch(0.45 0.20 25)",
    ],
  };
  const [bg, fg] = colorMap[tone];
  return {
    width: 26,
    height: 26,
    borderRadius: 5,
    border: "none",
    cursor: "pointer",
    background: bg,
    color: fg,
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };
}

// Sprint Inline-OCR (2026-05-06) — Pille für strukturierte Inline-Notizen
// im Inspector. Mono-Font + dezenter ember-Hintergrund.
const inlineNotePill: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--bd-mono)",
  padding: "2px 7px",
  borderRadius: 4,
  background: "color-mix(in oklab, var(--bd-ember) 10%, transparent)",
  color: "var(--bd-ember-strong)",
  border: "1px solid color-mix(in oklab, var(--bd-ember) 25%, transparent)",
  whiteSpace: "nowrap",
};

interface BundledRun {
  index: RunIndex;
  classification: ClassificationOutput;
  polygons: PolygonCollection;
  dimensions: DimensionsReport | null;
  rooms: RoomsOutput | null;
  doorWalls: DoorWallsOutput | null;
  primitives: PrimitivesOutput | null;
  userWalls: UserWall[];
  /** Sprint Inline-OCR (2026-05-06) — perRoomInlineText für Inspector. */
  annotations: PlanAnnotationsLite | null;
}

// Sprint A.8 — UI-Konstanten + Farb-Helper für CodeAssignments.
const CODE_ASSIGNMENT_STATUSES: CodeAssignmentStatus[] = [
  "entwurf",
  "in-pruefung",
  "freigegeben",
  "blockiert",
];
const CODE_ASSIGNMENT_STATUS_LABELS: Record<CodeAssignmentStatus, string> = {
  entwurf: "Entwurf",
  "in-pruefung": "In Prüfung",
  freigegeben: "Freigegeben",
  blockiert: "Blockiert",
};
const CODE_ASSIGNMENT_STATUS_CLASSES: Record<CodeAssignmentStatus, string> = {
  entwurf: "bg-slate-100 text-slate-700 border-slate-200",
  "in-pruefung": "bg-amber-100 text-amber-800 border-amber-200",
  freigegeben: "bg-emerald-100 text-emerald-800 border-emerald-200",
  blockiert: "bg-rose-100 text-rose-800 border-rose-200",
};
const CODE_ASSIGNMENT_SOURCE_LABELS: Record<CodeAssignmentSource, string> = {
  "user-manual": "manuell",
  "auto-stempel": "Stempel",
  "auto-inline-text": "Inline-Text",
  "auto-legend": "Legende",
  "auto-roomtype": "Raumtyp",
  "auto-schnitt": "Schnitt",
  "auto-thickness": "Wandstärke",
  "office-profile": "Profil",
};
const CODE_ASSIGNMENT_SOURCE_CLASSES: Record<CodeAssignmentSource, string> = {
  "user-manual": "bg-blue-50 text-blue-700 border-blue-200",
  "auto-stempel": "bg-violet-50 text-violet-700 border-violet-200",
  "auto-inline-text": "bg-violet-50 text-violet-700 border-violet-200",
  "auto-legend": "bg-violet-50 text-violet-700 border-violet-200",
  "auto-roomtype": "bg-violet-50 text-violet-700 border-violet-200",
  "auto-schnitt": "bg-violet-50 text-violet-700 border-violet-200",
  "auto-thickness": "bg-violet-50 text-violet-700 border-violet-200",
  "office-profile": "bg-orange-50 text-orange-700 border-orange-200",
};
const NACHWEIS_STATES: Array<NachweisStatus["status"]> = [
  "fehlt",
  "angefordert",
  "vorliegt",
  "geprueft",
  "nicht-erforderlich",
];
const NACHWEIS_LABELS: Record<NachweisStatus["status"], string> = {
  fehlt: "fehlt",
  angefordert: "angefordert",
  vorliegt: "vorliegt",
  geprueft: "geprüft",
  "nicht-erforderlich": "n/a",
};
const NACHWEIS_CLASSES: Record<NachweisStatus["status"], string> = {
  fehlt: "bg-rose-50 text-rose-700",
  angefordert: "bg-amber-50 text-amber-700",
  vorliegt: "bg-blue-50 text-blue-700",
  geprueft: "bg-emerald-50 text-emerald-700",
  "nicht-erforderlich": "bg-slate-50 text-slate-500",
};
const ALL_FACHPLANER: Fachplaner[] = [
  "architekt",
  "tragwerk",
  "bauphysik",
  "brandschutz",
  "schallschutz",
  "tga-sanitaer",
  "tga-heizung",
  "tga-lueftung",
  "elektroplaner",
  "abdichtungsplaner",
  "fassadenplaner",
  "dachplaner",
  "landschaftsarchitekt",
  "geotechniker",
  "akustikplaner",
  "innenarchitekt",
  "schadstoffgutachter",
  "aufzugsplaner",
  "sicherheitsplaner",
  "energieberater",
];
const FACHPLANER_LABELS: Record<Fachplaner, string> = {
  architekt: "Architekt",
  tragwerk: "Tragwerksplaner",
  bauphysik: "Bauphysik",
  brandschutz: "Brandschutz",
  schallschutz: "Schallschutz",
  "tga-sanitaer": "TGA Sanitär",
  "tga-heizung": "TGA Heizung",
  "tga-lueftung": "TGA Lüftung",
  elektroplaner: "Elektro",
  abdichtungsplaner: "Abdichtung",
  fassadenplaner: "Fassade",
  dachplaner: "Dach",
  landschaftsarchitekt: "Landschaft",
  geotechniker: "Geotechnik",
  akustikplaner: "Akustik",
  innenarchitekt: "Innenarchitekt",
  schadstoffgutachter: "Schadstoff",
  aufzugsplaner: "Aufzug",
  sicherheitsplaner: "Sicherheit",
  energieberater: "Energieberater",
};

/**
 * Aggregiert den Status einer Code-Liste zu einem Sammel-Status für Badges.
 * Vorrang: blockiert > in-pruefung > entwurf > freigegeben (aufmerksamkeit-
 * heischender Status gewinnt). Leere Liste → null.
 */
function aggregateCodeAssignmentStatus(
  list: CodeAssignment[]
): CodeAssignmentStatus | null {
  if (list.length === 0) return null;
  if (list.some((ca) => ca.status === "blockiert")) return "blockiert";
  if (list.some((ca) => ca.status === "in-pruefung")) return "in-pruefung";
  if (list.some((ca) => ca.status === "entwurf")) return "entwurf";
  return "freigegeben";
}

async function loadBundle(runId: string): Promise<BundledRun> {
  const index = await api.multiagentRunIndex(runId);
  // Sprint P (2026-05-04) — Vision-Plan-Typen (Schnitt/Ansicht-Fassade) haben
  // kein 03-polygons.geojson. Statt 404 zu werfen liefern wir eine leere
  // FeatureCollection — die Page rendert dann ein Plan-View ohne Overlays.
  const polygonsP = "03-polygons.geojson" in index.files
    ? api.multiagentPolygons(runId)
    : Promise.resolve({ type: "FeatureCollection", features: [], wallCatalog: {} } as unknown as PolygonCollection);
  const [
    classification,
    polygons,
    dimensions,
    rooms,
    doorWalls,
    primitives,
    userWallsResp,
    annotations,
  ] = await Promise.all([
    api.multiagentClassification(runId),
    polygonsP,
    "03-dimensions-report.json" in index.files
      ? api.multiagentDimensionsReport(runId)
      : Promise.resolve(null),
    "04-rooms.json" in index.files
      ? api.multiagentRooms(runId)
      : Promise.resolve(null),
    api.multiagentDoorWalls(runId).catch(() => null),
    "02-primitives.json" in index.files
      ? api.multiagentPrimitives(runId).catch(() => null)
      : Promise.resolve(null),
    api.multiagentUserWalls(runId).catch(() => ({ userWalls: [] })),
    "00-plan-annotations.json" in index.files
      ? api.multiagentAnnotations(runId).catch(() => null)
      : Promise.resolve(null),
  ]);
  return {
    index,
    classification,
    polygons,
    dimensions,
    rooms,
    doorWalls,
    primitives,
    userWalls: userWallsResp.userWalls,
    annotations,
  };
}

/**
 * Sprint 0.19 / Punkt 4 — System-Wand-Presets nach DIN 4172 (Maßordnung im
 * Hochbau, oktametrisches System, Rastermaß 12.5cm = Stein 11.5cm + Mörtelfuge 1cm).
 *
 * In der Entwurfs-/Genehmigungsplanung (1:100) wird oft nur die Gesamtdicke
 * angegeben (z.B. „49"). Der Material-Editor schlägt dann anhand des Plan-Maßes
 * den typischen Aufbau vor.
 */
interface SystemWallLayer {
  gewerk: "rohbau" | "putz" | "trockenbau";
  preset: "mauerwerk" | "trockenbau" | "beton" | "custom";
  materialName: string;
  dickeMm: number;
  putzSides?: 1 | 2;
}
interface SystemWallPreset {
  id: string;
  label: string;
  totalMm: number;
  description: string;
  norm?: string;
  layers: SystemWallLayer[];
}
const SYSTEM_WALL_PRESETS: SystemWallPreset[] = [
  // 49 cm — zweischaliges Außenmauerwerk mit Klinker (Standard-EFH)
  {
    id: "aw49a",
    label: "Außenwand 49cm — Variante A (massive Tragwand)",
    totalMm: 490,
    norm: "DIN 4172 + DIN EN 1996",
    description:
      "Klinker 11,5 + Dämmung/Luft 13,5 + Hintermauerwerk 24,0",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Hintermauerwerk (KS / Porenbeton / HLZ)",
        dickeMm: 240,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Dämmung + Luftschicht",
        dickeMm: 135,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Klinker / Verblender",
        dickeMm: 115,
      },
    ],
  },
  {
    id: "aw49b",
    label: "Außenwand 49cm — Variante B (dicke Dämmung)",
    totalMm: 490,
    norm: "DIN 4172 + GEG / DIN 4108",
    description:
      "Klinker 11,5 + Dämmung 20,0 + Hintermauerwerk 17,5",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Hintermauerwerk (KS / Porenbeton)",
        dickeMm: 175,
      },
      {
        gewerk: "trockenbau",
        preset: "custom",
        materialName: "Dämmung (GEG-konform)",
        dickeMm: 200,
      },
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Klinker / Verblender",
        dickeMm: 115,
      },
    ],
  },
  // 36,5 cm — monolithische Außenwand (Porenbeton / Hochlochziegel)
  {
    id: "aw365",
    label: "Außenwand 36,5cm — monolithisch",
    totalMm: 365,
    norm: "DIN 4172",
    description: "Hochwärmedämmstein 36,5cm (z.B. Porenbeton T7, Mz-Lochstein)",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "Wärmedämmstein",
        dickeMm: 365,
      },
    ],
  },
  // 24 cm — tragende Innenwand
  {
    id: "iw240",
    label: "Innenwand 24cm — tragend",
    totalMm: 240,
    norm: "DIN 4172 + DIN EN 1996",
    description: "Hintermauerwerk 24,0 (KS / Porenbeton) + Putz 1,5 beidseitig",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "KS / Porenbeton",
        dickeMm: 240,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 2,
      },
    ],
  },
  // 17,5 cm — nicht-tragende Innenwand
  {
    id: "iw175",
    label: "Innenwand 17,5cm — nicht-tragend",
    totalMm: 175,
    norm: "DIN 4172",
    description: "KS / Porenbeton 17,5 + Putz 1,5 beidseitig",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "KS / Porenbeton",
        dickeMm: 175,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 2,
      },
    ],
  },
  // 11,5 cm — Trennwand
  {
    id: "iw115",
    label: "Innenwand 11,5cm — Trennwand",
    totalMm: 115,
    norm: "DIN 4172",
    description: "KS 11,5 (Trennwand) + Putz 1,5 beidseitig",
    layers: [
      {
        gewerk: "rohbau",
        preset: "mauerwerk",
        materialName: "KS-Trennwand",
        dickeMm: 115,
      },
      {
        gewerk: "putz",
        preset: "custom",
        materialName: "Innenputz",
        dickeMm: 15,
        putzSides: 2,
      },
    ],
  },
];

/** Findet alle Presets deren `totalMm` innerhalb `toleranceMm` zum gegebenen Maß liegen. */
function findMatchingSystemPresets(
  thicknessMm: number,
  toleranceMm = 20
): SystemWallPreset[] {
  return SYSTEM_WALL_PRESETS.filter(
    (p) => Math.abs(p.totalMm - thicknessMm) <= toleranceMm
  );
}

/** Shoelace-Flaeche eines geschlossenen Rings in PDF-Pt². */
function ringAreaPt2(ring: number[][]): number {
  if (ring.length < 3) return 0;
  let s = 0;
  const closed =
    ring.length >= 2 &&
    Math.abs(ring[0][0] - ring[ring.length - 1][0]) < 1e-9 &&
    Math.abs(ring[0][1] - ring[ring.length - 1][1]) < 1e-9;
  const n = closed ? ring.length - 1 : ring.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    s += ring[i][0] * ring[j][1] - ring[j][0] * ring[i][1];
  }
  return Math.abs(s) / 2;
}

/** Umfang eines Rings als Summe der Kanten in PDF-Pt. */
function ringPerimeterPt(ring: number[][]): number {
  if (ring.length < 2) return 0;
  let p = 0;
  const closed =
    Math.abs(ring[0][0] - ring[ring.length - 1][0]) < 1e-9 &&
    Math.abs(ring[0][1] - ring[ring.length - 1][1]) < 1e-9;
  const n = closed ? ring.length - 1 : ring.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const dx = ring[j][0] - ring[i][0];
    const dy = ring[j][1] - ring[i][1];
    p += Math.hypot(dx, dy);
  }
  return p;
}

/**
 * Berechnet aus einem Ring (PDF-Pt) und dem aktuellen Maßstab die
 * Real-Welt-Werte areaM2 und perimeterM. PT_TO_MM_PAPER × ratio gibt
 * Real-mm pro Pt. Wir teilen durch 1000 für Meter.
 */
const PT_TO_MM_PAPER = 25.4 / 72;
function realMetricsFromRing(
  ring: number[][],
  ratio: number
): { areaM2: number; perimeterM: number } {
  const ptToM = (PT_TO_MM_PAPER * ratio) / 1000;
  return {
    areaM2: ringAreaPt2(ring) * ptToM * ptToM,
    perimeterM: ringPerimeterPt(ring) * ptToM,
  };
}

/**
 * Distanz vom Punkt zur Polygon-Ring-Boundary. Wenn der Punkt im Polygon liegt,
 * wird trotzdem der minimale Abstand zur Boundary zurückgegeben (= „wie tief
 * drin"). Für die Tür-/Fenster-Zuordnung reichen wir den Punkt durch und
 * akzeptieren Polygone deren Boundary nahe ist.
 */
function distancePointToRing(
  pt: { x: number; y: number },
  ring: number[][]
): number {
  let min = Infinity;
  for (let i = 0; i < ring.length - 1; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[i + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((pt.x - ax) * dx + (pt.y - ay) * dy) / lenSq : 0;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const cx = ax + t * dx;
    const cy = ay + t * dy;
    const d = Math.hypot(pt.x - cx, pt.y - cy);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Liefert den nächstgelegenen Punkt auf der Polygon-Boundary zu pt.
 * Wird genutzt, um z.B. ein OCR-Fenster-Label, das außerhalb der Außenwand
 * sitzt, auf die Wand zu projizieren (für Highlight im Plan).
 */
function closestPointOnRing(
  pt: { x: number; y: number },
  ring: number[][]
): { x: number; y: number; dist: number } | null {
  let min = Infinity;
  let best: { x: number; y: number } | null = null;
  for (let i = 0; i < ring.length - 1; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[i + 1];
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq > 0 ? ((pt.x - ax) * dx + (pt.y - ay) * dy) / lenSq : 0;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const cx = ax + t * dx;
    const cy = ay + t * dy;
    const d = Math.hypot(pt.x - cx, pt.y - cy);
    if (d < min) {
      min = d;
      best = { x: cx, y: cy };
    }
  }
  return best ? { ...best, dist: min } : null;
}

// Sprint D (2026-05-03) — DIN-277-Auswahl-Optionen für den manuellen Override.
// Gespiegelt aus packages/core/src/agents/room-classifier/index.ts (ROOM_TYPE_TABLE)
// + ergänzt um seltenere Codes (NUF2 Büro, NUF3 Produktion, KF Konstruktionsfläche).
type Din277Main = "NUF" | "VF" | "TF" | "KF";
interface Din277Option {
  main: Din277Main;
  sub: string;
  name: string;
}
const DIN_277_OPTIONS: Din277Option[] = [
  { main: "NUF", sub: "NUF1", name: "Wohnen und Aufenthalten" },
  { main: "NUF", sub: "NUF1.1", name: "Wohnen mit Küche" },
  { main: "NUF", sub: "NUF1.2", name: "Küche" },
  { main: "NUF", sub: "NUF2", name: "Büro" },
  { main: "NUF", sub: "NUF3", name: "Produktion / Werkstatt" },
  { main: "NUF", sub: "NUF4", name: "Lager / Verkauf" },
  { main: "NUF", sub: "NUF5", name: "Bildung / Unterricht" },
  { main: "NUF", sub: "NUF6", name: "Heilen und Pflegen (Bad/WC/Dusche)" },
  { main: "NUF", sub: "NUF7", name: "Sonstige Nutzung (Abstell/Keller/Balkon)" },
  { main: "VF", sub: "VF1", name: "Verkehrsflächen (Flur, Treppenhaus)" },
  { main: "VF", sub: "VF2", name: "Aufzug" },
  { main: "TF", sub: "TF0", name: "Gebäude-Außenkontur" },
  { main: "TF", sub: "TF1", name: "Betriebstechnik" },
  { main: "KF", sub: "KF", name: "Konstruktionsflächen" },
];

/**
 * Sprint D (2026-05-03) — Gewerke aus codeAssignments des Polygons ableiten,
 * statt aus DIN-277-Auto-Mapping. Liefert eine deduplizierte Liste der Gewerk-
 * Prefix-Codes (z.B. "BO" Boden, "AW" Außenwand, "FAS" Fassade, "SAN" Sanitär).
 * Lookup-Quelle: getCatalogEntryByCode → CatalogEntry.gewerk.
 */
function deriveGewerkeFromCodes(
  feat: PolygonFeature | null | undefined
): string[] {
  if (!feat) return [];
  const collected = new Set<string>();
  const polyAssignments = (feat.properties as {
    codeAssignments?: CodeAssignment[];
  }).codeAssignments ?? [];
  for (const ca of polyAssignments) {
    const entry = catalogLookup(ca.code);
    if (entry) collected.add(entry);
  }
  // Wand-Codes — jede Wand kann eigene CodeAssignments haben.
  const walls =
    (feat.properties as {
      walls?: Array<{ codeAssignments?: CodeAssignment[] }>;
    }).walls ?? [];
  for (const w of walls) {
    for (const ca of w.codeAssignments ?? []) {
      const entry = catalogLookup(ca.code);
      if (entry) collected.add(entry);
    }
  }
  return Array.from(collected).sort();
}

const GEWERK_DISPLAY_LABELS: Record<string, string> = {
  AW: "Außenwand",
  IW: "Innenwand",
  BO: "Boden",
  WA: "Wandoberfläche",
  DE: "Decke",
  SAN: "Sanitär",
  TW: "Trinkwasser",
  AWL: "Abwasser",
  EL: "Elektro",
  HZ: "Heizung",
  LUE: "Lüftung",
  KL: "Klima",
  DAH: "Dach",
  FE: "Fenster",
  TU: "Türen",
  GA: "GaLa",
  ERD: "Erdarbeiten",
  GR: "Gründung",
  MB: "Metallbau",
  BS: "Brandschutz",
  FAS: "Fassade",
};

function catalogLookup(code: string | undefined): string | null {
  if (!code) return null;
  // Heuristik: Code-Prefix (Buchstaben am Anfang) entspricht GewerkPrefix.
  const m = code.match(/^([A-Z]+)/);
  if (!m) return null;
  const prefix = m[1];
  return GEWERK_DISPLAY_LABELS[prefix] ?? prefix;
}

/**
 * Sprint D (2026-05-03) — Inspector-Editor für Raum-Label + DIN-277.
 * Auto-Vorschlag aus dem room-classifier-Agent bleibt als Default; User kann
 * frei überschreiben. Schreibt direkt nach `polygon.properties.userClassification`
 * via PATCH /polygons/:id/classification.
 *
 * `key={polygonId}` beim Mounten sorgt dafür, dass der Editor-State bei
 * Polygon-Wechsel sauber resettet wird.
 */
function RoomClassificationEditor(props: {
  runId: string;
  feat: PolygonFeature;
  classifiedRoom:
    | {
        label: string;
        din277?: { main: Din277Main; sub?: string; name?: string };
      }
    | null
    | undefined;
  derivedGewerke: string[];
  onChange: () => void;
}) {
  const { runId, feat, classifiedRoom, derivedGewerke, onChange } = props;
  const polygonId = String(feat.properties.id);
  const userClassification =
    (feat.properties as {
      userClassification?: {
        label?: string;
        din277Sub?: string;
        din277Main?: Din277Main;
        din277Name?: string;
      };
    }).userClassification ?? {};
  const initialLabel = userClassification.label ?? classifiedRoom?.label ?? "";
  const initialDin =
    userClassification.din277Sub ?? classifiedRoom?.din277?.sub ?? "";
  const [labelDraft, setLabelDraft] = useState(initialLabel);
  const [busy, setBusy] = useState(false);

  async function patchClassification(body: {
    label?: string | null;
    din277Sub?: string | null;
    din277Main?: Din277Main | null;
    din277Name?: string | null;
  }) {
    setBusy(true);
    try {
      await api.multiagentPatchPolygonClassification(runId, polygonId, body);
      onChange();
    } catch (err) {
      console.error("PATCH classification failed", err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="text-xs mb-3 space-y-2">
      <div className="flex items-center gap-2">
        <label className="text-slate-500 w-14 shrink-0">Label:</label>
        <input
          type="text"
          value={labelDraft}
          disabled={busy}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={(e) => {
            const next = e.currentTarget.value.trim();
            const current = (userClassification.label ?? classifiedRoom?.label ?? "").trim();
            if (next === current) return;
            patchClassification({ label: next === "" ? null : next });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              (e.currentTarget as HTMLInputElement).blur();
            } else if (e.key === "Escape") {
              setLabelDraft(initialLabel);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder={classifiedRoom?.label ?? "Raum-Label"}
          className="flex-1 px-2 py-1 rounded border font-medium"
          style={{
            background: "var(--bd-surface)",
            borderColor: userClassification.label
              ? "var(--bd-ember)"
              : "var(--bd-line)",
            color: "var(--bd-text)",
            fontSize: 12,
          }}
        />
        {userClassification.label !== undefined && !busy && (
          <button
            onClick={() => {
              setLabelDraft(classifiedRoom?.label ?? "");
              patchClassification({ label: null });
            }}
            className="text-[10px] text-slate-500 hover:text-slate-700 underline"
            title="Auto-Vorschlag wiederherstellen"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <label className="text-slate-500 w-14 shrink-0">DIN 277:</label>
        <select
          value={initialDin}
          disabled={busy}
          onChange={(e) => {
            const sub = e.currentTarget.value;
            if (sub === "") {
              patchClassification({
                din277Sub: null,
                din277Main: null,
                din277Name: null,
              });
              return;
            }
            const opt = DIN_277_OPTIONS.find((o) => o.sub === sub);
            if (!opt) return;
            patchClassification({
              din277Sub: opt.sub,
              din277Main: opt.main,
              din277Name: opt.name,
            });
          }}
          className="flex-1 px-2 py-1 rounded border font-mono"
          style={{
            background: "var(--bd-surface)",
            borderColor: userClassification.din277Sub
              ? "var(--bd-ember)"
              : "var(--bd-line)",
            color: "var(--bd-text)",
            fontSize: 11,
          }}
        >
          <option value="">— (Auto-Vorschlag)</option>
          {DIN_277_OPTIONS.map((o) => (
            <option key={o.sub} value={o.sub}>
              {o.sub} · {o.name}
            </option>
          ))}
        </select>
        {userClassification.din277Sub !== undefined && !busy && (
          <button
            onClick={() => {
              patchClassification({
                din277Sub: null,
                din277Main: null,
                din277Name: null,
              });
            }}
            className="text-[10px] text-slate-500 hover:text-slate-700 underline"
            title="Auto-Vorschlag wiederherstellen"
          >
            Reset
          </button>
        )}
      </div>

      <div className="flex items-start gap-2">
        <span className="text-slate-500 w-14 shrink-0">Gewerke:</span>
        <span className="flex-1">
          {derivedGewerke.length > 0 ? (
            <span className="font-mono">{derivedGewerke.join(", ")}</span>
          ) : (
            <span className="text-slate-400 italic">
              Bauteil-Codes vergeben für Gewerk-Anzeige
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

export default function MultiagentRunPage() {
  const { runId = "" } = useParams<{ runId: string }>();
  const [bundle, setBundle] = useState<BundledRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showFill, setShowFill] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showNgfAnchors, setShowNgfAnchors] = useState(true);
  const [showSplitsHighlight, setShowSplitsHighlight] = useState(true);
  const [showDoorWalls, setShowDoorWalls] = useState(true);
  // F16 Phase 3 — Auto-T-Stoss-Trennlinien als Overlay anzeigen + interagieren.
  // Persistiert in localStorage damit der Toggle session-übergreifend stabil ist.
  const [showAutoTJunctions, setShowAutoTJunctions] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("multiagent-show-auto-tjunctions");
    return v === null ? true : v === "true";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "multiagent-show-auto-tjunctions",
      String(showAutoTJunctions)
    );
  }, [showAutoTJunctions]);
  // 2026-05-01 — Möblierung im Voll-Plan ausblendbar (OCG-Layer A-Möblierung).
  // Hilft beim manuellen Wände-Zeichnen, weil Möbel/Sanitär-Symbole sonst
  // optisch mit Wand-Linien konkurrieren. Persistiert in localStorage.
  const [hideFurniture, setHideFurniture] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("multiagent-hide-furniture") === "true";
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "multiagent-hide-furniture",
      String(hideFurniture)
    );
  }, [hideFurniture]);
  // Sprint 0.19 — Außenbereichs-Polygone (Stellplätze, Symbol-Polygone) sind
  // standardmäßig grau/transparent. Toggle blendet sie ganz aus für sauberes
  // Plan-Bild.
  const [showOutdoor, setShowOutdoor] = useState(true);
  // Sprint 0.19 — Bidirektionales Highlight Wand-Liste ↔ Plan: User klickt
  // einen Wand-Eintrag rechts an, die zugehörige Linie wird im SVG dick blau
  // gerendert.
  const [highlightedWallIdx, setHighlightedWallIdx] = useState<number | null>(
    null
  );
  // F8 — bidirektionales Highlight: Klick auf Tür/Fenster in der Sidebar markiert
  // sie im Plan. Key-Format: "auto-door-3" / "user-7" (idx in properties.openings).
  const [highlightedOpening, setHighlightedOpening] = useState<{
    polyId: string;
    key: string;
    type: "door" | "window";
    source: "auto" | "user";
    idx: number;
  } | null>(null);

  // Mengen-Tabelle
  const [quantities, setQuantities] = useState<QuantitiesOutput | null>(null);
  const [quantitiesError, setQuantitiesError] = useState<string | null>(null);
  const [raumhoeheM, setRaumhoeheM] = useState(2.5);
  const [putzAbzugPercent, setPutzAbzugPercent] = useState(12);
  // Sprint 0.19 — Verschnitt-Aufschläge (default-Werte aus DACH-Erfahrung)
  const [verschnittBoden, setVerschnittBoden] = useState(5);
  const [verschnittFliesen, setVerschnittFliesen] = useState(8);
  const [verschnittRohbau, setVerschnittRohbau] = useState(3);
  const [showQuantities, setShowQuantities] = useState(false);
  const [expandedGewerk, setExpandedGewerk] = useState<string | null>(null);
  // Sprint A.10 (2026-05-02) — Toggle Legacy/v2 + V2-Daten
  const [quantitiesView, setQuantitiesView] = useState<"legacy" | "v2">("legacy");
  const [quantitiesV2, setQuantitiesV2] = useState<QuantitiesV2Output | null>(null);
  const [quantitiesV2Error, setQuantitiesV2Error] = useState<string | null>(null);
  const [v2HauptgruppeFilter, setV2HauptgruppeFilter] = useState<string>("alle");
  const [v2RaumFilter, setV2RaumFilter] = useState<string>("alle");
  const [v2QuelleFilter, setV2QuelleFilter] = useState<string>("alle");
  const [opacity, setOpacity] = useState(0.4);

  // Edit-Modus: Polygon-Split + Merge + Wand-Strich + Vertex-Drag + Wand-CRUD
  type EditMode =
    | "view"
    | "splitPicking"
    | "splitPreview"
    | "mergeTarget"
    | "mergePreview"
    | "drawWall"
    | "vertexEdit"
    | "drawWallSegment"
    | "editWallEndpoints"
    | "scalePicking"
    | "drawOpening"
    | "drawPolygon";
  const [editMode, setEditMode] = useState<EditMode>("view");
  const [splitTarget, setSplitTarget] = useState<string | null>(null);
  const [splitPoints, setSplitPoints] = useState<Array<{ x: number; y: number }>>(
    []
  );
  const [splitPreview, setSplitPreview] = useState<{
    ringA: Ring;
    ringB: Ring;
    fractionA: number;
  } | null>(null);
  const [splitError, setSplitError] = useState<string | null>(null);
  const [splitSaving, setSplitSaving] = useState(false);
  const [mergeFirstId, setMergeFirstId] = useState<string | null>(null);
  const [mergeSecondId, setMergeSecondId] = useState<string | null>(null);
  const [mergePreview, setMergePreview] = useState<{
    ring: Ring;
    area: number;
  } | null>(null);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeSaving, setMergeSaving] = useState(false);

  // Sprint 0.19 — Virtuelle Schnitte (User-Walls für Floodfill-Trennung).
  // Sind keine echten Wände mit Mauerwerk — nur visuelle Trennlinien die ins
  // Floodfill-Bitmap injiziert werden, um Räume sauber abzugrenzen.
  const [wallStart, setWallStart] = useState<{ x: number; y: number } | null>(
    null
  );
  const [wallSaving, setWallSaving] = useState(false);
  const [wallError, setWallError] = useState<string | null>(null);
  // rerunning entfernt mit dem rerunPipelineWithUserWalls-Pfad
  // (Sprint UX-Inspector 2026-05-06).
  // Sprint 0.19 — Highlight eines virtuellen Schnitts im Plan (vom Sidebar-Klick).
  // setSelectedUserWallIdx ist seit Sprint UX-Inspector (2026-05-06) ungenutzt
  // (Schnitt-Sidebar entfernt) — bidirektionales Highlight kann später wieder
  // rein wenn das W-Tool eine Sidebar bekommt.
  const [selectedUserWallIdx] = useState<number | null>(null);

  // Sprint 0.19 — Polygon-Vertex-Drag
  const [vertexEditPolygonId, setVertexEditPolygonId] = useState<string | null>(
    null
  );
  const [editingRing, setEditingRing] = useState<number[][] | null>(null);
  const [draggingVertexIdx, setDraggingVertexIdx] = useState<number | null>(
    null
  );
  const [vertexEditSaving, setVertexEditSaving] = useState(false);
  const [vertexEditError, setVertexEditError] = useState<string | null>(null);
  // Sprint #3 follow-up — Wand-Rematch-Trigger pro Polygon (manueller Reload
  // der walls[] aus den Sprint-W-Cluster-Mittellinien).
  const [wallRematchSaving, setWallRematchSaving] = useState(false);
  const [wallRematchInfo, setWallRematchInfo] = useState<string | null>(null);

  // Sprint 0.19 — History / Undo
  const [history, setHistory] = useState<HistorySnapshot[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Sprint 0.20 — Maßstab manuell setzen (ScaleSetter-Modal + 2-Punkt-Picking)
  const [scaleSetterOpen, setScaleSetterOpen] = useState(false);
  const [scalePickStart, setScalePickStart] = useState<{ x: number; y: number } | null>(null);
  const [scalePicked, setScalePicked] = useState<{
    from: { x: number; y: number };
    to: { x: number; y: number };
  } | null>(null);

  // Sprint 0.20 — Plan-Bitmap-Layer-Toggle: "all" zeigt vollen PDF-Render,
  // "walls" nur die als Wand klassifizierten Linien, "helpers" alles andere
  // (Achsen, Bemaßung, Möbel). Macht den non-OCG-Filter visuell überprüfbar.
  const [planBitmapLayer, setPlanBitmapLayer] = useState<
    "all" | "walls" | "helpers"
  >("all");

  // Sprint 0.20 — Öffnungs-Editor (Tür/Fenster manuell anlegen + editieren)
  // Picking-Mode: User klickt 2 Punkte auf der Wand, dann öffnet Modal mit
  // berechneter widthM und prefilled startPt/endPt.
  const [openingPickStart, setOpeningPickStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [openingPickContext, setOpeningPickContext] = useState<{
    polygonId: string;
    type: "door" | "window";
  } | null>(null);
  // Final picked Segment, wird im save-Handler an die API mitgeschickt.
  const [openingEditorSegment, setOpeningEditorSegment] = useState<{
    startPt: { x: number; y: number };
    endPt: { x: number; y: number };
  } | null>(null);
  // Sprint 0.20 — Polygon-Zeichnen (n-Klick + Doppelklick zum Schließen)
  const [drawPolygonPoints, setDrawPolygonPoints] = useState<
    Array<{ x: number; y: number }>
  >([]);
  const [drawPolygonSaving, setDrawPolygonSaving] = useState(false);
  // F4 — wenn true wird das gezeichnete Polygon als HAUS angelegt (isBuilding).
  const [drawPolygonIsHaus, setDrawPolygonIsHaus] = useState(false);

  // F13.6 — Sidebar-Tabs für Progressive Disclosure (statt alles vertikal stapeln).
  // Default 'run' (Run-Übersicht). Wechselt automatisch zu 'auswahl' wenn ein
  // Polygon selektiert wird, zu 'run' wenn die Selection aufgehoben wird.
  type SidebarTab = "run" | "auswahl" | "mengen" | "verlauf" | "anzeige";
  const [activeTab, setActiveTab] = useState<SidebarTab>("run");
  // UX3 — Filter im linken Panel: welcher Element-Typ wird gelistet
  type LeftListFilter = "rooms" | "doors" | "windows" | "walls";
  const [leftListFilter, setLeftListFilter] = useState<LeftListFilter>("rooms");

  // F13.1 — Resizable Sidebar (Splitter zwischen Plan-Canvas und rechter Sidebar).
  // Persistiert in localStorage. Default 384px (= w-96), Min 280, Max 720.
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 384;
    const raw = window.localStorage.getItem("multiagent-sidebar-width");
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 280 && n <= 720 ? n : 384;
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  // UX2 — Linkes Panel (Listen-Spalte): default 240px, persistiert.
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 240;
    const raw = window.localStorage.getItem("multiagent-left-panel-width");
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n >= 180 && n <= 420 ? n : 240;
  });
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  useEffect(() => {
    if (!isResizingSidebar) return;
    const handleMove = (e: MouseEvent) => {
      const next = Math.max(280, Math.min(720, window.innerWidth - e.clientX));
      setSidebarWidth(next);
    };
    const handleUp = () => setIsResizingSidebar(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizingSidebar]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("multiagent-sidebar-width", String(sidebarWidth));
  }, [sidebarWidth]);
  // UX2 — Linkes Panel: gleicher Mechanik wie F13.1
  useEffect(() => {
    if (!isResizingLeft) return;
    const handleMove = (e: MouseEvent) => {
      const next = Math.max(180, Math.min(420, e.clientX));
      setLeftPanelWidth(next);
    };
    const handleUp = () => setIsResizingLeft(false);
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizingLeft]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "multiagent-left-panel-width",
      String(leftPanelWidth)
    );
  }, [leftPanelWidth]);
  // F13.6 — Auto-Tab-Switch: Polygon ausgewählt → "auswahl"; deselektiert → "run"
  useEffect(() => {
    if (selectedId) setActiveTab("auswahl");
    else if (activeTab === "auswahl") setActiveTab("run");
    // activeTab absichtlich nicht in deps: nur auf selectedId-Change reagieren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const [openingEditorOpen, setOpeningEditorOpen] = useState(false);
  const [openingEditorTarget, setOpeningEditorTarget] = useState<{
    polygonId: string;
    idx: number | null; // null = neu anlegen
  } | null>(null);
  const [openingEditorType, setOpeningEditorType] = useState<"door" | "window">(
    "window"
  );
  const [openingEditorFields, setOpeningEditorFields] = useState<{
    widthM: string;
    heightM: string;
    bruestungM: string;
    label: string;
  }>({ widthM: "", heightM: "", bruestungM: "", label: "" });
  const [openingEditorSaving, setOpeningEditorSaving] = useState(false);
  const [openingEditorError, setOpeningEditorError] = useState<string | null>(
    null
  );

  // Sprint 0.19 — Wand-CRUD (Delete / Edit Endpoints / Add / Material-Editor)
  const [wallEditError, setWallEditError] = useState<string | null>(null);
  const [wallEditSaving, setWallEditSaving] = useState(false);

  // Sprint 0.20 — Multi-Mirror-Toast: Backend liefert mirroredToList, wenn die
  // angelegte Wand auf weitere Polygone gespiegelt wurde (innen-trennwand oder
  // aussenwand → HAUS).
  const [mirrorBanner, setMirrorBanner] = useState<{ text: string; ts: number } | null>(null);
  useEffect(() => {
    if (!mirrorBanner) return;
    const timer = setTimeout(() => setMirrorBanner(null), 3500);
    return () => clearTimeout(timer);
  }, [mirrorBanner]);

  // Sprint 0.21 — Run-Meta (Etage + Wohneinheiten)
  const [runMeta, setRunMeta] = useState<MultiagentRunMeta | null>(null);
  const [weAssignTarget, setWeAssignTarget] = useState<string | null>(null);
  const [weEditingId, setWeEditingId] = useState<string | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);

  // Sprint M (2026-05-03) — Mappe + ihre anderen Runs (Topbar-Switcher).
  const [mappeName, setMappeName] = useState<string | null>(null);
  const [mappeRuns, setMappeRuns] = useState<
    Array<{
      runId: string;
      displayLabel: string;
      sortOrder: number;
      etage: string | null;
    }> | null
  >(null);
  // Sprint P (2026-05-03) — Cross-Plan-Daten der Mappe (Etagen-Höhen aus
  // Schnitten + Σ Verblenderfläche aus Ansichten).
  const [crossPlanData, setCrossPlanData] = useState<MappeCrossPlanData | null>(
    null
  );
  useEffect(() => {
    const mappeId = runMeta?.mappeId;
    if (!mappeId) {
      setMappeRuns(null);
      setMappeName(null);
      setCrossPlanData(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      api.mappenGet(mappeId),
      api.mappenCrossPlanData(mappeId).catch(() => null),
    ])
      .then(([mappeRes, crossRes]) => {
        if (cancelled) return;
        setMappeName(mappeRes.mappe.name);
        setMappeRuns(
          mappeRes.runs.map((r) => ({
            runId: r.runId,
            displayLabel: r.displayLabel,
            sortOrder: r.sortOrder,
            etage: r.etage,
          }))
        );
        setCrossPlanData(crossRes);
      })
      .catch(() => {
        if (cancelled) return;
        setMappeRuns(null);
        setMappeName(null);
        setCrossPlanData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [runMeta?.mappeId]);

  // Sprint 0.22 — LV-Modul Feature-Flag + Transfer
  const [lvEnabled, setLvEnabled] = useState(false);
  const [lvTransferring, setLvTransferring] = useState(false);
  const [lvTransferError, setLvTransferError] = useState<string | null>(null);
  useEffect(() => {
    api
      .featureFlags()
      .then((f) => setLvEnabled(f.lv))
      .catch(() => setLvEnabled(false));
  }, []);
  const triggerLvTransfer = async () => {
    if (!runMeta?.etage) {
      setLvTransferError(
        "Etage muss vor LV-Transfer gesetzt sein (siehe oben)."
      );
      return;
    }
    setLvTransferring(true);
    setLvTransferError(null);
    try {
      const res = await api.lvTransfer(runId);
      // Nach erfolgreichem Transfer zur LV-Page navigieren
      window.location.href = `/lv/${res.lvRunId}`;
    } catch (err) {
      setLvTransferError(
        err instanceof Error ? err.message : String(err)
      );
    } finally {
      setLvTransferring(false);
    }
  };

  // Sprint Inline-OCR (2026-05-06) — Premium-Feature: pro Polygon-Bbox
  // Inline-Notizen via Vision-API lesen (RH/BRH/DFF/VK Decke/Vorwand/Stützen).
  // Kosten ~0,02 €/Polygon × Polygon-Anzahl. Idempotent — bestehende OCR-Daten
  // werden übersprungen außer bei `force`.
  const [inlineOcrRunning, setInlineOcrRunning] = useState(false);
  const [inlineOcrError, setInlineOcrError] = useState<string | null>(null);
  const [inlineOcrLastSummary, setInlineOcrLastSummary] = useState<{
    polygonsOcrCalled: number;
    polygonsWithText: number;
    totalCostEur: number;
    at: number;
  } | null>(null);

  const triggerInlineOcr = async (force: boolean) => {
    if (!bundle) return;
    const totalPolygons = bundle.polygons.features.filter(
      (f) =>
        !(f.properties as { isBuilding?: boolean; isGaLaArea?: boolean })
          .isBuilding &&
        !(f.properties as { isBuilding?: boolean; isGaLaArea?: boolean })
          .isGaLaArea
    ).length;
    const alreadyDone = Object.keys(
      bundle.annotations?.perRoomInlineText ?? {}
    ).length;
    const willProcess = force ? totalPolygons : Math.max(totalPolygons - alreadyDone, 0);
    if (willProcess === 0) {
      setInlineOcrError(
        "Alle Polygone haben bereits Inline-OCR-Daten. Mit ⤺ Force-Re-Run lassen sich die Daten erneut lesen."
      );
      return;
    }
    const estCostEur = willProcess * 0.02;
    const msg = force
      ? `Inline-OCR auf ${totalPolygons} Räume erzwingen (überschreibt bestehende Daten). Geschätzte Kosten: ~${estCostEur.toFixed(2)} €. Fortsetzen?`
      : `Inline-Notizen lesen für ${willProcess} Räume (${alreadyDone} bereits gelesen). Geschätzte Kosten: ~${estCostEur.toFixed(2)} €. Fortsetzen?`;
    if (!window.confirm(msg)) return;
    setInlineOcrRunning(true);
    setInlineOcrError(null);
    try {
      const res = await api.multiagentInlineOcr(runId, force);
      // Cost-Schätzung aus tatsächlichen Tokens: Sonnet 4.6 Vision
      // ≈ $3/M input + $15/M output ≈ 2,80 €/M input + 14 €/M output.
      const tokensIn = res.summary.totalInputTokens;
      const tokensOut = res.summary.totalOutputTokens;
      const costEur = (tokensIn / 1_000_000) * 2.8 + (tokensOut / 1_000_000) * 14;
      setInlineOcrLastSummary({
        polygonsOcrCalled: res.summary.polygonsOcrCalled,
        polygonsWithText: res.summary.polygonsWithText,
        totalCostEur: costEur,
        at: Date.now(),
      });
      // Bundle neu laden, damit der Inspector die OCR-Daten zeigt.
      const fresh = await loadBundle(runId);
      setBundle(fresh);
    } catch (err) {
      setInlineOcrError(err instanceof Error ? err.message : String(err));
    } finally {
      setInlineOcrRunning(false);
    }
  };
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    api.multiagentGetMeta(runId).then(({ meta }) => {
      if (!cancelled) setRunMeta(meta);
    }).catch((err) => {
      if (!cancelled) setMetaError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      cancelled = true;
    };
  }, [runId]);
  const patchMeta = async (patch: Partial<MultiagentRunMeta>) => {
    setMetaError(null);
    try {
      const { meta } = await api.multiagentPatchMeta(runId, patch);
      setRunMeta(meta);
    } catch (err) {
      setMetaError(err instanceof Error ? err.message : String(err));
    }
  };
  const addWohneinheit = () => {
    if (!runMeta) return;
    const existingIds = new Set(runMeta.wohneinheiten.map((w) => w.id));
    let n = runMeta.wohneinheiten.length + 1;
    while (existingIds.has(`WE${n}`)) n++;
    const newId = `WE${n}`;
    void patchMeta({
      wohneinheiten: [
        ...runMeta.wohneinheiten,
        { id: newId, label: newId, raumIds: [] },
      ],
    });
  };
  const renameWohneinheit = (id: string, label: string) => {
    if (!runMeta) return;
    void patchMeta({
      wohneinheiten: runMeta.wohneinheiten.map((w) =>
        w.id === id ? { ...w, label } : w
      ),
    });
  };
  const deleteWohneinheit = (id: string) => {
    if (!runMeta) return;
    void patchMeta({
      wohneinheiten: runMeta.wohneinheiten.filter((w) => w.id !== id),
    });
    if (weAssignTarget === id) setWeAssignTarget(null);
    if (weEditingId === id) setWeEditingId(null);
  };
  const toggleRaumInWohneinheit = (weId: string, roomId: string) => {
    if (!runMeta) return;
    const next = runMeta.wohneinheiten.map((w) => {
      if (w.id === weId) {
        return {
          ...w,
          raumIds: w.raumIds.includes(roomId)
            ? w.raumIds.filter((r) => r !== roomId)
            : [...w.raumIds, roomId],
        };
      }
      // Andere WEs: Raum darauf entfernen, falls er da war (Raum nur in 1 WE)
      return { ...w, raumIds: w.raumIds.filter((r) => r !== roomId) };
    });
    void patchMeta({ wohneinheiten: next });
  };
  // Lookup: roomId -> WE für Plan-View-Badges
  const roomToWe = useMemo(() => {
    const m = new Map<string, MultiagentRunWohneinheit>();
    for (const we of runMeta?.wohneinheiten ?? []) {
      for (const r of we.raumIds) m.set(r, we);
    }
    return m;
  }, [runMeta]);
  const weColorFor = (weId: string): string => {
    let h = 0;
    for (const c of weId) h = (h * 31 + c.charCodeAt(0)) % 360;
    return `hsl(${h}, 60%, 70%)`;
  };
  // F10 — m²-Summe pro WE (aus zugewiesenen Räumen, areaM2 aus 04-rooms.json).
  const weAreaSum = useMemo(() => {
    const result = new Map<string, number>();
    if (!bundle?.rooms?.rooms) return result;
    const areaByRoom = new Map<string, number>();
    for (const r of bundle.rooms.rooms) {
      if (typeof r.areaM2 === "number") areaByRoom.set(r.id, r.areaM2);
    }
    for (const we of runMeta?.wohneinheiten ?? []) {
      let sum = 0;
      for (const rid of we.raumIds) sum += areaByRoom.get(rid) ?? 0;
      result.set(we.id, sum);
    }
    return result;
  }, [bundle, runMeta]);

  // Material-Editor-Modal: editiert den Schicht-Stack einer Wand.
  // Schicht = { gewerk, preset, materialName?, dickeMm, putzSides? }
  type LayerForm = {
    gewerk: "rohbau" | "putz" | "trockenbau" | "oberflaeche" | "sonstiges";
    preset: "mauerwerk" | "trockenbau" | "beton" | "custom";
    materialName?: string;
    dickeMm: number;
    putzSides?: 1 | 2;
  };
  const [materialEditorOpen, setMaterialEditorOpen] = useState(false);
  const [materialEditorPolygonId, setMaterialEditorPolygonId] = useState<
    string | null
  >(null);
  const [materialEditorWallIdx, setMaterialEditorWallIdx] = useState<
    number | null
  >(null);
  const [materialEditorLayers, setMaterialEditorLayers] = useState<
    LayerForm[]
  >([]);
  // Stufe 1 (Wand-Katalog) — gewählter Wandtyp + Fassade. Wird beim Save
  // mit den Materials persistiert. `null` bedeutet: User hat keinen
  // Wandtyp aus dem Katalog gewählt (free-form Stack wie bisher).
  const [materialEditorWallTypeCode, setMaterialEditorWallTypeCode] =
    useState<string | null>(null);
  const [materialEditorFacadeCode, setMaterialEditorFacadeCode] = useState<
    string | null
  >(null);
  const [materialEditorCatalogTab, setMaterialEditorCatalogTab] =
    useState<"outer" | "inner">("inner");
  // Sprint A.8 — explizite Code-Liste pro Wand (Stufe 2). Wird vom Material-
  // Editor verwaltet und beim Save in PATCH /walls als codeAssignments-Body
  // geschrieben. Pro Code: Status + Quelle + Fachplaner-Nachweise.
  const [materialEditorCodeAssignments, setMaterialEditorCodeAssignments] =
    useState<CodeAssignment[]>([]);
  const [showCodePicker, setShowCodePicker] = useState(false);
  const [expandedCodeAssignmentId, setExpandedCodeAssignmentId] = useState<
    string | null
  >(null);
  const [wallCatalog, setWallCatalog] = useState<{
    walls: Array<{
      code: string;
      fullCode: string;
      category: "outer" | "inner";
      shortLabel: string;
      description: string;
      bauweise: string;
      tragend: string;
      schichten: string[];
      totalThicknessMmRange: [number, number];
      defaultLayers: LayerForm[];
      normReferenz: string[];
      einsatz: string;
      defaultFacadeCode?: string;
    }>;
    facades: Array<{
      code: string;
      fullCode: string;
      shortLabel: string;
      description: string;
      geeignetFuer: string[];
      normReferenz: string[];
    }>;
  } | null>(null);
  // Sprint 0.19 / Punkt 4 — Plan-Maß für System-Wand-Vorschlag (mm).
  // Default = aktuelle Wand-Stärke beim Modal-Öffnen. User kann das Maß
  // anpassen wenn der Plan z.B. „49" zeigt aber automatische Erkennung
  // aus dem CAD-Stream einen anderen Wert geliefert hat.
  const [materialEditorPlanThicknessMm, setMaterialEditorPlanThicknessMm] =
    useState<number>(0);

  // Sprint #5 (Wand-Material-Wizard, 2026-05-04) — 4-Step-Konfigurator
  // statt überfrachtetem Modal. State pro Wizard-Schritt:
  //   S1: Wandtyp + Plan-Maß + Innen/Außen — nutzt bestehende States
  //   S2: Aufbau prüfen + Akkordeon — nutzt bestehende States
  //   S3: Fachplaner-Status pro Code (Bestätigung der Default-Pflicht-Nachweise)
  //   S4: Oberflächen (Tapete/Anstrich/Fliesen ja/nein + Code-Picker)
  const [materialWizardStep, setMaterialWizardStep] = useState<1 | 2 | 3 | 4>(1);
  const [materialWizardLayersOpen, setMaterialWizardLayersOpen] = useState(false);
  // Surface-Toggles + ausgewählte Codes pro Surface-Slot.
  type SurfaceSlot = "tapete" | "anstrich" | "fliesen";
  const [materialWizardSurfaceOn, setMaterialWizardSurfaceOn] = useState<
    Record<SurfaceSlot, boolean>
  >({ tapete: false, anstrich: false, fliesen: false });
  const [materialWizardSurfaceCode, setMaterialWizardSurfaceCode] = useState<
    Record<SurfaceSlot, string | null>
  >({ tapete: null, anstrich: null, fliesen: null });
  // Add-Modus: Polygon zu dem die neue Wand gehört + 2-Klick-Vorschau
  const [wallSegmentTargetPolygonId, setWallSegmentTargetPolygonId] =
    useState<string | null>(null);
  const [wallSegmentStart, setWallSegmentStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Edit-Endpoints-Modus: welche Wand gerade editiert wird, welcher Endpunkt
  // gerade gezogen wird ("start" oder "end").
  const [wallEditPolygonId, setWallEditPolygonId] = useState<string | null>(
    null
  );
  const [wallEditIdx, setWallEditIdx] = useState<number | null>(null);
  const [wallEditEndpoints, setWallEditEndpoints] = useState<{
    startPt: { x: number; y: number };
    endPt: { x: number; y: number };
  } | null>(null);
  const [draggingWallEnd, setDraggingWallEnd] = useState<
    "start" | "end" | null
  >(null);

  const svgRef = useRef<SVGSVGElement | null>(null);

  // Zoom + Pan (SVG viewBox-basiert)
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 }); // viewBox origin in PDF-Pt

  // Sprint 0.19 — Detail-Lupe oben rechts: trackt Mausposition über dem Plan
  // (PDF-Pt-Coords) und rendert einen 5×-vergrößerten Ausschnitt um den Cursor.
  const [magnifierPt, setMagnifierPt] = useState<{ x: number; y: number } | null>(
    null
  );
  const [magnifierActive, setMagnifierActive] = useState(true);

  /**
   * Bubble-Größe zoom-invariant halten: bei zoom=1 bleibt r wie definiert,
   * bei zoom=10 wird r = baseR/10 — die Pixel-Größe auf dem Bildschirm bleibt
   * konstant, weil das viewBox um Faktor zoom kleiner wird.
   */
  const scaleR = (baseR: number) => baseR / zoom;
  const panStartRef = useRef<{
    clientX: number;
    clientY: number;
    panX: number;
    panY: number;
  } | null>(null);

  useEffect(() => {
    setHighlightedWallIdx(null);
  }, [selectedId]);

  useEffect(() => {
    let mounted = true;
    setBundle(null);
    setError(null);
    setSelectedId(null);
    loadBundle(runId)
      .then((b) => {
        if (mounted) setBundle(b);
      })
      .catch((e) => {
        if (mounted) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted = false;
    };
  }, [runId]);

  // Stufe 1 (Wand-Katalog) — einmal pro Run laden. Werden im Material-Editor
  // als Wandtyp-Auswahl angeboten und liefern den Default-Material-Stack.
  useEffect(() => {
    if (!runId) return;
    let mounted = true;
    api
      .multiagentWallCatalog(runId)
      .then((c) => {
        if (mounted) setWallCatalog(c);
      })
      .catch(() => {
        // Katalog ist optional — bei Fehler bleibt die alte DIN-4172-
        // Vorschlags-Logik aktiv.
      });
    return () => {
      mounted = false;
    };
  }, [runId]);

  // Sprint 0.19 — History laden, neu bei jedem Bundle-Wechsel (= nach jeder Mutation).
  useEffect(() => {
    let mounted = true;
    if (!bundle) {
      setHistory([]);
      return;
    }
    api
      .multiagentHistory(runId)
      .then((h) => {
        if (mounted) setHistory(h.snapshots);
      })
      .catch((e) => {
        if (mounted)
          setHistoryError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted = false;
    };
  }, [runId, bundle]);

  const restoreSnapshot = async (snapshotId: string) => {
    if (restoring || resetting) return;
    setRestoring(true);
    setHistoryError(null);
    try {
      await api.multiagentRestoreSnapshot(runId, snapshotId);
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setSelectedId(null);
      cancelEdit();
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : String(err));
    } finally {
      setRestoring(false);
    }
  };

  const resetToPipeline = async () => {
    if (resetting || restoring) return;
    if (
      !window.confirm(
        "Pipeline-Default wiederherstellen? Alle Edits (Virtuelle Schnitte, Splits, Merges, Vertex-Drags) werden verworfen — der vorherige Stand wird als Undo-Snapshot in der History abgelegt."
      )
    ) {
      return;
    }
    setResetting(true);
    setHistoryError(null);
    try {
      await api.multiagentResetToPipeline(runId);
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setSelectedId(null);
      cancelEdit();
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : String(err));
    } finally {
      setResetting(false);
    }
  };

  // Mengen neu laden bei Änderung von runId/bundle/Raumhöhe/Putz-Abzug
  useEffect(() => {
    let mounted = true;
    if (!bundle) {
      setQuantities(null);
      return;
    }
    api
      .multiagentQuantities(runId, {
        raumhoehe: raumhoeheM,
        putzAbzug: putzAbzugPercent,
        verschnittBoden,
        verschnittFliesen,
        verschnittRohbau,
      })
      .then((q) => {
        if (mounted) {
          setQuantities(q);
          setQuantitiesError(null);
        }
      })
      .catch((e) => {
        if (mounted) {
          setQuantities(null);
          setQuantitiesError(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      mounted = false;
    };
  }, [runId, bundle, raumhoeheM, putzAbzugPercent, verschnittBoden, verschnittFliesen, verschnittRohbau]);

  // Sprint A.10 — V2-Mengen laden (nur wenn Tab im v2-Modus, sonst spart's
  // einen Backend-Call). Wir verwenden die gleichen Verschnitt-/Raumhöhe-Slider
  // wie in v1; verschnittPct nimmt v1's verschnittBoden als grobe Approximation.
  useEffect(() => {
    let mounted = true;
    if (!bundle || quantitiesView !== "v2") {
      return;
    }
    api
      .multiagentQuantitiesV2(runId, {
        raumhoehe: raumhoeheM,
        verschnitt: verschnittBoden / 100,
      })
      .then((q) => {
        if (mounted) {
          setQuantitiesV2(q);
          setQuantitiesV2Error(null);
        }
      })
      .catch((e) => {
        if (mounted) {
          setQuantitiesV2(null);
          setQuantitiesV2Error(e instanceof Error ? e.message : String(e));
        }
      });
    return () => {
      mounted = false;
    };
  }, [runId, bundle, quantitiesView, raumhoeheM, verschnittBoden]);

  const splitMap = useMemo(() => {
    const m = new Map<string, "matched" | "splitCandidate" | "noStamp">();
    if (!bundle?.dimensions) return m;
    for (const pm of bundle.dimensions.roomNgf.polygonMatches) {
      m.set(pm.polygonId, pm.status);
    }
    return m;
  }, [bundle]);

  /**
   * Sprint 0.19 — Türen pro Polygon aus den door-walls (Bezier-Sehnen-
   * rekonstruierte Tür-Pfosten-Linien). Eine Tür sitzt fachlich ZWISCHEN
   * zwei Räumen, darf in der Mengenermittlung aber nur EINEM Raum zugezählt
   * werden, sonst doppelt gerechnet. Wir sortieren die anliegenden Polygone
   * (Boundary ≤ 8pt) nach Fläche aufsteigend — der kleinste Raum „besitzt"
   * die Tür (typisch Bad/Schlafzimmer vor Verkehrsfläche). Der zweite
   * Raum bekommt einen `sharedWith`-Hinweis, sieht die Tür aber nicht in
   * seiner Liste.
   */
  const ratio =
    bundle?.classification.pages[0]?.scale?.ratio ?? 100;
  /** PDF-Pt → Real-cm. Bei pilot-eg Ratio 77.32 → 1 Pt ≈ 2.73 cm. */
  const ptToCm = (ratio * 25.4) / 72 / 10;

  type DoorEntry = {
    widthCm: number;
    from: { x: number; y: number };
    to: { x: number; y: number };
    /** IDs anderer Polygone an die diese Tür auch grenzt (für Info, nicht doppelt zählen). */
    sharedWith: string[];
    /** Polygon-lokaler Index in doorsByPolyId.get(polyId). Für F7-Suppression. */
    _origIdx?: number;
    /** Globaler Index in bundle.doorWalls.doorWalls. Für SVG-Filter. */
    _globalIdx?: number;
  };
  const doorsByPolyId = useMemo(() => {
    const m = new Map<string, DoorEntry[]>();
    if (!bundle?.doorWalls) return m;
    bundle.doorWalls.doorWalls.forEach((dw, globalIdx) => {
      const mid = {
        x: (dw.from.x + dw.to.x) / 2,
        y: (dw.from.y + dw.to.y) / 2,
      };
      const widthPt = Math.hypot(dw.to.x - dw.from.x, dw.to.y - dw.from.y);
      const widthCm = widthPt * ptToCm;
      // Anliegende Polygone (Boundary-Distance ≤ 8pt).
      const candidates: Array<{ id: string; areaM2: number }> = [];
      for (const f of bundle.polygons.features) {
        const d = distancePointToRing(mid, f.geometry.coordinates[0]);
        if (d > 8) continue;
        candidates.push({
          id: f.properties.id,
          areaM2: f.properties.areaM2,
        });
      }
      if (candidates.length === 0) return;
      // Stabile Zuordnung: kleinster Raum gewinnt; bei gleicher Fläche
      // entscheidet die ID alphabetisch (deterministisch).
      candidates.sort(
        (a, b) => a.areaM2 - b.areaM2 || a.id.localeCompare(b.id)
      );
      const winner = candidates[0];
      const sharedWith = candidates.slice(1).map((c) => c.id);
      if (!m.has(winner.id)) m.set(winner.id, []);
      const list = m.get(winner.id)!;
      list.push({
        widthCm,
        from: dw.from,
        to: dw.to,
        sharedWith,
        _origIdx: list.length,
        _globalIdx: globalIdx,
      });
    });
    return m;
  }, [bundle, ptToCm]);

  // F7 — Set globaler Indizes der suppressed AUTO-Türen (für SVG-Filter).
  const suppressedAutoDoorGlobalIdx = useMemo(() => {
    const set = new Set<number>();
    if (!bundle) return set;
    for (const f of bundle.polygons.features) {
      const suppressed =
        ((f.properties as { suppressedAutoDoors?: number[] })
          .suppressedAutoDoors) ?? [];
      if (suppressed.length === 0) continue;
      const list = doorsByPolyId.get(f.properties.id) ?? [];
      for (const d of list) {
        if (
          d._origIdx !== undefined &&
          d._globalIdx !== undefined &&
          suppressed.includes(d._origIdx)
        ) {
          set.add(d._globalIdx);
        }
      }
    }
    return set;
  }, [bundle, doorsByPolyId]);

  /**
   * Sprint 0.19 — Fenster pro Polygon aus OCR-Beschriftungen.
   * Pattern: "X,XX x Y,YY" (Breite × Höhe in m) typischerweise außerhalb
   * der Außenwand notiert. BRH (Brüstungshöhe) ist ein separates Label
   * direkt darunter/darüber. Wir matchen Fenster-Labels via Pattern,
   * suchen das nächstgelegene BRH-Label im Umkreis von 30pt und ordnen
   * das Paar dem nächstgelegenen Polygon zu (≤ 60pt Boundary-Distanz).
   */
  const windowsByPolyId = useMemo(() => {
    const m = new Map<
      string,
      Array<{
        widthCm: number;
        heightCm: number;
        brhCm: number | null;
        rawText: string;
        /** OCR-Label-Anchor in PDF-Pt — für Highlight im Plan (Wand-Position). */
        anchor: { x: number; y: number };
      }>
    >();
    if (!bundle?.primitives) return m;
    const labels = bundle.primitives.pages[0]?.textLabels ?? [];
    const SIZE_RE = /^\s*(\d{1,2}[,.]\d{1,2})\s*[xX×]\s*(\d{1,2}[,.]\d{1,2})\s*$/;
    const BRH_RE = /^\s*BRH\s*(\d{1,2}[,.]?\d{0,2})\s*$/i;
    const sizeLabels = labels
      .map((l) => {
        const ma = SIZE_RE.exec(l.text.trim());
        if (!ma) return null;
        const w = parseFloat(ma[1].replace(",", "."));
        const h = parseFloat(ma[2].replace(",", "."));
        if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
        if (w < 0.4 || w > 6 || h < 0.4 || h > 4) return null;
        return { label: l, w, h };
      })
      .filter((x): x is { label: ExtractedTextLabel; w: number; h: number } => !!x);
    const brhLabels = labels
      .map((l) => {
        const ma = BRH_RE.exec(l.text.trim());
        if (!ma) return null;
        const v = parseFloat(ma[1].replace(",", "."));
        if (!Number.isFinite(v)) return null;
        // BRH-Werte sind manchmal in m (0,90), manchmal in cm (90).
        // Heuristik: Werte <= 5 sind m → ×100, sonst cm.
        const cm = v <= 5 ? v * 100 : v;
        return { label: l, brhCm: cm };
      })
      .filter(
        (x): x is { label: ExtractedTextLabel; brhCm: number } => !!x
      );

    for (const sz of sizeLabels) {
      // BRH-Label suchen im Umkreis (typisch ~10pt darüber/darunter).
      let brhCm: number | null = null;
      let bestBrhDist = Infinity;
      for (const b of brhLabels) {
        const d = Math.hypot(
          b.label.anchor.x - sz.label.anchor.x,
          b.label.anchor.y - sz.label.anchor.y
        );
        if (d < bestBrhDist && d < 30) {
          bestBrhDist = d;
          brhCm = b.brhCm;
        }
      }

      // Polygon-Zuordnung: das nächstgelegene Polygon.
      let bestId: string | null = null;
      let bestPolyDist = Infinity;
      for (const f of bundle.polygons.features) {
        const ring = f.geometry.coordinates[0];
        const d = distancePointToRing(sz.label.anchor, ring);
        if (d < bestPolyDist) {
          bestPolyDist = d;
          bestId = f.properties.id;
        }
      }
      if (!bestId || bestPolyDist > 60) continue;
      const widthCm = Math.round(sz.w * 100);
      const heightCm = Math.round(sz.h * 100);
      const brhRound = brhCm !== null ? Math.round(brhCm) : null;
      // Dedup: gleiche Maße + BRH innerhalb des Polygons → ein Eintrag.
      // Architekten beschriften Fenster manchmal beidseitig der Wand
      // (außen und innen) — beide Labels matchen das gleiche Fenster.
      // Plus: Durchsichts-Fenster zwischen 2 Räumen werden über die
      // 1:1-Polygon-Zuordnung (nächstgelegenes Polygon) bereits
      // automatisch nur einem Raum zugeordnet — kein Doppelzählen.
      const list = m.get(bestId) ?? [];
      const dup = list.find(
        (e) =>
          e.widthCm === widthCm &&
          e.heightCm === heightCm &&
          e.brhCm === brhRound
      );
      if (dup) continue;
      list.push({
        widthCm,
        heightCm,
        brhCm: brhRound,
        rawText: sz.label.text.trim(),
        anchor: { x: sz.label.anchor.x, y: sz.label.anchor.y },
      });
      m.set(bestId, list);
    }
    return m;
  }, [bundle]);

  /**
   * Sprint 0.19 — pro Polygon einen 4-Status berechnen, der das Rendering
   * steuert. Quellen: dimensions-report.roomNgf (NGF-Match) + rooms.json
   * (isInsideBuilding aus dem room-classifier).
   *
   *   - "matched":         Polygon hat genau 1 NGF-Stempel → fertig, keine User-Aktion.
   *   - "splitCandidate":  Polygon hat 2+ NGF-Stempel → muss gesplittet werden (gelb/⚠).
   *   - "outdoor":         isInsideBuilding=false → Stellplatz/Symbol/Außenfläche (grau/🚫).
   *   - "pending":         Polygon ist innen aber ohne NGF-Match → User soll prüfen (orange/✏).
   */
  type PolyStatus =
    | "matched"
    | "splitCandidate"
    | "outdoor"
    | "pending"
    | "building"
    | "gala";
  // Sprint #3 follow-up (2026-05-04) — Polygone mit outdoor-Label gelten
  // als outdoor unabhängig vom Centroid-im-HAUS-Test. Hintergrund: das HAUS-
  // Polygon kann (auch nach AW-Layer-Ring) Balkone/Loggien/Dachterrassen
  // geometrisch miteinschließen weil sie unter dem Dach liegen oder in
  // OG/DG-Plänen über der Geschoss-Außenkontur liegen — semantisch sind
  // sie aber Außenbereich (GaLa-Codes statt Boden/Decke/Wände).
  // Identisch zu `DEFAULT_OUTDOOR_STAMP_PATTERN` im dim-resolver, damit
  // Inspector-Mode und Auto-Polygon-Erzeugung konsistent klassifizieren.
  const OUTDOOR_LABEL_PATTERN =
    /balkon|terrasse|dachterrasse|loggia|stellplatz|carport|garage|garten|hof|pergola|veranda/i;
  const polyStatus = useMemo(() => {
    const m = new Map<string, PolyStatus>();
    if (!bundle) return m;
    const insideById = new Map<string, boolean>();
    for (const r of bundle.rooms?.rooms ?? []) {
      insideById.set(
        r.id,
        // default true wenn das Feld in legacy 04-rooms.json fehlt
        r.isInsideBuilding === undefined ? true : !!r.isInsideBuilding
      );
    }
    for (const f of bundle.polygons.features) {
      const id = f.properties.id;
      // Sprint 0.19 / Punkt 4 — Haus-Polygon erkennt sich selbst.
      if (f.properties.isBuilding) {
        m.set(id, "building");
        continue;
      }
      // Sprint #6 (2026-05-05) — GALA-Container erkennt sich selbst.
      if (f.properties.isGaLaArea) {
        m.set(id, "gala");
        continue;
      }
      const ngfStatus = splitMap.get(id);
      const insideFlag = insideById.get(id) ?? true;
      const labelStr = String(f.properties.label ?? "");
      const labelLooksOutdoor = OUTDOOR_LABEL_PATTERN.test(labelStr);
      // Label-Override: bei Balkon/Terrasse-/etc.-Label → outdoor, auch
      // wenn der Centroid noch im HAUS-Polygon liegt.
      const isInside = labelLooksOutdoor ? false : insideFlag;
      let st: PolyStatus;
      if (!isInside) st = "outdoor";
      else if (ngfStatus === "matched") st = "matched";
      else if (ngfStatus === "splitCandidate") st = "splitCandidate";
      else st = "pending";
      m.set(id, st);
    }
    return m;
  }, [bundle, splitMap]);

  /** Counter für die "Räume (X)"-Box: innen vs. außen vs. Haus vs. GaLa. */
  const roomCounts = useMemo(() => {
    let inside = 0;
    let outside = 0;
    let building = 0;
    let gala = 0;
    for (const st of polyStatus.values()) {
      if (st === "building") building++;
      else if (st === "gala") gala++;
      else if (st === "outdoor") outside++;
      else inside++;
    }
    return {
      inside,
      outside,
      building,
      gala,
      total: inside + outside,
    };
  }, [polyStatus]);

  /**
   * Sprint 0.19 / Punkt 4 — Map<wallId, Set<polygonId>>: zeigt welche Polygone
   * sich eine physische Wand teilen (= geteilte Wand zwischen 2 Räumen).
   *
   * Nutzt **toleranz-basiertes Cluster-Matching** (Helper aus shared): zwei
   * Wände gelten als geteilt wenn sie kollinear, parallel mit Distanz <
   * Wandstärke und Längs-Überlappung > 50%. Das löst das Floodfill-Polygon-
   * Drift-Problem, wo angrenzende Räume leicht verschobene Mittellinien
   * haben.
   */
  const wallSharers = useMemo(() => {
    const m = new Map<string, Set<string>>();
    if (!bundle) return m;
    // Kollineare Cluster-Matching inline (clusterSharedWalls aus shared liefe
    // sonst durch das Frontend-Bundle nicht — Helper ist Pure-TS, hier
    // dupliziert für direkte Nutzung).
    interface FlatWall {
      polygonId: string;
      startPt: { x: number; y: number };
      endPt: { x: number; y: number };
      wallId?: string;
    }
    const flat: FlatWall[] = [];
    for (const f of bundle.polygons.features) {
      for (const w of f.properties.walls ?? []) {
        flat.push({
          polygonId: f.properties.id,
          startPt: w.startPt,
          endPt: w.endPt,
          wallId: w.wallId,
        });
      }
    }
    const maxThicknessPt = 30;
    const maxAngleRad = (5 * Math.PI) / 180;
    const minOverlapRatio = 0.5;
    const cluster = new Array<number>(flat.length).fill(-1);
    const clusterPolygons: Set<string>[] = [];
    const sharedFn = (i: number, j: number): boolean => {
      const a = flat[i];
      const b = flat[j];
      if (a.polygonId === b.polygonId) return false;
      const ax = a.endPt.x - a.startPt.x;
      const ay = a.endPt.y - a.startPt.y;
      const bx = b.endPt.x - b.startPt.x;
      const by = b.endPt.y - b.startPt.y;
      const lenA = Math.hypot(ax, ay);
      const lenB = Math.hypot(bx, by);
      if (lenA < 1 || lenB < 1) return false;
      const angleA = Math.atan2(ay, ax);
      const angleB = Math.atan2(by, bx);
      let dAng = Math.abs(angleA - angleB);
      while (dAng > Math.PI) dAng -= Math.PI;
      if (dAng > maxAngleRad && Math.PI - dAng > maxAngleRad) return false;
      const ux = ax / lenA;
      const uy = ay / lenA;
      const nx = -uy;
      const ny = ux;
      const midA = { x: (a.startPt.x + a.endPt.x) / 2, y: (a.startPt.y + a.endPt.y) / 2 };
      const midB = { x: (b.startPt.x + b.endPt.x) / 2, y: (b.startPt.y + b.endPt.y) / 2 };
      const dx = midB.x - midA.x;
      const dy = midB.y - midA.y;
      const perpDist = Math.abs(dx * nx + dy * ny);
      if (perpDist > maxThicknessPt) return false;
      const tB1 = (b.startPt.x - a.startPt.x) * ux + (b.startPt.y - a.startPt.y) * uy;
      const tB2 = (b.endPt.x - a.startPt.x) * ux + (b.endPt.y - a.startPt.y) * uy;
      const tBLow = Math.min(tB1, tB2);
      const tBHigh = Math.max(tB1, tB2);
      const overlapLow = Math.max(0, tBLow);
      const overlapHigh = Math.min(lenA, tBHigh);
      const overlap = Math.max(0, overlapHigh - overlapLow);
      const minLen = Math.min(lenA, lenB);
      return overlap / minLen >= minOverlapRatio;
    };
    for (let i = 0; i < flat.length; i++) {
      if (cluster[i] !== -1) continue;
      const cid = clusterPolygons.length;
      clusterPolygons.push(new Set([flat[i].polygonId]));
      cluster[i] = cid;
      const queue = [i];
      while (queue.length > 0) {
        const j = queue.shift()!;
        for (let k = 0; k < flat.length; k++) {
          if (cluster[k] !== -1) continue;
          if (sharedFn(j, k)) {
            cluster[k] = cid;
            clusterPolygons[cid].add(flat[k].polygonId);
            queue.push(k);
          }
        }
      }
    }
    flat.forEach((w, i) => {
      if (!w.wallId) return;
      m.set(w.wallId, clusterPolygons[cluster[i]]);
    });
    return m;
  }, [bundle]);

  const selectedFeature: PolygonFeature | null = useMemo(() => {
    if (!bundle || !selectedId) return null;
    return bundle.polygons.features.find((f) => f.properties.id === selectedId) ?? null;
  }, [bundle, selectedId]);

  const selectedNgfMatch = useMemo(() => {
    if (!bundle?.dimensions || !selectedId) return null;
    return (
      bundle.dimensions.roomNgf.polygonMatches.find(
        (pm) => pm.polygonId === selectedId
      ) ?? null
    );
  }, [bundle, selectedId]);

  const selectedClassifiedRoom = useMemo(() => {
    if (!bundle?.rooms || !selectedId) return null;
    return bundle.rooms.rooms.find((r) => r.id === selectedId) ?? null;
  }, [bundle, selectedId]);

  // ---- Edit-Modus: Helpers ----
  const cancelEdit = () => {
    setEditMode("view");
    setSplitTarget(null);
    setSplitPoints([]);
    setSplitPreview(null);
    setSplitError(null);
    setMergeFirstId(null);
    setMergeSecondId(null);
    setMergePreview(null);
    setMergeError(null);
    setWallStart(null);
    setWallError(null);
    setVertexEditPolygonId(null);
    setEditingRing(null);
    setDraggingVertexIdx(null);
    setVertexEditError(null);
    setWallEditError(null);
    setWallSegmentTargetPolygonId(null);
    setWallSegmentStart(null);
    setWallEditPolygonId(null);
    setWallEditIdx(null);
    setWallEditEndpoints(null);
    setDraggingWallEnd(null);
  };
  // Backwards compatibility for the existing "Abbrechen"-Button name.
  const cancelSplit = cancelEdit;

  // ---- Sprint 0.19 — Wand-Strich-Tool ----
  const startDrawWall = () => {
    cancelEdit();
    setEditMode("drawWall");
  };

  const saveUserWall = async (
    from: { x: number; y: number },
    to: { x: number; y: number }
  ) => {
    setWallSaving(true);
    setWallError(null);
    try {
      // Wand wird gespeichert OHNE rerunPolygons — wir sammeln Wände erst,
      // der User triggert den Re-Run dann manuell mit dem "Pipeline neu rechnen"-
      // Button. Spart Sekunden wenn 5 Wände hintereinander gezeichnet werden.
      await api.multiagentAddUserWall(runId, { from, to });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      // Gleich nochmal in den drawWall-Mode springen, falls der User mehrere
      // Wände hintereinander zeichnen will. Reset wallStart + Vorschau.
      setWallStart(null);
      setEditMode("drawWall");
    } catch (err) {
      setWallError(err instanceof Error ? err.message : String(err));
    } finally {
      setWallSaving(false);
    }
  };

  // deleteUserWall + rerunPipelineWithUserWalls entfernt mit der Schnitte-
  // Sidebar (Sprint UX-Inspector 2026-05-06). Schnitte werden weiter über die
  // Tool-Rail (Hotkey W) gezeichnet, der saveUserWall-Pfad fügt automatisch
  // den polygons-/dimensions-/rooms-Re-Run an. Lösch-Flow + manueller Re-Run
  // kommen wieder rein, wenn Frank dort eine Sidebar einführt.

  // ---- Sprint 0.19 — Polygon-Vertex-Drag ----
  const startVertexEdit = (polygonId: string) => {
    if (!bundle) return;
    const f = bundle.polygons.features.find(
      (ff) => ff.properties.id === polygonId
    );
    if (!f) return;
    cancelEdit();
    setVertexEditPolygonId(polygonId);
    // Tiefkopie damit Drag-Updates den Bundle nicht mutieren.
    setEditingRing(f.geometry.coordinates[0].map((p) => [p[0], p[1]]));
    setEditMode("vertexEdit");
  };

  const saveVertexEdit = async () => {
    if (!vertexEditPolygonId || !editingRing) return;
    setVertexEditSaving(true);
    setVertexEditError(null);
    try {
      const res = await api.multiagentEditPolygonRing(runId, {
        polygonId: vertexEditPolygonId,
        ring: editingRing,
      });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      cancelEdit();
      // Sprint #3 follow-up — kurze Info wenn Backend Walls auto-rematched
      // hat (HAUS-Default). Bei Raum-Polygonen ist `used` false und der
      // User muss bewusst „Neu berechnen" im Inspector klicken.
      if (res.wallsRematchUsed) {
        setWallRematchInfo(
          `Wände automatisch neu zugeordnet: ${res.wallsRematched}/${res.wallsRematchEdgeCount} Polygon-Kanten gematcht.`
        );
        setTimeout(() => setWallRematchInfo(null), 4500);
      }
    } catch (err) {
      setVertexEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setVertexEditSaving(false);
    }
  };

  /**
   * Sprint #3 follow-up — manueller Wand-Rematch-Trigger für ein einzelnes
   * Polygon (Inspector-Button). Backend matched die Polygon-Edges gegen die
   * Sprint-W-Cluster und ersetzt walls[]. codeAssignments + openings bleiben.
   */
  const rematchWallsForPolygon = async (polygonId: string) => {
    if (wallRematchSaving) return;
    setWallRematchSaving(true);
    setWallRematchInfo(null);
    try {
      const res = await api.multiagentRematchWalls(runId, {
        polygonIds: [polygonId],
      });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      const r = res.results.find((x) => x.polygonId === polygonId);
      if (r && r.used) {
        const delta = r.wallsAfter - r.wallsBefore;
        const sign = delta >= 0 ? "+" : "";
        setWallRematchInfo(
          `${polygonId}: ${r.wallsAfter} Wände aus ${r.edgeCount} Polygon-Kanten (${sign}${delta} ggü. vorher).`
        );
      } else {
        setWallRematchInfo(
          `${polygonId}: keine Cluster-Matches gefunden — alte Wände bleiben.`
        );
      }
      setTimeout(() => setWallRematchInfo(null), 5000);
    } catch (err) {
      setWallRematchInfo(
        `Fehler: ${err instanceof Error ? err.message : String(err)}`
      );
      setTimeout(() => setWallRematchInfo(null), 6000);
    } finally {
      setWallRematchSaving(false);
    }
  };

  // ---- Sprint 0.19 — Wand-CRUD Helpers ----
  const deleteWall = async (polygonId: string, wallIdx: number) => {
    if (wallEditSaving) return;
    setWallEditSaving(true);
    setWallEditError(null);
    try {
      await api.multiagentDeleteWall(runId, polygonId, wallIdx);
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setHighlightedWallIdx(null);
    } catch (err) {
      setWallEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setWallEditSaving(false);
    }
  };

  const startDrawWallSegment = (polygonId: string) => {
    cancelEdit();
    setWallSegmentTargetPolygonId(polygonId);
    setEditMode("drawWallSegment");
  };

  const startEditWallEndpoints = (polygonId: string, wallIdx: number) => {
    if (!bundle) return;
    const f = bundle.polygons.features.find(
      (ff) => ff.properties.id === polygonId
    );
    const w = f?.properties.walls?.[wallIdx];
    if (!w) return;
    cancelEdit();
    setWallEditPolygonId(polygonId);
    setWallEditIdx(wallIdx);
    setWallEditEndpoints({ startPt: { ...w.startPt }, endPt: { ...w.endPt } });
    setEditMode("editWallEndpoints");
  };

  const saveWallEndpoints = async () => {
    if (
      !wallEditPolygonId ||
      wallEditIdx === null ||
      !wallEditEndpoints ||
      wallEditSaving
    )
      return;
    setWallEditSaving(true);
    setWallEditError(null);
    try {
      await api.multiagentPatchWall(runId, wallEditPolygonId, wallEditIdx, {
        startPt: wallEditEndpoints.startPt,
        endPt: wallEditEndpoints.endPt,
      });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      cancelEdit();
    } catch (err) {
      setWallEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setWallEditSaving(false);
    }
  };

  const saveNewWallSegment = async (
    from: { x: number; y: number },
    to: { x: number; y: number }
  ) => {
    if (!wallSegmentTargetPolygonId || wallEditSaving) return;
    setWallEditSaving(true);
    setWallEditError(null);
    try {
      const res = await api.multiagentAddWall(runId, wallSegmentTargetPolygonId, {
        startPt: from,
        endPt: to,
      });
      if (res.mirroredToList && res.mirroredToList.length > 0) {
        const others = res.mirroredToList.filter((id) => id !== wallSegmentTargetPolygonId);
        if (others.length > 0) {
          setMirrorBanner({
            text: `Wand auf ${others.length} weitere(s) Polygon(e) gespiegelt: ${others.join(", ")}`,
            ts: Date.now(),
          });
        }
      }
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      cancelEdit();
    } catch (err) {
      setWallEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setWallEditSaving(false);
    }
  };

  // ---- Sprint 0.19 / Punkt 4 — Material-Editor-Modal ----
  const openMaterialEditor = (polygonId: string, wallIdx: number) => {
    if (!bundle) return;
    const f = bundle.polygons.features.find(
      (ff) => ff.properties.id === polygonId
    );
    const w = f?.properties.walls?.[wallIdx];
    if (!w) return;
    setMaterialEditorPolygonId(polygonId);
    setMaterialEditorWallIdx(wallIdx);
    setMaterialEditorLayers(
      w.materials.map((m) => ({
        gewerk: m.gewerk as LayerForm["gewerk"],
        preset: m.preset as LayerForm["preset"],
        materialName: m.materialName,
        dickeMm: m.dickeMm,
        putzSides: m.putzSides,
      }))
    );
    // Plan-Maß initial = aktuelle Wand-Stärke (auf 5mm gerundet für DIN-Match).
    setMaterialEditorPlanThicknessMm(Math.round(w.totalThicknessMm / 5) * 5);
    // Stufe 1 — existierende Codes lesen (Property aus polygons.geojson).
    const wAny = w as unknown as Record<string, unknown>;
    setMaterialEditorWallTypeCode(
      typeof wAny.wallTypeCode === "string" ? wAny.wallTypeCode : null
    );
    setMaterialEditorFacadeCode(
      typeof wAny.facadeCode === "string" ? wAny.facadeCode : null
    );
    // Sprint A.8 — bestehende codeAssignments laden (Stufe 2). Wenn das Feld
    // fehlt, starten wir mit leerer Liste; der Code-Picker fügt User-Codes
    // direkt mit source=user-manual + status=entwurf hinzu.
    // Sprint #5 follow-up — bestehende CodeAssignments durch das Catalog-
    // Prefill laufen lassen, damit ältere Daten ohne nachweise-Defaults
    // sich beim Öffnen automatisch reparieren. User-Eingaben (geprueft etc.)
    // bleiben dabei intakt.
    const loadedAssignments: CodeAssignment[] = Array.isArray(
      wAny.codeAssignments
    )
      ? (wAny.codeAssignments as CodeAssignment[])
      : [];
    // wallTypeCode aus Stufe-1 als Auto-Assignment ergänzen, falls dieser
    // Code noch keine eigene CodeAssignment hat (= „Vom Wandtyp gesetzte
    // Codes erscheinen automatisch" im Wizard-S3).
    const wallTypeCodeStr =
      typeof wAny.wallTypeCode === "string" ? wAny.wallTypeCode : null;
    const facadeCodeStr =
      typeof wAny.facadeCode === "string" ? wAny.facadeCode : null;
    const ensureAuto = (code: string | null, list: CodeAssignment[]) => {
      if (!code) return list;
      if (list.some((c) => c.code === code)) return list;
      const auto: CodeAssignment = {
        id: makeCodeAssignmentId(),
        code,
        source: "auto-thickness",
        zugewiesenAm: new Date().toISOString(),
        nachweise: {},
        status: "entwurf",
      };
      return [...list, auto];
    };
    const withAuto = ensureAuto(
      facadeCodeStr,
      ensureAuto(wallTypeCodeStr, loadedAssignments)
    );
    const enriched = withAuto.map(enrichNachweiseFromCatalog);
    setMaterialEditorCodeAssignments(enriched);
    setShowCodePicker(false);
    setExpandedCodeAssignmentId(null);
    // Default-Tab: bei HAUS-Polygon Außenwand, sonst Innenwand.
    const isHausPolygon = (
      f?.properties as { isBuilding?: boolean } | undefined
    )?.isBuilding;
    setMaterialEditorCatalogTab(isHausPolygon ? "outer" : "inner");
    // Sprint #5 — Wizard-State auf S1 zurücksetzen + Surface-Slots aus
    // bestehenden oberflaeche-Layern rekonstruieren (Roundtrip-fähig).
    setMaterialWizardStep(1);
    setMaterialWizardLayersOpen(false);
    const surfaceFromLayers = (
      _slot: SurfaceSlot,
      keywords: RegExp
    ): { on: boolean; code: string | null } => {
      const found = w.materials.find(
        (m) =>
          m.gewerk === ("oberflaeche" as unknown as LayerForm["gewerk"]) &&
          keywords.test(m.materialName ?? "")
      );
      if (!found) return { on: false, code: null };
      const fAny = found as unknown as { code?: string };
      return { on: true, code: typeof fAny.code === "string" ? fAny.code : null };
    };
    const tap = surfaceFromLayers("tapete", /tapete|raufaser/i);
    const ans = surfaceFromLayers("anstrich", /anstrich|dispersion/i);
    const fli = surfaceFromLayers("fliesen", /fliese/i);
    setMaterialWizardSurfaceOn({
      tapete: tap.on,
      anstrich: ans.on,
      fliesen: fli.on,
    });
    setMaterialWizardSurfaceCode({
      tapete: tap.code,
      anstrich: ans.code,
      fliesen: fli.code,
    });
    setMaterialEditorOpen(true);
    setWallEditError(null);
  };

  /** Setzt die Layer-Liste auf einen Systemwand-Vorschlag (überschreibt aktuelle). */
  const applySystemWallPreset = (preset: SystemWallPreset) => {
    setMaterialEditorLayers(
      preset.layers.map((l) => ({
        gewerk: l.gewerk,
        preset: l.preset,
        materialName: l.materialName,
        dickeMm: l.dickeMm,
        putzSides: l.putzSides,
      }))
    );
  };

  const closeMaterialEditor = () => {
    setMaterialEditorOpen(false);
    setMaterialEditorPolygonId(null);
    setMaterialEditorWallIdx(null);
    setMaterialEditorLayers([]);
    setMaterialEditorWallTypeCode(null);
    setMaterialEditorFacadeCode(null);
    setMaterialEditorCodeAssignments([]);
    setShowCodePicker(false);
    setExpandedCodeAssignmentId(null);
  };

  // ---- Sprint A.8 — codeAssignments-Helpers ----
  /** Generiert eine stable ID für eine neue CodeAssignment. */
  const makeCodeAssignmentId = (): string => {
    if (
      typeof globalThis.crypto !== "undefined" &&
      typeof globalThis.crypto.randomUUID === "function"
    ) {
      return `ca-${globalThis.crypto.randomUUID().slice(0, 8)}`;
    }
    return `ca-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  };

  /** Fügt eine neue CodeAssignment für `code` hinzu (source=user-manual,
   *  status=entwurf, leere Nachweise). Idempotent: wenn der Code schon
   *  zugewiesen ist, passiert nichts. */
  /**
   * Sprint #5 follow-up — Default-Nachweise aus dem Catalog vorbefüllen.
   * Pro `benoetigteFachplanung`-Eintrag wird ein {@link NachweisStatus} mit
   * `status: "fehlt"` angelegt, falls nicht schon vorhanden. Idempotent —
   * darf auch auf bestehende CodeAssignments mit teilweise gefüllten
   * Nachweisen angewandt werden, ohne User-Eingaben zu überschreiben.
   */
  const enrichNachweiseFromCatalog = (
    ca: CodeAssignmentType
  ): CodeAssignmentType => {
    const entry = getCatalogEntryByCode(ca.code);
    const required = entry?.benoetigteFachplanung ?? [];
    if (required.length === 0) return ca;
    const nachweise = { ...ca.nachweise };
    let mutated = false;
    for (const fp of required) {
      if (!nachweise[fp]) {
        nachweise[fp] = { status: "fehlt" };
        mutated = true;
      }
    }
    return mutated ? { ...ca, nachweise } : ca;
  };

  const addCodeAssignment = (code: string) => {
    setMaterialEditorCodeAssignments((prev) => {
      if (prev.some((ca) => ca.code === code)) return prev;
      const ca: CodeAssignment = enrichNachweiseFromCatalog({
        id: makeCodeAssignmentId(),
        code,
        source: "user-manual",
        zugewiesenAm: new Date().toISOString(),
        nachweise: {},
        status: "entwurf",
      });
      return [...prev, ca];
    });
    setShowCodePicker(false);
  };

  const removeCodeAssignment = (id: string) => {
    setMaterialEditorCodeAssignments((prev) =>
      prev.filter((ca) => ca.id !== id)
    );
    if (expandedCodeAssignmentId === id) setExpandedCodeAssignmentId(null);
  };

  const setCodeAssignmentStatus = (id: string, status: CodeAssignmentStatus) => {
    setMaterialEditorCodeAssignments((prev) =>
      prev.map((ca) => (ca.id === id ? { ...ca, status } : ca))
    );
  };

  const setCodeAssignmentNachweis = (
    id: string,
    fachplaner: Fachplaner,
    status: NachweisStatus["status"] | null
  ) => {
    setMaterialEditorCodeAssignments((prev) =>
      prev.map((ca) => {
        if (ca.id !== id) return ca;
        const nachweise = { ...ca.nachweise };
        if (status === null) {
          delete nachweise[fachplaner];
        } else {
          nachweise[fachplaner] = {
            ...(nachweise[fachplaner] ?? {}),
            status,
            pruefungAm: new Date().toISOString(),
          };
        }
        return { ...ca, nachweise };
      })
    );
  };

  /** Stufe 1 — Wandtyp aus dem Katalog wählen: Schichten überschreiben +
   *  Code-Felder setzen. Default-Fassade wird automatisch mit übernommen. */
  const applyWallTypeFromCatalog = (code: string) => {
    if (!wallCatalog) return;
    const w = wallCatalog.walls.find((x) => x.code === code);
    if (!w) return;
    setMaterialEditorLayers(
      w.defaultLayers.map((l) => ({
        gewerk: l.gewerk as LayerForm["gewerk"],
        preset: l.preset as LayerForm["preset"],
        materialName: l.materialName,
        dickeMm: l.dickeMm,
        putzSides: l.putzSides,
      }))
    );
    setMaterialEditorWallTypeCode(w.code);
    setMaterialEditorFacadeCode(w.defaultFacadeCode ?? null);
    // Sprint #5 follow-up — Wandtyp-Klick legt automatisch eine
    // CodeAssignment für den Wandtyp-Code an (falls nicht schon vorhanden),
    // damit S3 (Fachplaner) sofort die Default-Nachweise aus dem Catalog
    // zeigt. Source = auto-thickness (Plan-Maß-Match war der Auslöser).
    setMaterialEditorCodeAssignments((prev) => {
      const existing = prev.some((c) => c.code === w.code);
      const codes = [...prev];
      if (!existing) {
        codes.push(
          enrichNachweiseFromCatalog({
            id: makeCodeAssignmentId(),
            code: w.code,
            source: "auto-thickness",
            zugewiesenAm: new Date().toISOString(),
            nachweise: {},
            status: "entwurf",
          })
        );
      }
      // Default-Fassade ebenfalls als Auto-Code dazu nehmen, falls vorhanden.
      if (
        w.defaultFacadeCode &&
        !codes.some((c) => c.code === w.defaultFacadeCode)
      ) {
        codes.push(
          enrichNachweiseFromCatalog({
            id: makeCodeAssignmentId(),
            code: w.defaultFacadeCode,
            source: "auto-thickness",
            zugewiesenAm: new Date().toISOString(),
            nachweise: {},
            status: "entwurf",
          })
        );
      }
      return codes;
    });
  };

  const updateMaterialLayer = (
    idx: number,
    patch: Partial<LayerForm>
  ): void => {
    setMaterialEditorLayers((prev) =>
      prev.map((l, i) => (i === idx ? { ...l, ...patch } : l))
    );
  };

  const removeMaterialLayer = (idx: number) => {
    setMaterialEditorLayers((prev) => prev.filter((_, i) => i !== idx));
  };

  const addMaterialLayer = (
    preset: "rohbau" | "putz" | "trockenbau" | "oberflaeche"
  ) => {
    const defaults: Record<string, LayerForm> = {
      rohbau: {
        gewerk: "rohbau",
        preset: "mauerwerk",
        dickeMm: 175,
      },
      putz: {
        gewerk: "putz",
        preset: "custom",
        materialName: "Putz",
        dickeMm: 15,
        putzSides: 1,
      },
      trockenbau: {
        gewerk: "trockenbau",
        preset: "trockenbau",
        dickeMm: 100,
      },
      // Sprint A.11 (2026-05-02): Oberflächen-Schicht für Tapete/Anstrich/Lasur.
      // Default = Vlies-Tapete 0,3 mm. Nutzer kann materialName + dickeMm im
      // Editor anpassen (z.B. "Anstrich Dispersion" mit dickeMm: 0).
      oberflaeche: {
        gewerk: "oberflaeche",
        preset: "custom",
        materialName: "Vlies-Tapete",
        dickeMm: 0.3,
      },
    };
    setMaterialEditorLayers((prev) => [...prev, defaults[preset]]);
  };

  const saveMaterialEditor = async () => {
    if (
      !materialEditorPolygonId ||
      materialEditorWallIdx === null ||
      wallEditSaving
    )
      return;
    setWallEditSaving(true);
    setWallEditError(null);
    try {
      // Sprint #5 — vor dem Save Surface-Slots als oberflaeche-Layer ans
      // Material-Stack anhängen (Idempotenz: erst alte oberflaeche-Layer mit
      // Surface-Pattern raus, dann die aktuellen Toggles wieder rein).
      const surfacePatterns: Record<SurfaceSlot, RegExp> = {
        tapete: /tapete|raufaser/i,
        anstrich: /anstrich|dispersion|lasur/i,
        fliesen: /fliese/i,
      };
      const cleanedLayers = materialEditorLayers.filter((l) => {
        if (l.gewerk !== ("oberflaeche" as unknown as LayerForm["gewerk"]))
          return true;
        const name = l.materialName ?? "";
        return !(
          surfacePatterns.tapete.test(name) ||
          surfacePatterns.anstrich.test(name) ||
          surfacePatterns.fliesen.test(name)
        );
      });
      const surfaceLayers: LayerForm[] = (
        ["tapete", "anstrich", "fliesen"] as const
      )
        .filter((slot) => materialWizardSurfaceOn[slot])
        .map((slot) => {
          const code = materialWizardSurfaceCode[slot];
          const entry = code
            ? (DEFAULT_CATALOG as CatalogEntry[]).find((e) => e.code === code)
            : null;
          return {
            gewerk: "oberflaeche" as unknown as LayerForm["gewerk"],
            preset: "custom" as LayerForm["preset"],
            materialName:
              entry?.shortLabel ??
              (slot === "tapete"
                ? "Tapete"
                : slot === "anstrich"
                  ? "Anstrich"
                  : "Fliese"),
            dickeMm: slot === "fliesen" ? 8 : 1,
            ...(code ? { code } : {}),
          } as LayerForm;
        });
      const layersForSave = [...cleanedLayers, ...surfaceLayers];

      await api.multiagentPatchWall(
        runId,
        materialEditorPolygonId,
        materialEditorWallIdx,
        {
          materials: layersForSave,
          wallTypeCode: materialEditorWallTypeCode,
          facadeCode: materialEditorFacadeCode,
          codeAssignments:
            materialEditorCodeAssignments.length > 0
              ? materialEditorCodeAssignments
              : null,
        }
      );
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      closeMaterialEditor();
    } catch (err) {
      setWallEditError(err instanceof Error ? err.message : String(err));
    } finally {
      setWallEditSaving(false);
    }
  };

  // Sprint 0.20 — Öffnungs-Editor (Tür/Fenster CRUD)
  const openOpeningEditorForCreate = (
    polygonId: string,
    type: "door" | "window",
    prefilledPoints?: {
      startPt: { x: number; y: number };
      endPt: { x: number; y: number };
    }
  ) => {
    setOpeningEditorTarget({ polygonId, idx: null });
    setOpeningEditorType(type);
    let widthM = type === "door" ? 0.885 : 1.26;
    if (prefilledPoints) {
      const dxPt = prefilledPoints.endPt.x - prefilledPoints.startPt.x;
      const dyPt = prefilledPoints.endPt.y - prefilledPoints.startPt.y;
      const lenPt = Math.hypot(dxPt, dyPt);
      // Pt → m via aktueller Maßstab
      const ratio = bundle?.classification.pages[0]?.scale?.ratio ?? 100;
      widthM = (lenPt * 25.4 * ratio) / 72 / 1000;
    }
    setOpeningEditorFields({
      widthM: widthM.toFixed(3),
      heightM: type === "door" ? "2.01" : "1.385",
      bruestungM: type === "window" ? "0.90" : "",
      label: "",
    });
    setOpeningEditorSegment(prefilledPoints ?? null);
    setOpeningEditorError(null);
    setOpeningEditorOpen(true);
  };

  // Sprint 0.20 — Picking-Workflow: User klickt + Tür/Fenster im Sidebar →
  // setEditMode("drawOpening") + openingPickContext gesetzt → User klickt 2
  // Punkte auf der Wand → handleSvgClick öffnet Modal mit prefilledPoints.
  const startDrawOpening = (
    polygonId: string,
    type: "door" | "window"
  ) => {
    setOpeningPickContext({ polygonId, type });
    setOpeningPickStart(null);
    setEditMode("drawOpening");
  };
  const openOpeningEditorForEdit = (
    polygonId: string,
    idx: number,
    existing: {
      type: "door" | "window";
      widthM: number;
      heightM?: number;
      bruestungM?: number;
      label?: string;
    }
  ) => {
    setOpeningEditorTarget({ polygonId, idx });
    setOpeningEditorType(existing.type);
    setOpeningEditorFields({
      widthM: String(existing.widthM ?? ""),
      heightM: String(existing.heightM ?? ""),
      bruestungM: String(existing.bruestungM ?? ""),
      label: existing.label ?? "",
    });
    setOpeningEditorError(null);
    setOpeningEditorOpen(true);
  };
  const closeOpeningEditor = () => {
    setOpeningEditorOpen(false);
    setOpeningEditorTarget(null);
    setOpeningEditorError(null);
    setOpeningEditorSegment(null);
    setOpeningPickContext(null);
    setOpeningPickStart(null);
  };
  const saveOpeningEditor = async () => {
    if (!openingEditorTarget) return;
    setOpeningEditorError(null);
    setOpeningEditorSaving(true);
    try {
      const w = Number(openingEditorFields.widthM.replace(",", "."));
      if (!Number.isFinite(w) || w <= 0 || w > 10) {
        throw new Error("Breite muss positive Zahl ≤ 10 m sein");
      }
      const h = Number(openingEditorFields.heightM.replace(",", "."));
      const hValid = Number.isFinite(h) && h > 0 && h <= 10;
      const br = Number(openingEditorFields.bruestungM.replace(",", "."));
      const brValid =
        openingEditorType === "window" &&
        Number.isFinite(br) &&
        br >= 0 &&
        br <= 5;
      const body = {
        widthM: w,
        ...(hValid ? { heightM: h } : {}),
        ...(brValid ? { bruestungM: br } : {}),
        ...(openingEditorFields.label.trim()
          ? { label: openingEditorFields.label.trim() }
          : {}),
        ...(openingEditorSegment
          ? {
              startPt: openingEditorSegment.startPt,
              endPt: openingEditorSegment.endPt,
            }
          : {}),
      };
      if (openingEditorTarget.idx === null) {
        await api.multiagentAddOpening(runId, openingEditorTarget.polygonId, {
          type: openingEditorType,
          ...body,
        });
      } else {
        await api.multiagentPatchOpening(
          runId,
          openingEditorTarget.polygonId,
          openingEditorTarget.idx,
          body
        );
      }
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      closeOpeningEditor();
    } catch (err) {
      setOpeningEditorError(err instanceof Error ? err.message : String(err));
    } finally {
      setOpeningEditorSaving(false);
    }
  };
  const deleteOpening = async (polygonId: string, idx: number) => {
    if (
      !window.confirm(
        "Diese Öffnung wirklich löschen? (Über ↶ Verlauf rückgängig)"
      )
    )
      return;
    try {
      await api.multiagentDeleteOpening(runId, polygonId, idx);
      const fresh = await loadBundle(runId);
      setBundle(fresh);
    } catch (err) {
      window.alert(
        "Löschen fehlgeschlagen: " +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  // Sprint 0.20 — Polygon komplett löschen
  const deletePolygon = async (polygonId: string) => {
    if (
      !window.confirm(
        `Polygon "${polygonId}" wirklich löschen? (Wände + Öffnungen + Mengen werden mitgelöscht; rückgängig über ↶ Verlauf)`
      )
    )
      return;
    try {
      await api.multiagentDeletePolygon(runId, polygonId);
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setSelectedId(null);
    } catch (err) {
      window.alert(
        "Polygon-Löschen fehlgeschlagen: " +
          (err instanceof Error ? err.message : String(err))
      );
    }
  };

  // Sprint 0.20 — Neuen Raum manuell zeichnen (n-Klick, Doppelklick zum Schließen)
  // F4 — isHaus=true erzeugt das HAUS-Polygon (Container für Außenwände).
  const startDrawPolygon = (isHaus = false) => {
    cancelEdit();
    setDrawPolygonPoints([]);
    setDrawPolygonIsHaus(isHaus);
    setEditMode("drawPolygon");
  };
  const cancelDrawPolygon = () => {
    setDrawPolygonPoints([]);
    setDrawPolygonIsHaus(false);
    setEditMode("view");
  };
  const finishDrawPolygon = async () => {
    if (drawPolygonPoints.length < 3) {
      window.alert(
        "Mindestens 3 Punkte für ein Polygon — Klick weitere Punkte oder ESC für Abbruch."
      );
      return;
    }
    setDrawPolygonSaving(true);
    try {
      const ring = drawPolygonPoints.map((p) => [p.x, p.y]);
      await api.multiagentAddPolygon(runId, {
        ring,
        ...(drawPolygonIsHaus ? { isBuilding: true } : {}),
      });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setDrawPolygonPoints([]);
      setDrawPolygonIsHaus(false);
      setEditMode("view");
    } catch (err) {
      window.alert(
        "Polygon-Anlegen fehlgeschlagen: " +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setDrawPolygonSaving(false);
    }
  };

  const startSplit = (polygonId: string) => {
    cancelEdit();
    setEditMode("splitPicking");
    setSplitTarget(polygonId);
  };

  /**
   * Wenn das aktuelle splitTarget ein splitCandidate-Polygon ist (genau 2
   * NGF-Anchors), berechne automatisch eine Trennlinie senkrecht zwischen
   * den beiden Anchors und zeige sie als splitPreview an.
   */
  const applyAutoSplitSuggestion = () => {
    if (!bundle || !splitTarget) return;
    const page = bundle.classification.pages[0];
    const match = bundle.dimensions?.roomNgf.polygonMatches.find(
      (m) => m.polygonId === splitTarget
    );
    if (!match || match.ngfStamps.length < 2) {
      setSplitError(
        "Auto-Vorschlag braucht 2 NGF-Stempel im Polygon. Bitte manuell zwei Punkte setzen."
      );
      return;
    }
    const anchors = match.ngfStamps.slice(0, 2).map((s) => s.anchor);
    const ngfValues = match.ngfStamps.slice(0, 2).map((s) => s.ngfM2) as [
      number,
      number,
    ];
    const targetFeature0 = bundle.polygons.features.find(
      (f) => f.properties.id === splitTarget
    );
    if (!targetFeature0) return;
    const ring0 = targetFeature0.geometry.coordinates[0] as Ring;
    const suggestion = suggestSplitFromAnchors(
      anchors,
      page.pageWidth,
      page.pageHeight,
      ring0,
      bundle.doorWalls?.doorWalls,
      ngfValues
    );
    if (!suggestion) {
      setSplitError("Auto-Vorschlag konnte keine Linie berechnen.");
      return;
    }
    const targetFeature = bundle.polygons.features.find(
      (f) => f.properties.id === splitTarget
    );
    if (!targetFeature) return;
    const ring = targetFeature.geometry.coordinates[0] as Ring;
    const result = splitPolygon(ring, suggestion.from, suggestion.to);
    if (!result) {
      setSplitError(
        "Auto-Vorschlag ergibt nicht genau 2 Schnittpunkte mit dem Polygon. Bitte manuell setzen."
      );
      return;
    }
    setSplitError(null);
    setSplitPoints([suggestion.from, suggestion.to]);
    setSplitPreview(result);
    setEditMode("splitPreview");
  };

  const startMerge = (polygonId: string) => {
    cancelEdit();
    setEditMode("mergeTarget");
    setMergeFirstId(polygonId);
  };

  const tryMergeWith = (otherId: string) => {
    if (!bundle || !mergeFirstId || mergeFirstId === otherId) return;
    const a = bundle.polygons.features.find(
      (f) => f.properties.id === mergeFirstId
    );
    const b = bundle.polygons.features.find((f) => f.properties.id === otherId);
    if (!a || !b) {
      setMergeError("Polygon nicht gefunden.");
      return;
    }
    const ringA = a.geometry.coordinates[0] as Ring;
    const ringB = b.geometry.coordinates[0] as Ring;
    const result = mergePolygons(ringA, ringB, {
      adjacentTolerance: 8,
      gridSize: 2,
    });
    if (!result) {
      setMergeError(
        "Polygone konnten nicht vereinigt werden — sie ueberlappen nicht und sind zu weit voneinander entfernt (mehr als ~1 cm Plan-Abstand)."
      );
      return;
    }
    setMergeError(null);
    setMergeSecondId(otherId);
    setMergePreview(result);
    setEditMode("mergePreview");
  };

  const confirmMerge = async () => {
    if (!bundle || !mergeFirstId || !mergeSecondId || !mergePreview) return;
    setMergeSaving(true);
    setMergeError(null);
    try {
      await api.multiagentMergePolygons(runId, {
        idA: mergeFirstId,
        idB: mergeSecondId,
        mergedRing: mergePreview.ring,
      });
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      setSelectedId(null);
      cancelEdit();
    } catch (err) {
      setMergeError(err instanceof Error ? err.message : String(err));
    } finally {
      setMergeSaving(false);
    }
  };

  /**
   * Wandelt einen DOM-Click auf das SVG in PDF-Pt-Koords um.
   * SVG hat y-down, PDF-Pt y-up — wir flippen.
   */
  const svgClickToPt = (
    e: React.MouseEvent<SVGElement>,
    pageHeight: number
  ): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: pageHeight - local.y };
  };

  const handleSvgClick = (e: React.MouseEvent<SVGElement>) => {
    if (!bundle) return;
    const page = bundle.classification.pages[0];

    // Sprint 0.19 — Wand-Strich-Tool: 2-Klick-Modus (User-Wand für Floodfill).
    if (editMode === "drawWall") {
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      if (!wallStart) {
        setWallStart(pt);
        return;
      }
      const dx = pt.x - wallStart.x;
      const dy = pt.y - wallStart.y;
      if (Math.hypot(dx, dy) < 1) {
        setWallError("Wand zu kurz — bitte zwei verschiedene Punkte wählen.");
        setWallStart(null);
        return;
      }
      saveUserWall(wallStart, pt);
      return;
    }

    // Sprint 0.19 — Wand-Segment-Zeichnen (für ein Polygon, mit Material-Stack).
    if (editMode === "drawWallSegment") {
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      if (!wallSegmentStart) {
        setWallSegmentStart(pt);
        return;
      }
      const dx = pt.x - wallSegmentStart.x;
      const dy = pt.y - wallSegmentStart.y;
      if (Math.hypot(dx, dy) < 1) {
        setWallEditError("Wand zu kurz — bitte zwei verschiedene Punkte wählen.");
        setWallSegmentStart(null);
        return;
      }
      saveNewWallSegment(wallSegmentStart, pt);
      return;
    }

    // Sprint 0.20 — Polygon zeichnen: n Punkte sammeln. Doppelklick / Click
    // auf den ersten Vertex schließt das Polygon und sendet es ans Backend.
    if (editMode === "drawPolygon") {
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      // Wenn nahe am ersten Vertex und mind. 3 Punkte → schließen
      if (drawPolygonPoints.length >= 3) {
        const first = drawPolygonPoints[0];
        const distPt = Math.hypot(pt.x - first.x, pt.y - first.y);
        if (distPt < 12) {
          void finishDrawPolygon();
          return;
        }
      }
      setDrawPolygonPoints((pts) => [...pts, pt]);
      return;
    }

    // Sprint 0.20 — Tür/Fenster-Picking: 2 Punkte auf einer Wand sammeln,
    // dann Modal mit Real-Maßen + prefilled startPt/endPt.
    if (editMode === "drawOpening") {
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt || !openingPickContext) return;
      if (!openingPickStart) {
        setOpeningPickStart(pt);
        return;
      }
      const dx = pt.x - openingPickStart.x;
      const dy = pt.y - openingPickStart.y;
      if (Math.hypot(dx, dy) < 1) {
        setOpeningPickStart(null);
        return;
      }
      // Modal mit prefilled-Endpunkten + computed widthM öffnen
      const ctx = openingPickContext;
      const start = openingPickStart;
      setEditMode("view");
      setOpeningPickStart(null);
      openOpeningEditorForCreate(ctx.polygonId, ctx.type, {
        startPt: start,
        endPt: pt,
      });
      return;
    }

    // Sprint 0.20 — Maßstab-Picking: 2 Punkte sammeln, dann Modal mit Real-Maß-Input.
    if (editMode === "scalePicking") {
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      if (!scalePickStart) {
        setScalePickStart(pt);
        return;
      }
      const dx = pt.x - scalePickStart.x;
      const dy = pt.y - scalePickStart.y;
      if (Math.hypot(dx, dy) < 1) {
        setScalePickStart(null);
        return;
      }
      setScalePicked({ from: scalePickStart, to: pt });
      setScalePickStart(null);
      setEditMode("view");
      setScaleSetterOpen(true);
      return;
    }

    if (editMode !== "splitPicking" || !splitTarget) return;
    const pt = svgClickToPt(e, page.pageHeight);
    if (!pt) return;
    const next = [...splitPoints, pt];
    if (next.length < 2) {
      setSplitPoints(next);
      return;
    }
    // 2 Punkte gesammelt → Vorschau berechnen
    const targetFeature = bundle.polygons.features.find(
      (f) => f.properties.id === splitTarget
    );
    if (!targetFeature) {
      setSplitError("Ziel-Polygon nicht gefunden");
      cancelSplit();
      return;
    }
    const ring = targetFeature.geometry.coordinates[0] as Ring;
    const result = splitPolygon(ring, next[0], next[1]);
    if (!result) {
      setSplitError(
        "Linie schneidet das Polygon nicht genau zweimal — bitte erneut zwei Punkte setzen, die durch das Polygon ziehen."
      );
      setSplitPoints([]);
      return;
    }
    setSplitError(null);
    setSplitPoints(next);
    setSplitPreview(result);
    setEditMode("splitPreview");
  };

  // ---- Zoom & Pan Handlers ----
  const onSvgWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    if (!bundle) return;
    e.preventDefault();
    const page = bundle.classification.pages[0];
    const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
    const newZoom = Math.max(1, Math.min(20, zoom * factor));
    if (newZoom === zoom) return;
    const svg = svgRef.current;
    if (!svg) {
      setZoom(newZoom);
      return;
    }
    const ctm = svg.getScreenCTM();
    if (!ctm) {
      setZoom(newZoom);
      return;
    }
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const before = pt.matrixTransform(ctm.inverse()); // Coord im aktuellen viewBox
    const vbW = page.pageWidth / zoom;
    const vbH = page.pageHeight / zoom;
    const fracX = vbW > 0 ? (before.x - pan.x) / vbW : 0.5;
    const fracY = vbH > 0 ? (before.y - pan.y) / vbH : 0.5;
    const newVbW = page.pageWidth / newZoom;
    const newVbH = page.pageHeight / newZoom;
    let newPanX = before.x - fracX * newVbW;
    let newPanY = before.y - fracY * newVbH;
    newPanX = Math.max(0, Math.min(page.pageWidth - newVbW, newPanX));
    newPanY = Math.max(0, Math.min(page.pageHeight - newVbH, newPanY));
    if (newZoom === 1) {
      newPanX = 0;
      newPanY = 0;
    }
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  const onSvgPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (zoom <= 1) return;
    // Pan nur bei Mittelmaustaste oder Shift-Klick — normaler Klick bleibt
    // fuer Polygon-Selektion / Split-Punkt-Setzen frei.
    if (e.button !== 1 && !e.shiftKey) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    panStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const onSvgPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!bundle) return;
    // Sprint 0.19 — Detail-Lupe trackt Mausposition (auch ohne Drag/Klick).
    if (magnifierActive) {
      const ptM = svgClickToPt(e, bundle.classification.pages[0].pageHeight);
      if (ptM) setMagnifierPt(ptM);
    }
    // Sprint 0.19 — Wand-Endpunkt-Drag im editWallEndpoints-Mode.
    if (
      editMode === "editWallEndpoints" &&
      draggingWallEnd !== null &&
      wallEditEndpoints
    ) {
      const page = bundle.classification.pages[0];
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      if (draggingWallEnd === "start") {
        setWallEditEndpoints({ ...wallEditEndpoints, startPt: pt });
      } else {
        setWallEditEndpoints({ ...wallEditEndpoints, endPt: pt });
      }
      return;
    }

    // Sprint 0.19 — Vertex-Drag im vertexEdit-Mode: hat Vorrang vor Pan.
    if (
      editMode === "vertexEdit" &&
      draggingVertexIdx !== null &&
      editingRing
    ) {
      const page = bundle.classification.pages[0];
      const pt = svgClickToPt(e, page.pageHeight);
      if (!pt) return;
      // editingRing[0] === editingRing[length-1] (geschlossener Ring) — wenn
      // der erste oder letzte Vertex gedraggt wird, müssen beide synchron
      // bleiben.
      const next = editingRing.map((p, i) =>
        i === draggingVertexIdx ? [pt.x, pt.y] : p
      );
      const lastIdx = next.length - 1;
      if (
        draggingVertexIdx === 0 &&
        next[0][0] !== undefined &&
        next[lastIdx]
      ) {
        next[lastIdx] = [pt.x, pt.y];
      } else if (draggingVertexIdx === lastIdx && next[0]) {
        next[0] = [pt.x, pt.y];
      }
      setEditingRing(next);
      return;
    }

    if (!panStartRef.current) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const page = bundle.classification.pages[0];
    const dxPx = e.clientX - panStartRef.current.clientX;
    const dyPx = e.clientY - panStartRef.current.clientY;
    const vbW = page.pageWidth / zoom;
    const vbH = page.pageHeight / zoom;
    const dxPt = (dxPx / rect.width) * vbW;
    const dyPt = (dyPx / rect.height) * vbH;
    let newPanX = panStartRef.current.panX - dxPt;
    let newPanY = panStartRef.current.panY - dyPt;
    newPanX = Math.max(0, Math.min(page.pageWidth - vbW, newPanX));
    newPanY = Math.max(0, Math.min(page.pageHeight - vbH, newPanY));
    setPan({ x: newPanX, y: newPanY });
  };

  const onSvgPointerUp = () => {
    panStartRef.current = null;
    if (draggingVertexIdx !== null) {
      setDraggingVertexIdx(null);
    }
    if (draggingWallEnd !== null) {
      setDraggingWallEnd(null);
    }
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  /**
   * Bulk: fuer jeden splitCandidate (Polygon mit ≥2 NGF-Anchors) automatisch
   * Auto-Vorschlag berechnen und ohne Vorschau direkt bestaetigen.
   * Speichert sequenziell, weil jeder Split-Aufruf das polygons.geojson mutiert.
   */
  const [bulkSplitting, setBulkSplitting] = useState(false);
  const [bulkSplitReport, setBulkSplitReport] = useState<{
    success: number;
    failed: number;
  } | null>(null);
  const runBulkAutoSplit = async () => {
    if (!bundle || bulkSplitting) return;
    const candidates = bundle.dimensions?.roomNgf.polygonMatches.filter(
      (m) => m.status === "splitCandidate" && m.ngfStamps.length === 2
    );
    if (!candidates || candidates.length === 0) {
      setBulkSplitReport({ success: 0, failed: 0 });
      return;
    }
    setBulkSplitting(true);
    setBulkSplitReport(null);
    let success = 0;
    let failed = 0;
    const page = bundle.classification.pages[0];
    // Wir arbeiten auf einer Kopie der Polygon-Liste — nach jedem Split holen wir
    // das frische bundle, weil Auto-Recompute neue NGF-Anchors zuweisen koennte.
    let workingBundle = bundle;
    for (const cand of candidates) {
      const feature = workingBundle.polygons.features.find(
        (f) => f.properties.id === cand.polygonId
      );
      if (!feature) {
        failed++;
        continue;
      }
      const ring = feature.geometry.coordinates[0] as Ring;
      const anchors = cand.ngfStamps.slice(0, 2).map((s) => s.anchor);
      const ngfs = cand.ngfStamps.slice(0, 2).map((s) => s.ngfM2) as [
        number,
        number,
      ];
      const suggestion = suggestSplitFromAnchors(
        anchors,
        page.pageWidth,
        page.pageHeight,
        ring,
        workingBundle.doorWalls?.doorWalls,
        ngfs
      );
      if (!suggestion) {
        failed++;
        continue;
      }
      const result = splitPolygon(ring, suggestion.from, suggestion.to);
      if (!result) {
        failed++;
        continue;
      }
      try {
        await api.multiagentSplitPolygon(runId, {
          polygonId: cand.polygonId,
          ringA: result.ringA,
          ringB: result.ringB,
          areaFractionA: result.fractionA,
        });
        success++;
        workingBundle = await loadBundle(runId);
      } catch {
        failed++;
      }
    }
    setBundle(workingBundle);
    setSelectedId(null);
    setBulkSplitting(false);
    setBulkSplitReport({ success, failed });
  };

  const confirmSplit = async () => {
    if (!bundle || !splitTarget || !splitPreview) return;
    setSplitSaving(true);
    setSplitError(null);
    try {
      await api.multiagentSplitPolygon(runId, {
        polygonId: splitTarget,
        ringA: splitPreview.ringA,
        ringB: splitPreview.ringB,
        areaFractionA: splitPreview.fractionA,
      });
      // Bundle neu laden — die neuen IDs sollten in den Polygonen erscheinen
      const fresh = await loadBundle(runId);
      setBundle(fresh);
      cancelSplit();
      setSelectedId(null);
    } catch (err) {
      setSplitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSplitSaving(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <Link to="/multiagent" className="text-sm text-brand-600 hover:underline">
          ← Zur Run-Liste
        </Link>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-slate-500">
        Lädt Run-Daten…
      </div>
    );
  }

  const page = bundle.classification.pages[0];
  const pwPt = page.pageWidth;
  const phPt = page.pageHeight;
  const features = bundle.polygons.features;
  // Container-Polygone zuerst rendern (unten im z-order), sonst überdecken
  // sie Innenräume und blockieren Klicks. Reihenfolge bottom-up:
  //   1. GALA (Außenanlage, größter Container — Sprint #6)
  //   2. HAUS (Gebäudehülle, innerhalb GALA)
  //   3. Räume (innerhalb HAUS)
  // Sort-Key: GALA=2, HAUS=1, Raum=0. b-a → höchster Key zuerst (= unten).
  const polygonZKey = (p: { properties: { isBuilding?: boolean; isGaLaArea?: boolean } }): number => {
    if (p.properties.isGaLaArea) return 2;
    if (p.properties.isBuilding) return 1;
    return 0;
  };
  const featuresZSorted = [...features].sort(
    (a, b) => polygonZKey(b) - polygonZKey(a)
  );
  const ngfStamps = bundle.dimensions?.roomNgf.stamps ?? [];

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* Sprint 0.20 — Multi-Mirror-Toast (oben rechts, ~3.5s) */}
      {mirrorBanner && (
        <div
          key={mirrorBanner.ts}
          className="fixed top-20 right-6 z-50 bg-cyan-600 text-white rounded-lg shadow-lg px-4 py-3 text-sm max-w-md animate-fade-in"
        >
          <div className="flex items-start gap-2">
            <span className="text-lg leading-none">↺</span>
            <div className="flex-1">
              <div className="font-semibold mb-0.5">Wand-Mirror</div>
              <div className="text-cyan-50">{mirrorBanner.text}</div>
            </div>
            <button
              onClick={() => setMirrorBanner(null)}
              className="text-cyan-100 hover:text-white text-xs"
              aria-label="Banner schliessen"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Atelier-Topbar (Sprint Atelier Phase 3 — 2026-05-02) — Logo +
          Breadcrumb (Run-Liste → RunId) + Etagen-Switcher + WE-Pills +
          Aufmaß-abschließen / LV-Button. Runs haben heute keine festen
          Etagen-Listen, daher freier Etage-Input statt segmentierter Switch
          (Sprint A.10c könnte Pflicht-Etagen-Listen einführen). */}
      <header
        className="flex items-center gap-4 flex-shrink-0"
        style={{
          height: 52,
          padding: "0 16px",
          background: "var(--bd-surface)",
          borderBottom: "1px solid var(--bd-line)",
        }}
      >
        {/* Breadcrumb */}
        <div
          className="flex items-center gap-2 min-w-0"
          style={{ color: "var(--bd-text-2)", fontSize: 13 }}
        >
          <FolderSm />
          <Link
            to="/multiagent"
            className="no-underline whitespace-nowrap"
            style={{ color: "var(--bd-text-3)" }}
          >
            Multi-Agent
          </Link>
          <ChevRSm style={{ color: "var(--bd-text-4)" }} />
          <span
            className="font-mono truncate"
            title={runId}
            style={{
              color: "var(--bd-text)",
              fontWeight: 500,
              fontSize: 12,
              fontFamily: "var(--bd-mono)",
            }}
          >
            {runId}
          </span>
        </div>

        {/* Sprint M (2026-05-03) — Mappe-Switcher: zeigt segmentierte Buttons
            für alle Runs der aktuellen Mappe. Klick navigiert zum Run. Wenn
            der Run keiner Mappe zugeordnet ist, fällt es auf das Read-Only-
            Etage-Label aus Sprint Atelier zurück. */}
        {mappeRuns && mappeRuns.length > 1 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              paddingLeft: 16,
              borderLeft: "1px solid var(--bd-line)",
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: 10,
                color: "var(--bd-text-3)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title={mappeName ?? undefined}
            >
              📁 Mappe
            </span>
            <div
              style={{
                display: "flex",
                gap: 2,
                padding: 2,
                borderRadius: 6,
                background: "var(--bd-surface2)",
                border: "1px solid var(--bd-line)",
              }}
              role="tablist"
            >
              {mappeRuns.map((r) => {
                const isActive = r.runId === runId;
                return (
                  <Link
                    key={r.runId}
                    to={`/multiagent/${r.runId}`}
                    role="tab"
                    aria-current={isActive ? "page" : undefined}
                    className="no-underline"
                    title={`${r.displayLabel}${r.etage ? ` · ${r.etage}` : ""} · ${r.runId}`}
                    style={{
                      padding: "3px 9px",
                      borderRadius: 4,
                      fontFamily: "var(--bd-mono)",
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: 0.3,
                      color: isActive
                        ? "#fff"
                        : "var(--bd-text-2)",
                      background: isActive
                        ? "var(--bd-ember)"
                        : "transparent",
                      transition: "background 0.12s ease-out",
                    }}
                    onMouseEnter={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = "var(--bd-hover)";
                    }}
                    onMouseLeave={(e) => {
                      if (isActive) return;
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {r.displayLabel}
                  </Link>
                );
              })}
            </div>
          </div>
        ) : runMeta?.etage ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              paddingLeft: 16,
              borderLeft: "1px solid var(--bd-line)",
            }}
          >
            <span
              style={{
                fontSize: 10,
                color: "var(--bd-text-3)",
                letterSpacing: 0.4,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Etage
            </span>
            <span
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                background: "var(--bd-surface2)",
                color: "var(--bd-text)",
                fontFamily: "var(--bd-mono)",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.3,
                border: "1px solid var(--bd-line)",
              }}
              title="Etage wird im Inspector → Übersicht gepflegt. Mappe noch nicht zugeordnet."
            >
              {runMeta.etage}
            </span>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              paddingLeft: 16,
              borderLeft: "1px solid var(--bd-line)",
              fontSize: 11,
              color: "var(--bd-text-3)",
              fontStyle: "italic",
            }}
            title="Etage im Inspector → Übersicht setzen, oder Run einer Mappe zuordnen."
          >
            keine Etage gesetzt
          </div>
        )}

        {/* WE-Pills — Atelier-Style */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            minWidth: 0,
            paddingLeft: 12,
            borderLeft: "1px solid var(--bd-line)",
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: "var(--bd-text-3)",
              letterSpacing: 0.4,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            WE
          </span>
          {(runMeta?.wohneinheiten ?? []).length === 0 ? (
            <span
              style={{
                fontSize: 11,
                color: "var(--bd-text-4)",
                fontStyle: "italic",
              }}
            >
              keine — im Inspector anlegen
            </span>
          ) : (
            (runMeta?.wohneinheiten ?? []).slice(0, 4).map((we) => (
              <span
                key={we.id}
                style={{
                  padding: "2px 7px",
                  borderRadius: 4,
                  fontSize: 11,
                  fontWeight: 500,
                  fontFamily: "var(--bd-sans)",
                  border: `1px solid ${weColorFor(we.id)}`,
                  background: weColorFor(we.id) + "20",
                  color: "var(--bd-text)",
                }}
                title={`${we.label} · ${we.raumIds.length} Räume · ${(weAreaSum.get(we.id) ?? 0).toFixed(2)} m²`}
              >
                {we.label}
                <span
                  style={{
                    marginLeft: 4,
                    color: "var(--bd-text-3)",
                    fontFamily: "var(--bd-mono)",
                    fontSize: 10,
                  }}
                >
                  {we.raumIds.length}
                </span>
              </span>
            ))
          )}
          {(runMeta?.wohneinheiten ?? []).length > 4 && (
            <span
              style={{
                fontSize: 11,
                color: "var(--bd-text-3)",
                fontFamily: "var(--bd-mono)",
              }}
            >
              +{(runMeta?.wohneinheiten.length ?? 0) - 4}
            </span>
          )}
        </div>

        <span style={{ flex: 1 }} />

        {/* Sprint Inline-OCR (2026-05-06) — Premium-Toggle: Inline-Notizen
            pro Polygon via Vision-API. Kosten ~0,02 €/Raum. Idempotent. */}
        {bundle && bundle.polygons.features.length > 0 && (() => {
          const totalRooms = bundle.polygons.features.filter(
            (f) =>
              !(f.properties as { isBuilding?: boolean; isGaLaArea?: boolean })
                .isBuilding &&
              !(f.properties as { isBuilding?: boolean; isGaLaArea?: boolean })
                .isGaLaArea
          ).length;
          // Nur Polygone zählen, die auch in der Frontend-Liste sind — sonst
          // entsteht ein irritierendes "44/43" wenn das annotations-File
          // auch HAUS oder andere ausgefilterte Polygone enthält.
          const visibleIds = new Set(
            bundle.polygons.features
              .filter(
                (f) =>
                  !(f.properties as {
                    isBuilding?: boolean;
                    isGaLaArea?: boolean;
                  }).isBuilding &&
                  !(f.properties as {
                    isBuilding?: boolean;
                    isGaLaArea?: boolean;
                  }).isGaLaArea
              )
              .map((f) => String(f.properties.id))
          );
          const ocrCount = Object.entries(
            bundle.annotations?.perRoomInlineText ?? {}
          ).filter(
            ([id, entry]) =>
              visibleIds.has(id) &&
              entry &&
              entry.rawText !== undefined &&
              entry.rawText.trim().length > 0
          ).length;
          const allDone = ocrCount >= totalRooms && totalRooms > 0;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginRight: 8,
              }}
            >
              <button
                onClick={() => triggerInlineOcr(false)}
                disabled={inlineOcrRunning}
                title={
                  allDone
                    ? `Alle ${totalRooms} Räume bereits gelesen. Klick = nichts. Force über ⤺.`
                    : `Pro Polygon-Bbox die Inline-Notizen via Vision-API lesen (RH/BRH/DFF/Stützen…). ~${(((totalRooms - ocrCount) || totalRooms) * 0.02).toFixed(2)} € geschätzt für ${totalRooms - ocrCount} ungelesene Räume.`
                }
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--bd-line)",
                  cursor: inlineOcrRunning ? "wait" : "pointer",
                  background: allDone
                    ? "color-mix(in oklab, var(--bd-ember) 8%, transparent)"
                    : "var(--bd-surface)",
                  color: "var(--bd-text)",
                  fontFamily: "var(--bd-sans)",
                  fontSize: 12,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  opacity: inlineOcrRunning ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                <span>🔍</span>
                <span>
                  {inlineOcrRunning
                    ? "Lese…"
                    : allDone
                      ? `Inline-Notizen (${ocrCount}/${totalRooms})`
                      : `Inline-Notizen lesen${ocrCount > 0 ? ` (${ocrCount}/${totalRooms})` : ""}`}
                </span>
              </button>
              {allDone && (
                <button
                  onClick={() => triggerInlineOcr(true)}
                  disabled={inlineOcrRunning}
                  title="Force: alle Polygone neu lesen — überschreibt bestehende OCR-Daten."
                  style={{
                    padding: "6px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--bd-line)",
                    cursor: inlineOcrRunning ? "wait" : "pointer",
                    background: "var(--bd-surface)",
                    color: "var(--bd-text-3)",
                    fontFamily: "var(--bd-sans)",
                    fontSize: 12,
                  }}
                >
                  ⤺
                </button>
              )}
              {inlineOcrLastSummary &&
                Date.now() - inlineOcrLastSummary.at < 30_000 && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--bd-text-3)",
                      fontFamily: "var(--bd-mono)",
                    }}
                    title={`${inlineOcrLastSummary.polygonsOcrCalled} OCR-Calls, ${inlineOcrLastSummary.polygonsWithText} mit Text`}
                  >
                    +{inlineOcrLastSummary.polygonsWithText} ·{" "}
                    {inlineOcrLastSummary.totalCostEur.toFixed(3)} €
                  </span>
                )}
              {inlineOcrError && (
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--bd-rose, #be123c)",
                    maxWidth: 220,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={inlineOcrError}
                >
                  ⚠ {inlineOcrError}
                </span>
              )}
            </div>
          );
        })()}

        {/* Aufmaß abschließen / LV-Button — Atelier ember accent */}
        {lvEnabled && bundle && (
          <button
            onClick={triggerLvTransfer}
            disabled={lvTransferring || !runMeta?.etage}
            style={{
              padding: "7px 14px",
              borderRadius: 6,
              border: "none",
              cursor:
                lvTransferring || !runMeta?.etage ? "not-allowed" : "pointer",
              background: "var(--bd-ember)",
              color: "#fff",
              fontFamily: "var(--bd-sans)",
              fontSize: 12,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: lvTransferring || !runMeta?.etage ? 0.5 : 1,
              whiteSpace: "nowrap",
            }}
            title={
              !runMeta?.etage
                ? "Etage zuerst setzen"
                : "Erzeugt einen neuen LV-Run aus den aktuellen Mengen"
            }
          >
            <CheckSm /> {lvTransferring ? "LV…" : "LV erstellen"}
          </button>
        )}
      </header>

      {/* UX2 — 3-Spalten-Body: Linkes Listen-Panel + Plan + Detail-Sidebar. */}
      <div className="flex flex-1 overflow-hidden">

      {/* Atelier-Tool-Rail (Sprint Atelier Phase 7 — 2026-05-02) — schmale
          44px Sidebar links mit Hotkeys V/P/H/W/S/L. Tools rufen die
          existierenden Edit-Mode-Setter auf. Aktiver Tool-Zustand wird aus
          editMode + scaleSetterOpen + magnifierActive abgeleitet. */}
      <div
        style={{
          width: 44,
          flexShrink: 0,
          borderRight: "1px solid var(--bd-line)",
          background: "var(--bd-surface)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "10px 0",
          gap: 2,
        }}
      >
        {(
          [
            {
              name: "cursor",
              label: "Auswahl",
              hk: "V",
              active: editMode === "view",
              onClick: () => setEditMode("view"),
              icon: "↖",
            },
            {
              name: "polygon",
              label: "Raum zeichnen",
              hk: "P",
              active: editMode === "drawPolygon",
              onClick: () => startDrawPolygon(false),
              icon: "⬡",
            },
            {
              name: "haus",
              label: "HAUS-Außenkontur",
              hk: "H",
              active: false,
              onClick: () => startDrawPolygon(true),
              icon: "🏠",
              disabled: roomCounts.building > 0,
            },
            {
              name: "wall",
              label: "Wand zeichnen",
              hk: "W",
              active: editMode === "drawWall",
              onClick: () => startDrawWall(),
              icon: "▍",
            },
          ] as Array<{
            name: string;
            label: string;
            hk: string;
            active: boolean;
            onClick: () => void;
            icon: string;
            disabled?: boolean;
          }>
        ).map((t) => (
          <button
            key={t.name}
            onClick={() => {
              if (!t.disabled) t.onClick();
            }}
            disabled={t.disabled}
            title={`${t.label} · ${t.hk}`}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: "none",
              cursor: t.disabled ? "not-allowed" : "pointer",
              background: t.active
                ? "color-mix(in oklab, var(--bd-ember) 14%, transparent)"
                : "transparent",
              color: t.active
                ? "var(--bd-ember-strong)"
                : t.disabled
                  ? "var(--bd-text-4)"
                  : "var(--bd-text-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              position: "relative",
              opacity: t.disabled ? 0.45 : 1,
            }}
          >
            {t.icon}
            {t.active && (
              <span
                style={{
                  position: "absolute",
                  left: -1,
                  top: 6,
                  bottom: 6,
                  width: 2,
                  background: "var(--bd-ember)",
                  borderRadius: 2,
                }}
              />
            )}
          </button>
        ))}

        <div
          style={{
            width: 24,
            height: 1,
            background: "var(--bd-line)",
            margin: "8px 0",
          }}
        />

        {/* Sekundäre Werkzeuge */}
        {(
          [
            {
              name: "scale",
              label: "Maßstab kalibrieren",
              hk: "S",
              onClick: () => {
                setScalePicked(null);
                setScalePickStart(null);
                setScaleSetterOpen(true);
              },
              icon: "📏",
            },
            {
              name: "magnify",
              label: magnifierActive ? "Lupe aus" : "Lupe ein",
              hk: "L",
              active: magnifierActive,
              onClick: () => setMagnifierActive(!magnifierActive),
              icon: "🔍",
            },
          ] as Array<{
            name: string;
            label: string;
            hk: string;
            active?: boolean;
            onClick: () => void;
            icon: string;
          }>
        ).map((t) => (
          <button
            key={t.name}
            onClick={t.onClick}
            title={`${t.label} · ${t.hk}`}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
              background: t.active
                ? "color-mix(in oklab, var(--bd-ember) 14%, transparent)"
                : "transparent",
              color: t.active
                ? "var(--bd-ember-strong)"
                : "var(--bd-text-3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
            }}
          >
            {t.icon}
          </button>
        ))}

        <div style={{ flex: 1 }} />

        {/* Settings unten — Verlinkung zum Anzeige-Tab */}
        <button
          onClick={() => setActiveTab("anzeige")}
          title="Anzeige-Einstellungen"
          style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            background:
              activeTab === "anzeige"
                ? "color-mix(in oklab, var(--bd-ember) 14%, transparent)"
                : "transparent",
            color:
              activeTab === "anzeige"
                ? "var(--bd-ember-strong)"
                : "var(--bd-text-3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
          }}
        >
          ⚙️
        </button>
      </div>

      {/* Linkes Panel — Atelier-Style (Sprint Atelier Phase 4 — 2026-05-02). */}
      <aside
        style={{
          width: leftPanelWidth,
          background: "var(--bd-surface)",
          borderRight: "1px solid var(--bd-line)",
        }}
        className="overflow-y-auto flex-shrink-0 flex flex-col"
        id="left-panel"
      >
        {(() => {
          const roomFeatures = featuresZSorted.filter(
            (f) =>
              !(f.properties as {
                isBuilding?: boolean;
                isGaLaArea?: boolean;
              }).isBuilding &&
              !(f.properties as {
                isBuilding?: boolean;
                isGaLaArea?: boolean;
              }).isGaLaArea
          );
          const totalDoors = featuresZSorted.reduce((sum, f) => {
            const polyId = f.properties.id;
            const auto = doorsByPolyId.get(polyId)?.length ?? 0;
            const user = (
              (f.properties.openings ?? []) as Array<{ type?: string }>
            ).filter((o) => o.type === "door").length;
            return sum + auto + user;
          }, 0);
          const totalWindows = featuresZSorted.reduce((sum, f) => {
            const polyId = f.properties.id;
            const auto = windowsByPolyId.get(polyId)?.length ?? 0;
            const user = (
              (f.properties.openings ?? []) as Array<{ type?: string }>
            ).filter((o) => o.type === "window").length;
            return sum + auto + user;
          }, 0);
          const totalWalls = featuresZSorted.reduce(
            (sum, f) => sum + (f.properties.walls?.length ?? 0),
            0
          );
          // Gesamt-Fläche für Footer-Total (nur Innenräume, nicht HAUS).
          const totalArea = roomFeatures.reduce(
            (sum, f) => sum + (f.properties.areaM2 ?? 0),
            0
          );
          return (
            <>
              {/* Atelier-Header: Räume-Title + Count + Quick-Actions */}
              <div
                className="sticky top-0 z-20"
                style={{
                  background: "var(--bd-surface)",
                  borderBottom: "1px solid var(--bd-line)",
                }}
              >
                <div
                  style={{
                    padding: "10px 14px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--bd-text-3)",
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      Räume {runMeta?.etage ? `· ${runMeta.etage}` : ""}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--bd-mono)",
                        fontSize: 13,
                        color: "var(--bd-text)",
                        marginTop: 2,
                      }}
                    >
                      {roomFeatures.length}{" "}
                      <span
                        style={{ color: "var(--bd-text-3)", fontWeight: 400 }}
                      >
                        · {totalArea.toFixed(2).replace(".", ",")} m²
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => startDrawPolygon(false)}
                      disabled={editMode !== "view"}
                      style={{
                        padding: "5px 9px",
                        borderRadius: 6,
                        border: "1px solid var(--bd-line-2)",
                        background: "var(--bd-surface)",
                        color: "var(--bd-text-2)",
                        fontSize: 11,
                        fontWeight: 500,
                        cursor: editMode === "view" ? "pointer" : "not-allowed",
                        opacity: editMode === "view" ? 1 : 0.4,
                        whiteSpace: "nowrap",
                      }}
                      title="Neuen Raum zeichnen"
                    >
                      + Raum
                    </button>
                    <button
                      onClick={() => startDrawPolygon(true)}
                      disabled={editMode !== "view" || roomCounts.building > 0}
                      style={{
                        padding: "5px 9px",
                        borderRadius: 6,
                        border: "1px solid var(--bd-line-2)",
                        background: "var(--bd-surface)",
                        color: "var(--bd-text-2)",
                        fontSize: 11,
                        fontWeight: 500,
                        cursor:
                          editMode === "view" && roomCounts.building === 0
                            ? "pointer"
                            : "not-allowed",
                        opacity:
                          editMode === "view" && roomCounts.building === 0
                            ? 1
                            : 0.4,
                        whiteSpace: "nowrap",
                      }}
                      title={
                        roomCounts.building > 0
                          ? "HAUS existiert bereits"
                          : "HAUS-Außenkontur zeichnen"
                      }
                    >
                      + HAUS
                    </button>
                  </div>
                </div>

                {/* Atelier-Filter-Pills: Räume / Türen / Fenster / Wände */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    fontSize: 11,
                  }}
                >
                  {(
                    [
                      {
                        id: "rooms" as LeftListFilter,
                        label: "Räume",
                        emoji: "📐",
                        count: roomFeatures.length,
                      },
                      {
                        id: "doors" as LeftListFilter,
                        label: "Türen",
                        emoji: "🚪",
                        count: totalDoors,
                      },
                      {
                        id: "windows" as LeftListFilter,
                        label: "Fenster",
                        emoji: "🪟",
                        count: totalWindows,
                      },
                      {
                        id: "walls" as LeftListFilter,
                        label: "Wände",
                        emoji: "🧱",
                        count: totalWalls,
                      },
                    ]
                  ).map((f) => {
                    const isActive = leftListFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setLeftListFilter(f.id)}
                        title={f.label}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          padding: "8px 0",
                          border: "none",
                          background: "transparent",
                          color: isActive
                            ? "var(--bd-ember-strong)"
                            : "var(--bd-text-3)",
                          fontWeight: isActive ? 600 : 500,
                          borderBottom: isActive
                            ? "2px solid var(--bd-ember)"
                            : "2px solid transparent",
                          marginBottom: -1,
                          cursor: "pointer",
                          gap: 2,
                        }}
                      >
                        <span style={{ fontSize: 14, lineHeight: 1 }}>
                          {f.emoji}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            fontFamily: "var(--bd-mono)",
                            letterSpacing: 0.2,
                          }}
                        >
                          {f.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Listen-Inhalt */}
              <div className="flex-1 overflow-y-auto">
                {leftListFilter === "rooms" && (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {roomFeatures.map((f) => {
                      const id = f.properties.id;
                      const cr = bundle.rooms?.rooms.find((r) => r.id === id);
                      const isSel = selectedId === id;
                      const status = polyStatus.get(id) ?? "pending";
                      const dotColor =
                        status === "matched"
                          ? "oklch(0.62 0.16 145)"
                          : status === "splitCandidate"
                            ? "oklch(0.72 0.15 80)"
                            : status === "outdoor"
                              ? "var(--bd-text-4)"
                              : status === "building"
                                ? "var(--bd-text)"
                                : status === "gala"
                                  ? "rgb(22, 163, 74)"
                                  : "var(--bd-ember)";
                      const we = roomToWe.get(id);
                      // Atelier-Color-Dot: kleines abgerundetes Quadrat
                      // (Raumkategorie-Farbe), wie im Mockup.
                      return (
                        <li
                          key={id}
                          onClick={() => setSelectedId(isSel ? null : id)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 14px",
                            borderBottom: "1px solid var(--bd-line)",
                            cursor: "pointer",
                            background: isSel
                              ? "color-mix(in oklab, var(--bd-ember) 8%, transparent)"
                              : "transparent",
                            borderLeft: isSel
                              ? "2px solid var(--bd-ember)"
                              : "2px solid transparent",
                            paddingLeft: isSel ? 12 : 14,
                            transition: "background 0.1s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isSel)
                              e.currentTarget.style.background =
                                "var(--bd-hover)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSel)
                              e.currentTarget.style.background = "transparent";
                          }}
                          title={`${id} · ${cr?.label ?? "—"} · ${f.properties.areaM2.toFixed(2)} m²`}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: dotColor,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              fontFamily: "var(--bd-mono)",
                              fontSize: 11,
                              color: isSel
                                ? "var(--bd-ember-strong)"
                                : "var(--bd-text-3)",
                              fontWeight: isSel ? 600 : 500,
                              width: 32,
                              flexShrink: 0,
                            }}
                          >
                            {id}
                          </span>
                          <span
                            style={{
                              flex: 1,
                              fontSize: 12,
                              color: "var(--bd-text)",
                              fontWeight: isSel ? 500 : 400,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cr?.label ?? "—"}
                          </span>
                          {we && (
                            <span
                              style={{
                                fontSize: 10,
                                padding: "1px 5px",
                                borderRadius: 3,
                                background: weColorFor(we.id) + "30",
                                color: "var(--bd-text-2)",
                                fontWeight: 500,
                                flexShrink: 0,
                              }}
                              title={we.label}
                            >
                              {we.label}
                            </span>
                          )}
                          <span
                            style={{
                              fontFamily: "var(--bd-mono)",
                              fontSize: 11,
                              color: "var(--bd-text-2)",
                              fontVariantNumeric: "tabular-nums",
                              flexShrink: 0,
                            }}
                          >
                            {f.properties.areaM2.toFixed(2).replace(".", ",")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {leftListFilter === "doors" && (
                  <ul className="text-xs">
                    {featuresZSorted.flatMap((f) => {
                      const polyId = f.properties.id;
                      const auto = doorsByPolyId.get(polyId) ?? [];
                      const suppressed =
                        ((f.properties as {
                          suppressedAutoDoors?: number[];
                        }).suppressedAutoDoors) ?? [];
                      const userDoors = (
                        (f.properties.openings ?? []) as Array<{
                          type: "door" | "window";
                          widthM: number;
                          heightM?: number;
                          label?: string;
                        }>
                      )
                        .map((o, i) => ({ ...o, _idx: i }))
                        .filter((o) => o.type === "door");
                      const items: React.ReactNode[] = [];
                      auto.forEach((d) => {
                        const origIdx = d._origIdx ?? 0;
                        if (suppressed.includes(origIdx)) return;
                        const key = `${polyId}-auto-door-${origIdx}`;
                        const isHl =
                          highlightedOpening?.polyId === polyId &&
                          highlightedOpening.key === `auto-door-${origIdx}`;
                        items.push(
                          <li
                            key={key}
                            onClick={() => {
                              setSelectedId(polyId);
                              setHighlightedOpening({
                                polyId,
                                key: `auto-door-${origIdx}`,
                                type: "door",
                                source: "auto",
                                idx: origIdx,
                              });
                            }}
                            className={`px-2 py-1 border-b cursor-pointer flex items-center gap-1.5 ${
                              isHl
                                ? "bg-amber-100 ring-1 ring-amber-500"
                                : "hover:bg-slate-50"
                            }`}
                          >
                            <span className="text-[9px] uppercase text-slate-400 flex-shrink-0">
                              auto
                            </span>
                            <span className="font-mono text-[10px] flex-shrink-0">
                              {polyId}
                            </span>
                            <span className="text-slate-700 flex-1 truncate">
                              Tür {origIdx + 1}
                            </span>
                            <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">
                              {d.widthCm.toFixed(0)}
                            </span>
                          </li>
                        );
                      });
                      userDoors.forEach((d) => {
                        const key = `${polyId}-user-${d._idx}`;
                        const isHl =
                          highlightedOpening?.polyId === polyId &&
                          highlightedOpening.key === `user-${d._idx}`;
                        items.push(
                          <li
                            key={key}
                            onClick={() => {
                              setSelectedId(polyId);
                              setHighlightedOpening({
                                polyId,
                                key: `user-${d._idx}`,
                                type: "door",
                                source: "user",
                                idx: d._idx,
                              });
                            }}
                            className={`px-2 py-1 border-b cursor-pointer flex items-center gap-1.5 ${
                              isHl
                                ? "bg-amber-100 ring-1 ring-amber-500"
                                : "bg-amber-50 hover:bg-amber-100"
                            }`}
                          >
                            <span className="text-[9px] uppercase text-amber-700 font-semibold flex-shrink-0">
                              user
                            </span>
                            <span className="font-mono text-[10px] flex-shrink-0">
                              {polyId}
                            </span>
                            <span className="text-slate-700 flex-1 truncate">
                              {d.label || `Tür ${d._idx + 1}`}
                            </span>
                            <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">
                              {(d.widthM * 100).toFixed(0)}
                            </span>
                          </li>
                        );
                      });
                      return items;
                    })}
                    {totalDoors === 0 && (
                      <li className="p-3 text-[11px] italic text-slate-400">
                        Keine Türen erkannt.
                      </li>
                    )}
                  </ul>
                )}
                {leftListFilter === "windows" && (
                  <ul className="text-xs">
                    {featuresZSorted.flatMap((f) => {
                      const polyId = f.properties.id;
                      const auto = windowsByPolyId.get(polyId) ?? [];
                      const suppressed =
                        ((f.properties as {
                          suppressedAutoWindows?: number[];
                        }).suppressedAutoWindows) ?? [];
                      const userWindows = (
                        (f.properties.openings ?? []) as Array<{
                          type: "door" | "window";
                          widthM: number;
                          heightM?: number;
                          bruestungM?: number;
                          label?: string;
                        }>
                      )
                        .map((o, i) => ({ ...o, _idx: i }))
                        .filter((o) => o.type === "window");
                      const items: React.ReactNode[] = [];
                      auto.forEach((w, i) => {
                        if (suppressed.includes(i)) return;
                        const key = `${polyId}-auto-window-${i}`;
                        const isHl =
                          highlightedOpening?.polyId === polyId &&
                          highlightedOpening.key === `auto-window-${i}`;
                        items.push(
                          <li
                            key={key}
                            onClick={() => {
                              setSelectedId(polyId);
                              setHighlightedOpening({
                                polyId,
                                key: `auto-window-${i}`,
                                type: "window",
                                source: "auto",
                                idx: i,
                              });
                            }}
                            className={`px-2 py-1 border-b cursor-pointer flex items-center gap-1.5 ${
                              isHl
                                ? "bg-cyan-100 ring-1 ring-cyan-500"
                                : "hover:bg-slate-50"
                            }`}
                          >
                            <span className="text-[9px] uppercase text-slate-400 flex-shrink-0">
                              auto
                            </span>
                            <span className="font-mono text-[10px] flex-shrink-0">
                              {polyId}
                            </span>
                            <span className="text-slate-700 flex-1 truncate">
                              {w.rawText}
                            </span>
                          </li>
                        );
                      });
                      userWindows.forEach((w) => {
                        const key = `${polyId}-user-${w._idx}`;
                        const isHl =
                          highlightedOpening?.polyId === polyId &&
                          highlightedOpening.key === `user-${w._idx}`;
                        items.push(
                          <li
                            key={key}
                            onClick={() => {
                              setSelectedId(polyId);
                              setHighlightedOpening({
                                polyId,
                                key: `user-${w._idx}`,
                                type: "window",
                                source: "user",
                                idx: w._idx,
                              });
                            }}
                            className={`px-2 py-1 border-b cursor-pointer flex items-center gap-1.5 ${
                              isHl
                                ? "bg-cyan-100 ring-1 ring-cyan-500"
                                : "bg-cyan-50 hover:bg-cyan-100"
                            }`}
                          >
                            <span className="text-[9px] uppercase text-cyan-700 font-semibold flex-shrink-0">
                              user
                            </span>
                            <span className="font-mono text-[10px] flex-shrink-0">
                              {polyId}
                            </span>
                            <span className="text-slate-700 flex-1 truncate">
                              {w.label || `Fenster ${w._idx + 1}`}
                            </span>
                          </li>
                        );
                      });
                      return items;
                    })}
                    {totalWindows === 0 && (
                      <li className="p-3 text-[11px] italic text-slate-400">
                        Keine Fenster erkannt.
                      </li>
                    )}
                  </ul>
                )}
                {leftListFilter === "walls" && (
                  <ul className="text-xs">
                    {featuresZSorted.flatMap((f) =>
                      (f.properties.walls ?? []).map((w, i) => {
                        const polyId = f.properties.id;
                        const lengthM = (w.lengthMm ?? 0) / 1000;
                        const isSel =
                          selectedId === polyId && highlightedWallIdx === i;
                        return (
                          <li
                            key={`${polyId}-wall-${i}`}
                            onClick={() => {
                              setSelectedId(polyId);
                              setHighlightedWallIdx(i);
                            }}
                            className={`px-2 py-1 border-b cursor-pointer flex items-center gap-1.5 ${
                              isSel
                                ? "bg-blue-100 ring-1 ring-blue-500"
                                : "hover:bg-slate-50"
                            }`}
                          >
                            <span className="font-mono text-[10px] flex-shrink-0">
                              {polyId}
                            </span>
                            <span className="text-slate-700 flex-1 truncate">
                              Wand {i + 1}
                            </span>
                            <span className="font-mono text-[10px] text-slate-500 flex-shrink-0">
                              {lengthM.toFixed(2)} m
                            </span>
                          </li>
                        );
                      })
                    )}
                    {totalWalls === 0 && (
                      <li className="p-3 text-[11px] italic text-slate-400">
                        Keine Wände erkannt.
                      </li>
                    )}
                  </ul>
                )}
              </div>

              {/* Atelier-Footer: Gesamt-Fläche */}
              <div
                style={{
                  padding: "10px 14px",
                  borderTop: "1px solid var(--bd-line)",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  background: "var(--bd-surface2)",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--bd-text-3)",
                    letterSpacing: 0.4,
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Gesamt {runMeta?.etage ? runMeta.etage : ""}
                </span>
                <span
                  style={{
                    fontFamily: "var(--bd-mono)",
                    fontSize: 14,
                    color: "var(--bd-text)",
                    fontWeight: 600,
                  }}
                >
                  {totalArea.toFixed(2).replace(".", ",")}{" "}
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--bd-text-3)",
                      fontWeight: 400,
                    }}
                  >
                    m²
                  </span>
                </span>
              </div>
            </>
          );
        })()}
      </aside>

      {/* Splitter Linkes Panel ↔ Plan */}
      <div
        onMouseDown={() => setIsResizingLeft(true)}
        className={`w-1 cursor-col-resize flex-shrink-0 transition-colors ${
          isResizingLeft
            ? "bg-cyan-500"
            : "bg-slate-200 hover:bg-cyan-400"
        }`}
        title="Linke Panel-Breite anpassen"
      />

      {/* Plan-View Mitte (Sprint Atelier — heller Architekten-Papier-Hintergrund
          statt dem alten slate-900). */}
      <div
        className="flex-1 overflow-auto p-4 relative"
        style={{ background: "var(--bd-bg)" }}
      >
        {/* Atelier Mini-Stats-Overlay top-left (Sprint Atelier Phase 5) —
            zeigt Total-Area + Polygon-Count + Wand-Count, sodass auch ohne
            Auswahl die wichtigsten Kennzahlen direkt am Plan ablesbar sind. */}
        {(() => {
          const innerRooms = featuresZSorted.filter(
            (f) =>
              !(f.properties as {
                isBuilding?: boolean;
                isGaLaArea?: boolean;
              }).isBuilding &&
              !(f.properties as {
                isBuilding?: boolean;
                isGaLaArea?: boolean;
              }).isGaLaArea
          );
          const totalAreaM2 = innerRooms.reduce(
            (s, f) => s + (f.properties.areaM2 ?? 0),
            0
          );
          const totalWalls = featuresZSorted.reduce(
            (s, f) => s + (f.properties.walls?.length ?? 0),
            0
          );
          if (innerRooms.length === 0) return null;
          return (
            <div
              className="absolute z-10"
              style={{
                top: 12,
                left: 12,
                padding: "8px 10px",
                background:
                  "color-mix(in oklab, var(--bd-surface) 92%, transparent)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
                borderRadius: 6,
                border: "1px solid var(--bd-line)",
                display: "flex",
                gap: 14,
                fontFamily: "var(--bd-mono)",
                fontSize: 11,
                alignItems: "center",
                pointerEvents: "none",
                boxShadow: "var(--bd-shadow)",
              }}
            >
              <div>
                <div
                  style={{
                    color: "var(--bd-text-3)",
                    fontSize: 9,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Fläche
                </div>
                <div
                  style={{
                    color: "var(--bd-text)",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalAreaM2.toFixed(2).replace(".", ",")} m²
                </div>
              </div>
              <div
                style={{
                  width: 1,
                  height: 22,
                  background: "var(--bd-line)",
                }}
              />
              <div>
                <div
                  style={{
                    color: "var(--bd-text-3)",
                    fontSize: 9,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Räume
                </div>
                <div
                  style={{
                    color: "var(--bd-text)",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {innerRooms.length}
                </div>
              </div>
              <div
                style={{
                  width: 1,
                  height: 22,
                  background: "var(--bd-line)",
                }}
              />
              <div>
                <div
                  style={{
                    color: "var(--bd-text-3)",
                    fontSize: 9,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Wände
                </div>
                <div
                  style={{
                    color: "var(--bd-text)",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalWalls}
                </div>
              </div>
            </div>
          );
        })()}
        {/* Atelier Floating Edit-Toolbar (Sprint Atelier Phase 8 — 2026-05-02).
            Sticky am Top-Rand des Plan-Containers, damit Speichern/Abbrechen
            beim Scrollen erreichbar bleiben. Atelier-Look: heller Surface-Bg
            mit subtilen Schatten + Mono-Status-Text statt amber-Banner. */}
        {editMode !== "view" && (
          <div
            className="sticky top-0 z-20 mx-auto w-fit max-w-full text-sm flex items-center gap-3"
            style={{
              background: "var(--bd-surface)",
              border: "1px solid var(--bd-line)",
              borderLeft: "3px solid var(--bd-ember)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(28,26,23,0.12)",
              padding: "8px 14px",
              color: "var(--bd-text)",
              fontFamily: "var(--bd-sans)",
            }}
          >
            {editMode === "splitPicking" && (
              <>
                <span className="font-semibold">Polygon teilen: {splitTarget}</span>
                <span>
                  {splitPoints.length === 0
                    ? "Klick 1: Startpunkt der Trennlinie."
                    : "Klick 2: Endpunkt der Trennlinie."}
                </span>
                {(() => {
                  const m = bundle?.dimensions?.roomNgf.polygonMatches.find(
                    (mm) => mm.polygonId === splitTarget
                  );
                  if (!m || m.ngfStamps.length < 2) return null;
                  return (
                    <button
                      onClick={applyAutoSplitSuggestion}
                      className="bg-indigo-600 text-white text-xs px-3 py-1 rounded hover:bg-indigo-700"
                      title={`Schlaegt eine Trennlinie zwischen den ${m.ngfStamps.length} NGF-Stempeln vor`}
                    >
                      ✨ Auto-Vorschlag ({m.ngfStamps.map((s) => s.roomName).join(" / ")})
                    </button>
                  );
                })()}
              </>
            )}
            {editMode === "splitPreview" && (
              <>
                <span className="font-semibold">Split-Vorschau {splitTarget}</span>
                <span>
                  Aufteilung: {Math.round((splitPreview?.fractionA ?? 0) * 100)}% /{" "}
                  {Math.round((1 - (splitPreview?.fractionA ?? 0)) * 100)}%
                </span>
                <button
                  onClick={confirmSplit}
                  disabled={splitSaving}
                  className="bg-emerald-600 text-white text-xs px-3 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  {splitSaving ? "Speichert…" : "Split bestätigen"}
                </button>
              </>
            )}
            {editMode === "mergeTarget" && (
              <>
                <span className="font-semibold">Mergen mit: {mergeFirstId}</span>
                <span>
                  Klicke das zweite Polygon, das mit {mergeFirstId} zusammengefuegt
                  werden soll.
                </span>
              </>
            )}
            {editMode === "mergePreview" && (
              <>
                <span className="font-semibold">
                  Merge-Vorschau {mergeFirstId} + {mergeSecondId}
                </span>
                <span>
                  Neue Flaeche: {mergePreview ? mergePreview.area.toFixed(0) : "—"} pt²
                </span>
                <button
                  onClick={confirmMerge}
                  disabled={mergeSaving}
                  className="bg-emerald-600 text-white text-xs px-3 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  {mergeSaving ? "Speichert…" : "Merge bestätigen"}
                </button>
              </>
            )}
            {editMode === "drawWall" && (
              <>
                <span className="font-semibold">Virtuellen Schnitt zeichnen</span>
                <span>
                  {!wallStart
                    ? "Klick 1: Schnitt-Anfang setzen."
                    : "Klick 2: Schnitt-Ende setzen — wird gespeichert."}
                </span>
                {wallSaving && (
                  <span className="text-emerald-700">Speichert…</span>
                )}
              </>
            )}
            {editMode === "drawWallSegment" && (
              <>
                <span className="font-semibold">
                  Wand-Segment zu {wallSegmentTargetPolygonId} hinzufügen
                </span>
                <span>
                  {!wallSegmentStart
                    ? "Klick 1: Anfang der Wand."
                    : "Klick 2: Ende der Wand — wird mit Default-Material gespeichert."}
                </span>
                {wallEditSaving && (
                  <span className="text-emerald-700">Speichert…</span>
                )}
              </>
            )}
            {editMode === "drawPolygon" && (
              <>
                <span className="font-semibold">
                  {drawPolygonIsHaus ? "🏠 HAUS-Außenkontur zeichnen" : "📐 Raum zeichnen"}
                </span>
                <span>
                  {drawPolygonIsHaus
                    ? "Außenwand-Linie nachzeichnen — Container für Klinker, Dämmung, Außenputz."
                    : null}
                  {drawPolygonIsHaus ? <br /> : null}
                  Klicks setzen Vertices ({drawPolygonPoints.length} bisher).
                  {drawPolygonPoints.length >= 3
                    ? " Klick auf ersten Punkt (grün) oder „Abschließen“ speichert."
                    : " Mindestens 3 Punkte nötig."}
                </span>
                {drawPolygonPoints.length >= 3 && (
                  <button
                    onClick={finishDrawPolygon}
                    disabled={drawPolygonSaving}
                    className="text-xs px-2 py-0.5 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {drawPolygonSaving ? "Speichert…" : "Abschließen"}
                  </button>
                )}
                <button
                  onClick={cancelDrawPolygon}
                  className="text-xs text-amber-900 underline hover:text-amber-700"
                >
                  Abbrechen
                </button>
              </>
            )}
            {editMode === "drawOpening" && openingPickContext && (
              <>
                <span className="font-semibold">
                  {openingPickContext.type === "door" ? "🚪 Tür" : "🪟 Fenster"}{" "}
                  in {openingPickContext.polygonId} setzen
                </span>
                <span>
                  {!openingPickStart
                    ? "Klick 1: Anfang der Öffnung an der Wand."
                    : "Klick 2: Ende der Öffnung — danach öffnet das Maß-Modal."}
                </span>
                <button
                  onClick={() => {
                    setEditMode("view");
                    setOpeningPickStart(null);
                    setOpeningPickContext(null);
                  }}
                  className="text-xs text-amber-900 underline hover:text-amber-700"
                >
                  Abbrechen
                </button>
              </>
            )}
            {editMode === "scalePicking" && (
              <>
                <span className="font-semibold">Maßstab-Punkte setzen</span>
                <span>
                  {!scalePickStart
                    ? "Klick 1: Erster Punkt einer bekannten Strecke."
                    : "Klick 2: Zweiter Punkt — danach Real-Maß eingeben."}
                </span>
                <button
                  onClick={() => {
                    setEditMode("view");
                    setScalePickStart(null);
                  }}
                  className="text-xs text-amber-900 underline hover:text-amber-700"
                >
                  Abbrechen
                </button>
              </>
            )}
            {editMode === "editWallEndpoints" &&
              wallEditPolygonId &&
              wallEditEndpoints && (() => {
                const dx = wallEditEndpoints.endPt.x - wallEditEndpoints.startPt.x;
                const dy = wallEditEndpoints.endPt.y - wallEditEndpoints.startPt.y;
                const lenPt = Math.hypot(dx, dy);
                const ratioFor = bundle?.classification.pages[0]?.scale?.ratio ?? 100;
                const lenM = (lenPt * 25.4 * ratioFor) / 72 / 1000;
                return (
                  <>
                    <span className="font-semibold">
                      Wand-Endpunkte: {wallEditPolygonId} #{wallEditIdx}
                    </span>
                    <span>{lenM.toFixed(2)} m</span>
                    <button
                      onClick={saveWallEndpoints}
                      disabled={wallEditSaving}
                      className="bg-emerald-600 text-white text-xs px-3 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {wallEditSaving ? "Speichert…" : "Speichern"}
                    </button>
                  </>
                );
              })()}
            {editMode === "vertexEdit" &&
              vertexEditPolygonId &&
              editingRing &&
              (() => {
                const ratioFor = bundle?.classification.pages[0]?.scale?.ratio ?? 100;
                const m = realMetricsFromRing(editingRing, ratioFor);
                return (
                  <>
                    <span className="font-semibold">
                      Polygon bearbeiten: {vertexEditPolygonId}
                    </span>
                    <span>
                      {m.areaM2.toFixed(2)} m² · {m.perimeterM.toFixed(2)} m
                    </span>
                    <button
                      onClick={saveVertexEdit}
                      disabled={vertexEditSaving}
                      className="bg-emerald-600 text-white text-xs px-3 py-1 rounded hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {vertexEditSaving ? "Speichert…" : "Speichern"}
                    </button>
                  </>
                );
              })()}
            <button
              onClick={cancelEdit}
              className="bg-slate-700 text-white text-xs px-3 py-1 rounded hover:bg-slate-800"
            >
              Abbrechen
            </button>
          </div>
        )}
        {wallError && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 bg-red-100 border border-red-300 text-red-800 rounded px-3 py-1 text-xs max-w-2xl">
            {wallError}
          </div>
        )}
        {vertexEditError && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 bg-red-100 border border-red-300 text-red-800 rounded px-3 py-1 text-xs max-w-2xl">
            {vertexEditError}
          </div>
        )}
        {wallEditError && (
          <div className="sticky top-12 z-20 mx-auto w-fit max-w-2xl bg-red-100 border border-red-300 text-red-800 rounded px-3 py-1 text-xs mt-2">
            {wallEditError}
          </div>
        )}
        {splitError && (
          <div className="sticky top-12 z-20 mx-auto w-fit max-w-2xl bg-red-100 border border-red-300 text-red-800 rounded px-3 py-1 text-xs mt-2">
            {splitError}
          </div>
        )}
        {mergeError && (
          <div className="sticky top-12 z-20 mx-auto w-fit max-w-2xl bg-red-100 border border-red-300 text-red-800 rounded px-3 py-1 text-xs mt-2">
            {mergeError}
          </div>
        )}

        {/* Atelier (Sprint Phase 5 — 2026-05-02) — Zoom-Toolbar unten zentriert,
            jetzt im hellen Atelier-Look (Surface-Bg, Line-Border, Mono-Anzeige). */}
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center"
          style={{
            background: "var(--bd-surface)",
            border: "1px solid var(--bd-line)",
            borderRadius: 7,
            boxShadow: "var(--bd-shadow)",
            padding: 2,
            gap: 1,
          }}
        >
          <button
            onClick={() => {
              const newZoom = Math.max(1, zoom / 1.5);
              setZoom(newZoom);
              if (newZoom === 1) setPan({ x: 0, y: 0 });
            }}
            style={{
              padding: "5px 9px",
              border: "none",
              background: "transparent",
              color: "var(--bd-text-2)",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              borderRadius: 5,
            }}
            title="Auszoomen"
          >
            −
          </button>
          <span
            style={{
              padding: "5px 8px",
              fontFamily: "var(--bd-mono)",
              fontSize: 11,
              color: "var(--bd-text)",
              fontVariantNumeric: "tabular-nums",
              minWidth: 44,
              textAlign: "center",
            }}
          >
            {zoom.toFixed(1)}×
          </span>
          <button
            onClick={() => setZoom(Math.min(20, zoom * 1.5))}
            style={{
              padding: "5px 9px",
              border: "none",
              background: "transparent",
              color: "var(--bd-text-2)",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              borderRadius: 5,
            }}
            title="Reinzoomen"
          >
            +
          </button>
          {zoom > 1 && (
            <button
              onClick={resetZoom}
              style={{
                padding: "5px 9px",
                border: "none",
                background: "transparent",
                color: "var(--bd-ember-strong)",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                borderRadius: 5,
              }}
              title="Zoom zuruecksetzen"
            >
              ↺
            </button>
          )}
          {!magnifierActive && (
            <>
              <span
                style={{
                  width: 1,
                  height: 18,
                  background: "var(--bd-line)",
                  margin: "0 2px",
                }}
              />
              <button
                onClick={() => setMagnifierActive(true)}
                style={{
                  padding: "5px 9px",
                  border: "none",
                  background: "transparent",
                  color: "var(--bd-text-2)",
                  fontSize: 13,
                  cursor: "pointer",
                  borderRadius: 5,
                }}
                title="Detail-Lupe einblenden (folgt der Maus)"
              >
                🔍
              </button>
            </>
          )}
        </div>
        {zoom > 1 && (
          <div className="absolute bottom-4 left-4 z-10 bg-white/90 border rounded text-[11px] px-2 py-1 text-slate-600">
            Shift + Drag oder Mittelmaus = Verschieben · Mausrad = Zoom
          </div>
        )}

        {/* Sprint 0.19 — Detail-Lupe oben rechts (Cursor-Vergrößerung).
            ~6.7× Lupen-Vergrößerung bei zoom=1. Folgt der Maus über dem Plan.
            pointer-events: none am Container damit Klicks durchgehen — nur
            der ×-Schließen-Button hat eigene Events. */}
        {magnifierActive && magnifierPt && (
          <div
            className="absolute bottom-14 right-4 z-10 bg-white border-2 border-slate-300 rounded shadow-lg overflow-hidden"
            style={{
              width: 200,
              height: 200,
              pointerEvents: "none",
            }}
          >
            <div className="absolute top-1 left-1 z-20 bg-white/85 px-1.5 py-0.5 rounded text-[10px] font-mono text-slate-700">
              🔍 ×{(6.67 * zoom).toFixed(1)}
            </div>
            <button
              onClick={() => setMagnifierActive(false)}
              className="absolute top-0.5 right-0.5 z-20 w-5 h-5 bg-white/90 hover:bg-red-100 text-slate-500 hover:text-red-600 rounded text-xs leading-none flex items-center justify-center"
              style={{ pointerEvents: "auto" }}
              title="Lupe schließen"
            >
              ×
            </button>
            <svg
              viewBox={`${magnifierPt.x - 15 / zoom} ${phPt - magnifierPt.y - 15 / zoom} ${30 / zoom} ${30 / zoom}`}
              width={200}
              height={200}
              preserveAspectRatio="xMidYMid meet"
              style={{ display: "block" }}
            >
              {bundle.index.planBitmap && (
                <image
                  href={api.multiagentPlanBitmapUrl(runId, planBitmapLayer)}
                  x={0}
                  y={0}
                  width={pwPt}
                  height={phPt}
                  preserveAspectRatio="none"
                  style={{ pointerEvents: "none" }}
                />
              )}
              {/* Polygon-Outlines (kein Fill, damit Plan sichtbar bleibt) */}
              {featuresZSorted.map((f) => {
                const id = f.properties.id;
                const st = polyStatus.get(id) ?? "pending";
                if (st === "outdoor" && !showOutdoor) return null;
                const ring = f.geometry.coordinates[0];
                const pts = ring
                  .map(
                    ([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`
                  )
                  .join(" ");
                const stroke =
                  st === "matched"
                    ? "rgb(34, 197, 94)"
                    : st === "splitCandidate"
                      ? "rgb(202, 138, 4)"
                      : st === "pending"
                        ? "rgb(234, 88, 12)"
                        : "rgb(148, 163, 184)";
                return (
                  <polygon
                    key={id}
                    points={pts}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
              {/* User-Wand-Striche */}
              {bundle.userWalls.map((w, i) => (
                <line
                  key={`mag-uw-${i}`}
                  x1={w.from.x}
                  y1={phPt - w.from.y}
                  x2={w.to.x}
                  y2={phPt - w.to.y}
                  stroke="rgb(37, 99, 235)"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {/* Vertex-Edit-Polygon + Handles in der Lupe */}
              {editMode === "vertexEdit" && editingRing && (
                <g>
                  <polygon
                    points={editingRing
                      .map(
                        ([x, y]) =>
                          `${x.toFixed(2)},${(phPt - y).toFixed(2)}`
                      )
                      .join(" ")}
                    fill="rgba(59, 130, 246, 0.10)"
                    stroke="rgb(29, 78, 216)"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                  />
                  {editingRing.slice(0, -1).map(([x, y], i) => (
                    <circle
                      key={`mag-vh-${i}`}
                      cx={x}
                      cy={phPt - y}
                      r={2 / zoom}
                      fill={
                        draggingVertexIdx === i
                          ? "rgb(220, 38, 38)"
                          : "rgb(29, 78, 216)"
                      }
                      stroke="white"
                      strokeWidth={1}
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </g>
              )}
              {/* Fadenkreuz an Cursor-Position */}
              <g style={{ pointerEvents: "none" }}>
                <line
                  x1={magnifierPt.x - 5 / zoom}
                  y1={phPt - magnifierPt.y}
                  x2={magnifierPt.x + 5 / zoom}
                  y2={phPt - magnifierPt.y}
                  stroke="rgb(220, 38, 38)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <line
                  x1={magnifierPt.x}
                  y1={phPt - magnifierPt.y - 5 / zoom}
                  x2={magnifierPt.x}
                  y2={phPt - magnifierPt.y + 5 / zoom}
                  stroke="rgb(220, 38, 38)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
                <circle
                  cx={magnifierPt.x}
                  cy={phPt - magnifierPt.y}
                  r={1.5 / zoom}
                  fill="none"
                  stroke="rgb(220, 38, 38)"
                  strokeWidth={1}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            </svg>
          </div>
        )}
        {/* F13.2 — Lupen-Toggle wandert in die untere Toolbar (siehe oben). */}

        <div
          className="bg-white shadow-2xl mx-auto"
          style={{ aspectRatio: `${pwPt} / ${phPt}`, maxWidth: "100%" }}
        >
          <div className="relative w-full h-full">
            <svg
              ref={svgRef}
              onClick={handleSvgClick}
              onWheel={onSvgWheel}
              onPointerDown={onSvgPointerDown}
              onPointerMove={onSvgPointerMove}
              onPointerUp={onSvgPointerUp}
              onPointerCancel={onSvgPointerUp}
              viewBox={`${pan.x} ${pan.y} ${pwPt / zoom} ${phPt / zoom}`}
              preserveAspectRatio="xMidYMid meet"
              className="absolute inset-0 w-full h-full"
              style={{
                cursor:
                  editMode === "splitPicking" ||
                  editMode === "drawWall" ||
                  editMode === "drawWallSegment" ||
                  editMode === "scalePicking" ||
                  editMode === "drawOpening" ||
                  editMode === "drawPolygon"
                    ? "crosshair"
                    : panStartRef.current
                      ? "grabbing"
                      : zoom > 1
                        ? "grab"
                        : "default",
                touchAction: zoom > 1 ? "none" : "auto",
              }}
            >
              {bundle.index.planBitmap && (
                <image
                  href={api.multiagentPlanBitmapUrl(runId, planBitmapLayer, {
                    hideFurniture: hideFurniture && planBitmapLayer === "all",
                  })}
                  x={0}
                  y={0}
                  width={pwPt}
                  height={phPt}
                  style={{ pointerEvents: "none" }}
                  preserveAspectRatio="none"
                />
              )}
              {featuresZSorted.map((f) => {
                const ring = f.geometry.coordinates[0];
                const points = ring
                  .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                  .join(" ");
                const id = f.properties.id;
                const status = polyStatus.get(id) ?? "pending";
                const isSelected = selectedId === id;

                // Sprint 0.19 — outdoor optional komplett ausblenden
                if (status === "outdoor" && !showOutdoor) return null;

                // Status-spezifische Farbgebung. Grundsatz:
                //   matched         → fertig, fast unsichtbar (nur dünne Outline)
                //   splitCandidate  → gelb/orange, ⚠ User-Eingriff empfohlen
                //   pending         → orange Fill, ✏ User-Eingriff nötig
                //   outdoor         → grau dimmed, 🚫 standardmäßig nicht-relevant
                let fill = "rgba(0,0,0,0)";
                let stroke = "rgb(120, 130, 145)";
                let strokeWidth = 1.5;
                let strokeDash: string | undefined;
                let marker: string | null = null;
                let markerColor = "rgb(50, 50, 50)";

                if (status === "building") {
                  // Sprint 0.19 / Punkt 4 — Haus-Polygon: dunkel-grau dicker
                  // Outline, kein Fill (damit Innenräume sichtbar bleiben).
                  fill = "rgba(0,0,0,0)";
                  stroke = "rgb(55, 65, 81)";
                  strokeWidth = 4;
                  strokeDash = "10,5";
                  marker = "🏠";
                  markerColor = "rgb(31, 41, 55)";
                } else if (status === "gala") {
                  // Sprint #6 (2026-05-05) — GALA-Polygon: grün gestrichelt,
                  // sehr dezenter Fill (damit HAUS + Räume drüber sichtbar
                  // bleiben). Klicks werden disabled (analog HAUS) — User
                  // selektiert GALA über die Sidebar.
                  fill = `rgba(34, 197, 94, ${opacity * 0.08})`;
                  stroke = "rgb(22, 163, 74)";
                  strokeWidth = 3;
                  strokeDash = "12,6";
                  marker = "🌿";
                  markerColor = "rgb(20, 83, 45)";
                } else if (status === "matched") {
                  fill = showFill
                    ? `rgba(34, 197, 94, ${opacity * 0.35})` // sehr dezent grünlich
                    : "rgba(0,0,0,0)";
                  stroke = "rgb(34, 197, 94)";
                  strokeWidth = 1.5;
                  marker = "✓";
                  markerColor = "rgb(21, 128, 61)";
                } else if (status === "splitCandidate") {
                  fill = showFill
                    ? `rgba(234, 179, 8, ${opacity * 1.1})` // gelb dimmed
                    : "rgba(0,0,0,0)";
                  stroke = "rgb(202, 138, 4)";
                  strokeWidth = showSplitsHighlight ? 3 : 2;
                  marker = "⚠";
                  markerColor = "rgb(120, 53, 15)";
                } else if (status === "pending") {
                  fill = showFill
                    ? `rgba(249, 115, 22, ${opacity})` // orange — User soll prüfen
                    : "rgba(0,0,0,0)";
                  stroke = "rgb(234, 88, 12)";
                  strokeWidth = 2;
                  marker = "✏";
                  markerColor = "rgb(154, 52, 18)";
                } else {
                  // outdoor — sehr dezent grau, gestrichelt, halb-transparent
                  fill = `rgba(148, 163, 184, ${opacity * 0.25})`;
                  stroke = "rgb(148, 163, 184)";
                  strokeWidth = 1;
                  strokeDash = "4,3";
                  marker = "🚫";
                  markerColor = "rgb(71, 85, 105)";
                }

                if (isSelected) {
                  stroke = "rgb(15, 23, 42)";
                  strokeWidth = 4;
                }

                const cx = ring.reduce((s, [x]) => s + x, 0) / ring.length;
                const cy =
                  phPt - ring.reduce((s, [, y]) => s + y, 0) / ring.length;
                return (
                  <g
                    key={id}
                    onClick={(e) => {
                      // HAUS und GALA fangen im Plan keine Klicks ab — sonst
                      // kann man Innenräume nicht mehr anwählen oder
                      // splitPicking-Punkte innerhalb der Außenkontur setzen.
                      // Beide sind weiterhin per Sidebar-Eintrag selektierbar.
                      if (status === "building" || status === "gala") return;
                      e.stopPropagation();
                      if (weAssignTarget && editMode === "view") {
                        // Sprint 0.21 — WE-Assignment-Modus: Klick toggelt
                        // Raum in/aus der aktiven WE.
                        toggleRaumInWohneinheit(weAssignTarget, id);
                        return;
                      }
                      if (editMode === "mergeTarget") {
                        tryMergeWith(id);
                      } else if (editMode === "view") {
                        setSelectedId(id);
                      }
                    }}
                    style={{
                      cursor:
                        editMode === "mergeTarget" && id !== mergeFirstId
                          ? "copy"
                          : editMode === "view"
                            ? "pointer"
                            : "default",
                      opacity: status === "outdoor" ? 0.55 : 1,
                      pointerEvents:
                        status === "building" || status === "gala"
                          ? "none"
                          : undefined,
                    }}
                  >
                    <polygon
                      points={points}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                      strokeDasharray={strokeDash}
                      vectorEffect="non-scaling-stroke"
                    />
                    {/* Sprint 0.21 — WE-Tint Overlay: zarter Farb-Wash pro WE */}
                    {(() => {
                      const we = roomToWe.get(id);
                      if (!we) return null;
                      const tint = weColorFor(we.id);
                      const isAssigning = weAssignTarget === we.id;
                      return (
                        <polygon
                          points={points}
                          fill={tint}
                          fillOpacity={isAssigning ? 0.35 : 0.18}
                          stroke="none"
                          style={{ pointerEvents: "none" }}
                        />
                      );
                    })()}
                    {showLabels && (
                      <>
                        {marker && (
                          <text
                            x={cx.toFixed(2)}
                            y={(cy - 8).toFixed(2)}
                            fontSize={11}
                            textAnchor="middle"
                            fontFamily="sans-serif"
                            fill={markerColor}
                            stroke="white"
                            strokeWidth={0.6}
                            paintOrder="stroke"
                            style={{ pointerEvents: "none" }}
                          >
                            {marker}
                          </text>
                        )}
                        <text
                          x={cx.toFixed(2)}
                          y={cy.toFixed(2)}
                          fontSize={12}
                          textAnchor="middle"
                          fontFamily="sans-serif"
                          fontWeight="bold"
                          fill="black"
                          stroke="white"
                          strokeWidth={0.6}
                          paintOrder="stroke"
                          style={{ pointerEvents: "none" }}
                        >
                          {id} · {f.properties.areaM2.toFixed(2)}m²
                        </text>
                        {(() => {
                          // Sprint 0.21 — WE-Badge unter dem Raum-Label
                          const we = roomToWe.get(id);
                          if (!we) return null;
                          return (
                            <text
                              x={cx.toFixed(2)}
                              y={(cy + 12).toFixed(2)}
                              fontSize={9}
                              textAnchor="middle"
                              fontFamily="sans-serif"
                              fill="rgb(15, 23, 42)"
                              stroke="white"
                              strokeWidth={0.6}
                              paintOrder="stroke"
                              style={{ pointerEvents: "none" }}
                            >
                              {we.label}
                            </text>
                          );
                        })()}
                      </>
                    )}
                  </g>
                );
              })}

              {/* NGF-Stempel-Anchors */}
              {showNgfAnchors &&
                ngfStamps.map((s, i) => (
                  <g key={`ngf-${i}`} style={{ pointerEvents: "none" }}>
                    <circle
                      cx={s.anchor.x}
                      cy={phPt - s.anchor.y}
                      r={scaleR(3)}
                      fill="rgb(37, 99, 235)"
                      stroke="white"
                      strokeWidth={1}
                      vectorEffect="non-scaling-stroke"
                    />
                    <text
                      x={s.anchor.x + 5}
                      y={phPt - s.anchor.y - 5}
                      fontSize={9}
                      fill="rgb(30, 58, 138)"
                      stroke="white"
                      strokeWidth={0.4}
                      paintOrder="stroke"
                      fontFamily="sans-serif"
                    >
                      {s.roomName} · {s.ngfM2}m²
                    </text>
                  </g>
                ))}

              {/* Sprint 0.19 — markierte Wand aus dem Detail-Panel */}
              {selectedFeature &&
                highlightedWallIdx !== null &&
                selectedFeature.properties.walls?.[highlightedWallIdx] &&
                (() => {
                  const w =
                    selectedFeature.properties.walls![highlightedWallIdx];
                  return (
                    <g style={{ pointerEvents: "none" }}>
                      <line
                        x1={w.startPt.x}
                        y1={phPt - w.startPt.y}
                        x2={w.endPt.x}
                        y2={phPt - w.endPt.y}
                        stroke="rgb(37, 99, 235)"
                        strokeWidth={5}
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                        opacity={0.85}
                      />
                      <circle
                        cx={w.startPt.x}
                        cy={phPt - w.startPt.y}
                        r={scaleR(4)}
                        fill="rgb(37, 99, 235)"
                        stroke="white"
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                      />
                      <circle
                        cx={w.endPt.x}
                        cy={phPt - w.endPt.y}
                        r={scaleR(4)}
                        fill="rgb(37, 99, 235)"
                        stroke="white"
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                      />
                    </g>
                  );
                })()}

              {/* F8 — Highlight für die in der Sidebar geklickte Tür/Fenster.
                  AUTO-Türen: Linie + Endpoint-Circles aus doorsByPolyId.
                  USER-Öffnungen: Linie + Circles aus properties.openings[idx].
                  AUTO-Fenster: gelber Ring um das Polygon (genaue Wandposition
                  nicht bekannt, da OCR-Label außerhalb der Außenwand). */}
              {highlightedOpening &&
                (() => {
                  const ho = highlightedOpening;
                  const yellowStroke = "rgb(234, 179, 8)";
                  // AUTO-Tür → Linie + Endpoints aus doorsByPolyId
                  if (ho.source === "auto" && ho.type === "door") {
                    const d = doorsByPolyId.get(ho.polyId)?.[ho.idx];
                    if (!d) return null;
                    return (
                      <g style={{ pointerEvents: "none" }}>
                        <line
                          x1={d.from.x}
                          y1={phPt - d.from.y}
                          x2={d.to.x}
                          y2={phPt - d.to.y}
                          stroke={yellowStroke}
                          strokeWidth={6}
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                          opacity={0.9}
                        />
                        <circle
                          cx={d.from.x}
                          cy={phPt - d.from.y}
                          r={scaleR(5)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                        <circle
                          cx={d.to.x}
                          cy={phPt - d.to.y}
                          r={scaleR(5)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    );
                  }
                  // USER-Öffnung → Linie aus properties.openings[idx]
                  if (ho.source === "user") {
                    const f = features.find(
                      (ff) => ff.properties.id === ho.polyId
                    );
                    const o = (
                      f?.properties.openings as
                        | Array<{
                            startPt?: { x: number; y: number };
                            endPt?: { x: number; y: number };
                          }>
                        | undefined
                    )?.[ho.idx];
                    if (!o?.startPt || !o.endPt) {
                      // Fallback: ring um Polygon
                      if (!f) return null;
                      const pts = f.geometry.coordinates[0]
                        .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                        .join(" ");
                      return (
                        <polygon
                          points={pts}
                          fill="none"
                          stroke={yellowStroke}
                          strokeWidth={4}
                          strokeDasharray="6,3"
                          vectorEffect="non-scaling-stroke"
                          style={{ pointerEvents: "none" }}
                        />
                      );
                    }
                    return (
                      <g style={{ pointerEvents: "none" }}>
                        <line
                          x1={o.startPt.x}
                          y1={phPt - o.startPt.y}
                          x2={o.endPt.x}
                          y2={phPt - o.endPt.y}
                          stroke={yellowStroke}
                          strokeWidth={6}
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                          opacity={0.9}
                        />
                        <circle
                          cx={o.startPt.x}
                          cy={phPt - o.startPt.y}
                          r={scaleR(5)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                        <circle
                          cx={o.endPt.x}
                          cy={phPt - o.endPt.y}
                          r={scaleR(5)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    );
                  }
                  // AUTO-Fenster → OCR-Anchor auf nächste Wand projizieren
                  // und dort einen Marker rendern (statt das ganze Polygon
                  // gestrichelt zu umranden).
                  if (ho.source === "auto" && ho.type === "window") {
                    const f = features.find(
                      (ff) => ff.properties.id === ho.polyId
                    );
                    if (!f) return null;
                    const w = windowsByPolyId.get(ho.polyId)?.[ho.idx];
                    if (!w?.anchor) {
                      // Fallback: Polygon-Ring (sollte praktisch nicht vorkommen)
                      const pts = f.geometry.coordinates[0]
                        .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                        .join(" ");
                      return (
                        <polygon
                          points={pts}
                          fill="none"
                          stroke={yellowStroke}
                          strokeWidth={4}
                          strokeDasharray="6,3"
                          vectorEffect="non-scaling-stroke"
                          style={{ pointerEvents: "none" }}
                        />
                      );
                    }
                    const ring = f.geometry.coordinates[0];
                    const proj = closestPointOnRing(w.anchor, ring);
                    if (!proj) return null;
                    // Wand-Halb-Breite ableiten: Fenster-Breite ist
                    // w.widthCm cm in Real-Welt; in Pt: widthCm / ptToCm.
                    // Wir nutzen das, um den Wand-Marker als kurze Linie
                    // entlang der Wand-Tangente zu zeichnen.
                    const widthPt = w.widthCm / ptToCm;
                    // Tangente am projizierten Punkt: nähestes Edge-Segment
                    // → finde das Edge-Segment in dem proj liegt.
                    let tx = 1;
                    let ty = 0;
                    let bestSeg = Infinity;
                    for (let i = 0; i < ring.length - 1; i++) {
                      const [ax, ay] = ring[i];
                      const [bx, by] = ring[i + 1];
                      const dx = bx - ax;
                      const dy = by - ay;
                      const lenSq = dx * dx + dy * dy;
                      let t = lenSq > 0 ? ((proj.x - ax) * dx + (proj.y - ay) * dy) / lenSq : 0;
                      if (t < 0) t = 0;
                      else if (t > 1) t = 1;
                      const cx = ax + t * dx;
                      const cy = ay + t * dy;
                      const d = Math.hypot(proj.x - cx, proj.y - cy);
                      if (d < bestSeg) {
                        bestSeg = d;
                        const len = Math.sqrt(lenSq);
                        if (len > 0) {
                          tx = dx / len;
                          ty = dy / len;
                        }
                      }
                    }
                    const halfW = widthPt / 2;
                    const x1 = proj.x - tx * halfW;
                    const y1 = proj.y - ty * halfW;
                    const x2 = proj.x + tx * halfW;
                    const y2 = proj.y + ty * halfW;
                    return (
                      <g style={{ pointerEvents: "none" }}>
                        {/* dünne Hilfslinie vom OCR-Label zur Wand */}
                        <line
                          x1={w.anchor.x}
                          y1={phPt - w.anchor.y}
                          x2={proj.x}
                          y2={phPt - proj.y}
                          stroke={yellowStroke}
                          strokeWidth={1.5}
                          strokeDasharray="3,2"
                          vectorEffect="non-scaling-stroke"
                          opacity={0.55}
                        />
                        {/* Fenster-Marker auf der Wand (Linie in Wand-Tangente) */}
                        <line
                          x1={x1}
                          y1={phPt - y1}
                          x2={x2}
                          y2={phPt - y2}
                          stroke={yellowStroke}
                          strokeWidth={6}
                          strokeLinecap="round"
                          vectorEffect="non-scaling-stroke"
                          opacity={0.9}
                        />
                        <circle
                          cx={x1}
                          cy={phPt - y1}
                          r={scaleR(4)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                        <circle
                          cx={x2}
                          cy={phPt - y2}
                          r={scaleR(4)}
                          fill={yellowStroke}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    );
                  }
                  return null;
                })()}

              {/* Sprint 0.20 — User-Öffnungen (Türen + Fenster aus openings[])
                  als Symbol auf der Wand. Tür: dicke Linie + 90°-Bogen
                  (Schwenk-Andeutung). Fenster: Doppellinie cyan. */}
              {features.flatMap((f) => {
                const polyId = f.properties.id;
                const opens = (f.properties.openings ?? []) as Array<{
                  type: "door" | "window";
                  startPt?: { x: number; y: number };
                  endPt?: { x: number; y: number };
                  addedBy?: "auto" | "user";
                }>;
                return opens
                  .map((o, i) => {
                    if (!o.startPt || !o.endPt) return null;
                    const x1 = o.startPt.x;
                    const y1 = phPt - o.startPt.y;
                    const x2 = o.endPt.x;
                    const y2 = phPt - o.endPt.y;
                    if (o.type === "door") {
                      const dx = x2 - x1;
                      const dy = y2 - y1;
                      const len = Math.hypot(dx, dy) || 1;
                      // Bogen-Endpunkt senkrecht zur Tür-Linie auf einer Seite
                      const nx = -dy / len;
                      const ny = dx / len;
                      const arcEndX = x1 + nx * len;
                      const arcEndY = y1 + ny * len;
                      return (
                        <g
                          key={`open-${polyId}-${i}`}
                          style={{ pointerEvents: "none" }}
                        >
                          <line
                            x1={x1}
                            y1={y1}
                            x2={x2}
                            y2={y2}
                            stroke="rgb(217, 119, 6)"
                            strokeWidth={3}
                            strokeLinecap="round"
                            vectorEffect="non-scaling-stroke"
                          />
                          {/* Tür-Schwenk als Quadrant-Bogen */}
                          <path
                            d={`M ${x2} ${y2} A ${len} ${len} 0 0 0 ${arcEndX} ${arcEndY}`}
                            stroke="rgb(217, 119, 6)"
                            strokeWidth={1}
                            fill="none"
                            strokeDasharray="2,2"
                            vectorEffect="non-scaling-stroke"
                          />
                        </g>
                      );
                    }
                    // Fenster: Doppellinie
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const len = Math.hypot(dx, dy) || 1;
                    const nx = (-dy / len) * 3;
                    const ny = (dx / len) * 3;
                    return (
                      <g
                        key={`open-${polyId}-${i}`}
                        style={{ pointerEvents: "none" }}
                      >
                        <line
                          x1={x1 + nx}
                          y1={y1 + ny}
                          x2={x2 + nx}
                          y2={y2 + ny}
                          stroke="rgb(8, 145, 178)"
                          strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1={x1 - nx}
                          y1={y1 - ny}
                          x2={x2 - nx}
                          y2={y2 - ny}
                          stroke="rgb(8, 145, 178)"
                          strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                        />
                        <line
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="rgb(8, 145, 178)"
                          strokeWidth={1}
                          vectorEffect="non-scaling-stroke"
                        />
                      </g>
                    );
                  })
                  .filter(Boolean);
              })}

              {/* Sprint 0.20 — In-progress Polygon während drawPolygon */}
              {editMode === "drawPolygon" && drawPolygonPoints.length > 0 && (
                <g style={{ pointerEvents: "none" }}>
                  {drawPolygonPoints.length > 1 && (
                    <polyline
                      points={drawPolygonPoints
                        .map((p) => `${p.x},${phPt - p.y}`)
                        .join(" ")}
                      fill="none"
                      stroke="rgb(16, 185, 129)"
                      strokeWidth={2}
                      strokeDasharray="4,3"
                      vectorEffect="non-scaling-stroke"
                    />
                  )}
                  {drawPolygonPoints.length >= 3 && (
                    <line
                      x1={
                        drawPolygonPoints[drawPolygonPoints.length - 1].x
                      }
                      y1={
                        phPt -
                        drawPolygonPoints[drawPolygonPoints.length - 1].y
                      }
                      x2={drawPolygonPoints[0].x}
                      y2={phPt - drawPolygonPoints[0].y}
                      stroke="rgb(16, 185, 129)"
                      strokeWidth={1}
                      strokeDasharray="2,4"
                      vectorEffect="non-scaling-stroke"
                      opacity={0.5}
                    />
                  )}
                  {drawPolygonPoints.map((p, i) => (
                    <circle
                      key={`dp-${i}`}
                      cx={p.x}
                      cy={phPt - p.y}
                      r={i === 0 ? scaleR(5) : scaleR(3)}
                      fill={i === 0 ? "rgb(5, 150, 105)" : "white"}
                      stroke="rgb(16, 185, 129)"
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  ))}
                </g>
              )}

              {/* Sprint 0.20 — Picking-Anker während drawOpening */}
              {editMode === "drawOpening" && openingPickStart && (
                <circle
                  cx={openingPickStart.x}
                  cy={phPt - openingPickStart.y}
                  r={scaleR(4)}
                  fill={
                    openingPickContext?.type === "door"
                      ? "rgb(217, 119, 6)"
                      : "rgb(8, 145, 178)"
                  }
                  stroke="white"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                />
              )}

              {/* F16 Phase 3 — Auto-T-Stoss-Trennlinien (orange-gestrichelt).
                  Klick auf eine Linie deaktiviert sie, triggert Re-Run. */}
              {showAutoTJunctions &&
                bundle.polygons.autoTJunctions?.map((tj) => (
                  <g
                    key={`tj-${tj.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (
                        !window.confirm(
                          tj.disabled
                            ? `Trennlinie ${tj.id} reaktivieren?\n\nPipeline rechnet automatisch neu.`
                            : `Trennlinie ${tj.id} deaktivieren?\n\nPipeline rechnet automatisch neu.`
                        )
                      ) {
                        return;
                      }
                      try {
                        if (tj.disabled) {
                          await api.multiagentEnableAutoTJunction(
                            runId,
                            tj.id,
                            true
                          );
                        } else {
                          await api.multiagentDisableAutoTJunction(
                            runId,
                            tj.id,
                            { rerunPolygons: true }
                          );
                        }
                        const fresh = await loadBundle(runId);
                        setBundle(fresh);
                      } catch (err) {
                        alert(
                          err instanceof Error
                            ? err.message
                            : "T-Stoss-Override fehlgeschlagen"
                        );
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <line
                      x1={tj.from.x}
                      y1={phPt - tj.from.y}
                      x2={tj.to.x}
                      y2={phPt - tj.to.y}
                      stroke={
                        tj.disabled ? "rgb(148, 163, 184)" : "rgb(234, 88, 12)"
                      }
                      strokeWidth={tj.disabled ? 1.5 : 2.5}
                      strokeDasharray="6,3"
                      opacity={tj.disabled ? 0.5 : 0.9}
                      vectorEffect="non-scaling-stroke"
                    />
                    <line
                      x1={tj.from.x}
                      y1={phPt - tj.from.y}
                      x2={tj.to.x}
                      y2={phPt - tj.to.y}
                      stroke="transparent"
                      strokeWidth={10}
                      vectorEffect="non-scaling-stroke"
                      style={{ cursor: "pointer" }}
                    />
                  </g>
                ))}

              {/* Tür-Wand-Linien: aus Bezier-Sehnen rekonstruierte Tür-Schließer.
                  F7 — suppressed AUTO-Türen werden ausgeblendet. */}
              {showDoorWalls &&
                bundle.doorWalls?.doorWalls.map((dw, i) => {
                  if (suppressedAutoDoorGlobalIdx.has(i)) return null;
                  return (
                  <g key={`door-${i}`} style={{ pointerEvents: "none" }}>
                    <line
                      x1={dw.from.x}
                      y1={phPt - dw.from.y}
                      x2={dw.to.x}
                      y2={phPt - dw.to.y}
                      stroke="rgb(244, 63, 94)"
                      strokeWidth={2}
                      strokeDasharray="4,3"
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={dw.from.x}
                      cy={phPt - dw.from.y}
                      r={scaleR(2)}
                      fill="rgb(244, 63, 94)"
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={dw.to.x}
                      cy={phPt - dw.to.y}
                      r={scaleR(2)}
                      fill="rgb(244, 63, 94)"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                  );
                })}

              {/* Sprint 0.19 — Virtuelle Schnitte (immer sichtbar). Aktiver
                  Schnitt aus dem Sidebar-Klick wird dick grün gerendert. */}
              {bundle.userWalls.map((w, i) => {
                const isHighlighted = selectedUserWallIdx === i;
                const stroke = isHighlighted
                  ? "rgb(16, 185, 129)"
                  : "rgb(37, 99, 235)";
                const strokeWidth = isHighlighted ? 6 : 3;
                return (
                  <g
                    key={`user-wall-${i}`}
                    style={{ pointerEvents: "none" }}
                  >
                    <line
                      x1={w.from.x}
                      y1={phPt - w.from.y}
                      x2={w.to.x}
                      y2={phPt - w.to.y}
                      stroke={stroke}
                      strokeWidth={strokeWidth}
                      strokeLinecap="round"
                      vectorEffect="non-scaling-stroke"
                      opacity={0.85}
                    />
                    <circle
                      cx={w.from.x}
                      cy={phPt - w.from.y}
                      r={scaleR(isHighlighted ? 5 : 3)}
                      fill={stroke}
                      stroke="white"
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={w.to.x}
                      cy={phPt - w.to.y}
                      r={scaleR(isHighlighted ? 5 : 3)}
                      fill={stroke}
                      stroke="white"
                      strokeWidth={1.5}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                );
              })}

              {/* Sprint 0.19 — Wand-Strich-Tool: Vorschau (1. Klick → Cursor) */}
              {editMode === "drawWall" && wallStart && (
                <circle
                  cx={wallStart.x}
                  cy={phPt - wallStart.y}
                  r={scaleR(5)}
                  fill="rgb(37, 99, 235)"
                  stroke="white"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  style={{ pointerEvents: "none" }}
                />
              )}

              {/* Sprint 0.19 — Wand-Segment zeichnen: Vorschau-Kreis */}
              {editMode === "drawWallSegment" && wallSegmentStart && (
                <circle
                  cx={wallSegmentStart.x}
                  cy={phPt - wallSegmentStart.y}
                  r={scaleR(5)}
                  fill="rgb(168, 85, 247)"
                  stroke="white"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  style={{ pointerEvents: "none" }}
                />
              )}

              {/* Sprint 0.19 — Wand-Endpunkt-Edit: Live-Linie + Drag-Handles */}
              {editMode === "editWallEndpoints" && wallEditEndpoints && (
                <g>
                  <line
                    x1={wallEditEndpoints.startPt.x}
                    y1={phPt - wallEditEndpoints.startPt.y}
                    x2={wallEditEndpoints.endPt.x}
                    y2={phPt - wallEditEndpoints.endPt.y}
                    stroke="rgb(37, 99, 235)"
                    strokeWidth={4}
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    opacity={0.8}
                    style={{ pointerEvents: "none" }}
                  />
                  {(["start", "end"] as const).map((which) => {
                    const pt =
                      which === "start"
                        ? wallEditEndpoints.startPt
                        : wallEditEndpoints.endPt;
                    const isActive = draggingWallEnd === which;
                    return (
                      <circle
                        key={`we-${which}`}
                        cx={pt.x}
                        cy={phPt - pt.y}
                        r={scaleR(isActive ? 7 : 5)}
                        fill={
                          isActive ? "rgb(220, 38, 38)" : "rgb(29, 78, 216)"
                        }
                        stroke="white"
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        style={{ cursor: "grab", pointerEvents: "all" }}
                        onPointerDown={(ev) => {
                          if (ev.button === 2) return;
                          ev.stopPropagation();
                          (ev.currentTarget as SVGCircleElement).setPointerCapture(
                            ev.pointerId
                          );
                          setDraggingWallEnd(which);
                        }}
                        onPointerUp={(ev) => {
                          ev.stopPropagation();
                          (ev.currentTarget as SVGCircleElement).releasePointerCapture(
                            ev.pointerId
                          );
                          setDraggingWallEnd(null);
                        }}
                      />
                    );
                  })}
                </g>
              )}

              {/* Sprint 0.19 — Polygon-Vertex-Drag: editiertes Polygon + Handles */}
              {editMode === "vertexEdit" && editingRing && (
                <g>
                  <polygon
                    points={editingRing
                      .map(
                        ([x, y]) =>
                          `${x.toFixed(2)},${(phPt - y).toFixed(2)}`
                      )
                      .join(" ")}
                    fill="rgba(59, 130, 246, 0.20)"
                    stroke="rgb(29, 78, 216)"
                    strokeWidth={3}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "none" }}
                  />
                  {/* Edge-Mid-Plus: kleines grünes Plus auf jeder Edge zum Hinzufügen
                      eines neuen Vertex an dieser Stelle (Mittelpunkt der Edge). */}
                  {editingRing.slice(0, -1).map((_, i) => {
                    const a = editingRing[i];
                    const b = editingRing[i + 1];
                    const mx = (a[0] + b[0]) / 2;
                    const my = (a[1] + b[1]) / 2;
                    return (
                      <g
                        key={`add-vh-${i}`}
                        style={{
                          cursor: "copy",
                          pointerEvents: "all",
                        }}
                        onPointerDown={(ev) => {
                          ev.stopPropagation();
                          if (!editingRing) return;
                          const next = [
                            ...editingRing.slice(0, i + 1),
                            [mx, my],
                            ...editingRing.slice(i + 1),
                          ];
                          setEditingRing(next);
                        }}
                      >
                        <circle
                          cx={mx}
                          cy={phPt - my}
                          r={scaleR(4)}
                          fill="rgba(34, 197, 94, 0.85)"
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                        >
                          <title>Vertex an dieser Edge einfügen</title>
                        </circle>
                        {/* Plus-Symbol (zwei Striche) */}
                        <line
                          x1={mx - scaleR(2)}
                          y1={phPt - my}
                          x2={mx + scaleR(2)}
                          y2={phPt - my}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                          style={{ pointerEvents: "none" }}
                        />
                        <line
                          x1={mx}
                          y1={phPt - my - scaleR(2)}
                          x2={mx}
                          y2={phPt - my + scaleR(2)}
                          stroke="white"
                          strokeWidth={1.5}
                          vectorEffect="non-scaling-stroke"
                          style={{ pointerEvents: "none" }}
                        />
                      </g>
                    );
                  })}
                  {/* Handles auf jedem Vertex (außer dem doppelten Schluss-Vertex).
                      Linksklick + Drag = verschieben. Rechtsklick = entfernen
                      (mit Mindest-3-Vertex-Schutz). */}
                  {editingRing.slice(0, -1).map(([x, y], i) => {
                    const isActive = draggingVertexIdx === i;
                    const canDelete = editingRing.length > 4; // 3 unique + 1 closure
                    return (
                      <circle
                        key={`vh-${i}`}
                        cx={x}
                        cy={phPt - y}
                        r={scaleR(isActive ? 7 : 5)}
                        fill={isActive ? "rgb(220, 38, 38)" : "rgb(29, 78, 216)"}
                        stroke="white"
                        strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        style={{
                          cursor: "grab",
                          pointerEvents: "all",
                        }}
                        onPointerDown={(ev) => {
                          // Rechtsklick (button=2) NICHT als Drag werten;
                          // wir lassen onContextMenu das Delete handhaben.
                          if (ev.button === 2) return;
                          ev.stopPropagation();
                          (ev.currentTarget as SVGCircleElement).setPointerCapture(
                            ev.pointerId
                          );
                          setDraggingVertexIdx(i);
                        }}
                        onPointerMove={(ev) => {
                          if (draggingVertexIdx !== i || !editingRing) return;
                          ev.stopPropagation();
                          const ptM = svgClickToPt(ev, phPt);
                          if (!ptM) return;
                          const next = editingRing.map((p, j) =>
                            j === i ? [ptM.x, ptM.y] : p
                          );
                          const lastIdx = next.length - 1;
                          if (i === 0) next[lastIdx] = [ptM.x, ptM.y];
                          else if (i === lastIdx) next[0] = [ptM.x, ptM.y];
                          setEditingRing(next);
                        }}
                        onPointerUp={(ev) => {
                          ev.stopPropagation();
                          (ev.currentTarget as SVGCircleElement).releasePointerCapture(
                            ev.pointerId
                          );
                          setDraggingVertexIdx(null);
                        }}
                        onContextMenu={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          if (!editingRing || !canDelete) return;
                          const unique = editingRing
                            .slice(0, -1)
                            .filter((_, j) => j !== i);
                          if (unique.length < 3) return;
                          // Closure: ersten Vertex am Ende anhängen
                          setEditingRing([...unique, [unique[0][0], unique[0][1]]]);
                        }}
                      >
                        <title>
                          {canDelete
                            ? "Drag = verschieben · Rechtsklick = entfernen"
                            : "Drag = verschieben (mind. 3 Vertices nötig)"}
                        </title>
                      </circle>
                    );
                  })}
                </g>
              )}

              {/* Split-Modus: User-gesetzte Punkte + Vorschau-Linie */}
              {editMode !== "view" &&
                splitPoints.map((p, i) => (
                  <circle
                    key={`split-pt-${i}`}
                    cx={p.x}
                    cy={phPt - p.y}
                    r={scaleR(5)}
                    fill="rgb(220, 38, 38)"
                    stroke="white"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    style={{ pointerEvents: "none" }}
                  />
                ))}
              {editMode !== "view" && splitPoints.length === 2 && (
                <line
                  x1={splitPoints[0].x}
                  y1={phPt - splitPoints[0].y}
                  x2={splitPoints[1].x}
                  y2={phPt - splitPoints[1].y}
                  stroke="rgb(220, 38, 38)"
                  strokeWidth={3}
                  strokeDasharray="6,4"
                  vectorEffect="non-scaling-stroke"
                  style={{ pointerEvents: "none" }}
                />
              )}

              {/* Split-Vorschau: zwei Polygone in Grün und Blau */}
              {editMode === "splitPreview" && splitPreview && (
                <g style={{ pointerEvents: "none" }}>
                  <polygon
                    points={splitPreview.ringA
                      .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                      .join(" ")}
                    fill="rgba(16, 185, 129, 0.45)"
                    stroke="rgb(5, 122, 85)"
                    strokeWidth={3}
                    vectorEffect="non-scaling-stroke"
                  />
                  <polygon
                    points={splitPreview.ringB
                      .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                      .join(" ")}
                    fill="rgba(59, 130, 246, 0.45)"
                    stroke="rgb(29, 78, 216)"
                    strokeWidth={3}
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              )}

              {/* Merge-Vorschau: vereinigtes Polygon in Violett */}
              {editMode === "mergePreview" && mergePreview && (
                <polygon
                  points={mergePreview.ring
                    .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                    .join(" ")}
                  fill="rgba(168, 85, 247, 0.45)"
                  stroke="rgb(126, 34, 206)"
                  strokeWidth={4}
                  vectorEffect="non-scaling-stroke"
                  style={{ pointerEvents: "none" }}
                />
              )}

              {/* Merge-Modus: das erste Polygon prominent rahmen */}
              {(editMode === "mergeTarget" || editMode === "mergePreview") &&
                mergeFirstId &&
                (() => {
                  const f = features.find((ff) => ff.properties.id === mergeFirstId);
                  if (!f) return null;
                  const ring = f.geometry.coordinates[0];
                  const points = ring
                    .map(([x, y]) => `${x.toFixed(2)},${(phPt - y).toFixed(2)}`)
                    .join(" ");
                  return (
                    <polygon
                      points={points}
                      fill="rgba(168, 85, 247, 0.20)"
                      stroke="rgb(126, 34, 206)"
                      strokeWidth={4}
                      strokeDasharray="6,4"
                      vectorEffect="non-scaling-stroke"
                      style={{ pointerEvents: "none" }}
                    />
                  );
                })()}
            </svg>
          </div>
        </div>
      </div>

      {/* Detail-Panel rechts */}
      <div
        onMouseDown={() => setIsResizingSidebar(true)}
        className={`w-1 cursor-col-resize flex-shrink-0 transition-colors ${
          isResizingSidebar
            ? "bg-cyan-500"
            : "bg-slate-200 hover:bg-cyan-400"
        }`}
        title="Sidebar-Breite anpassen (ziehen)"
      />
      <aside
        style={{ width: sidebarWidth }}
        className="border-l bg-white overflow-y-auto flex-shrink-0"
      >
        <div className="p-4 border-b">
          <Link
            to="/multiagent"
            className="text-xs text-brand-600 hover:underline"
          >
            ← Run-Liste
          </Link>
          <h2 className="text-base font-semibold mt-1 font-mono">{runId}</h2>
          {bundle.index.pdfPath && (
            <p className="text-xs text-slate-500 mt-1 break-all">
              {bundle.index.pdfPath.split(/[\\/]/).pop()}
            </p>
          )}
          {selectedFeature && (
            <p className="text-[11px] text-cyan-700 mt-1.5 bg-cyan-50 px-2 py-0.5 rounded inline-block">
              Aktiv: {selectedFeature.properties.id}
              {selectedFeature.properties.label
                ? ` · ${String(selectedFeature.properties.label)}`
                : ""}
            </p>
          )}
        </div>

        {/* Atelier-Inspector-Tabs (Sprint Atelier Phase 6 — 2026-05-02).
            Inspector-Header mit 5 Tabs: Übersicht / Auswahl / Mengen /
            Verlauf / Anzeige. Aktive Tab = Ember-Underline + Ember-Text. */}
        <div
          className="sticky top-0 z-10 flex"
          style={{
            background: "var(--bd-surface)",
            borderBottom: "1px solid var(--bd-line)",
          }}
        >
          {(
            [
              { id: "run" as SidebarTab, emoji: "🏠", label: "Übersicht", title: "Run-Übersicht (Maßstab, Etage/WE, Räume)" },
              { id: "auswahl" as SidebarTab, emoji: "🎯", label: "Auswahl", title: "Aktuelle Auswahl (Polygon-Details)" },
              { id: "mengen" as SidebarTab, emoji: "📊", label: "Mengen", title: "Mengen pro Gewerk" },
              { id: "verlauf" as SidebarTab, emoji: "🕒", label: "Verlauf", title: "Verlauf / Undo" },
              { id: "anzeige" as SidebarTab, emoji: "⚙️", label: "Anzeige", title: "Anzeige-Einstellungen" },
            ]
          ).map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                title={tab.title}
                style={{
                  flex: 1,
                  padding: "10px 4px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 3,
                  color: isActive ? "var(--bd-text)" : "var(--bd-text-3)",
                  borderBottom: isActive
                    ? "2px solid var(--bd-ember)"
                    : "2px solid transparent",
                  marginBottom: -1,
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isActive)
                    e.currentTarget.style.color = "var(--bd-text-2)";
                }}
                onMouseLeave={(e) => {
                  if (!isActive)
                    e.currentTarget.style.color = "var(--bd-text-3)";
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{tab.emoji}</span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: isActive ? 600 : 500,
                    letterSpacing: 0.3,
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Maßstabs-Kalibrierung */}
        {activeTab === "run" && bundle.dimensions && (
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">
                Maßstabs-Kalibrierung
              </h3>
              <button
                onClick={() => {
                  setScalePicked(null);
                  setScalePickStart(null);
                  setScaleSetterOpen(true);
                }}
                className="text-[11px] px-2 py-0.5 rounded border border-brand-200 bg-brand-50 hover:bg-brand-100 text-brand-700"
                title="Maßstab manuell setzen — überschreibt Auto-Kalibrierung"
              >
                📏 manuell
              </button>
            </div>
            {bundle.classification.pages[0]?.scale?.source === "user" && (
              <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2">
                User-Maßstab gepinned — we-stamps blockiert.
              </div>
            )}
            {bundle.dimensions.scaleCalibration ? (
              <dl className="text-xs space-y-1">
                <div className="flex justify-between">
                  <dt className="text-slate-500">Quelle:</dt>
                  <dd className="font-mono">
                    {bundle.dimensions.scaleCalibration.source}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">ratio:</dt>
                  <dd className="font-mono">
                    1:{bundle.dimensions.scaleCalibration.newRatio.toFixed(2)} (von 1:
                    {bundle.dimensions.scaleCalibration.previousRatio})
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Samples:</dt>
                  <dd className="font-mono">
                    {bundle.dimensions.scaleCalibration.calibratedFromLabels}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">Linear-Drift:</dt>
                  <dd className="font-mono">
                    {bundle.dimensions.scaleCalibration.medianDriftFactor.toFixed(4)}
                  </dd>
                </div>
              </dl>
            ) : (
              <p className="text-xs text-slate-500">
                Keine signifikante Drift — aktueller ratio: 1:
                {bundle.classification.pages[0]?.scale?.ratio?.toFixed(0) ?? 100}
                {bundle.classification.pages[0]?.scale?.source &&
                  bundle.classification.pages[0].scale.source !== "default" &&
                  ` (${bundle.classification.pages[0].scale.source})`}
                .
              </p>
            )}
          </div>
        )}

        {/* Sprint 0.21 — Etage & Wohneinheiten */}
        {activeTab === "run" && runMeta && (
          <div className="p-4 border-b">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              🏷️ Etage & Wohneinheiten
            </h3>
            <div className="mb-3">
              <label className="block text-[11px] text-slate-500 mb-1">
                Etage
              </label>
              <input
                type="text"
                list="multiagent-etage-suggestions"
                defaultValue={runMeta.etage ?? ""}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim();
                  if ((v || null) !== runMeta.etage) {
                    void patchMeta({ etage: v || null });
                  }
                }}
                placeholder="EG, OG, KG, …"
                className="w-full px-2 py-1 border rounded text-sm"
              />
              <datalist id="multiagent-etage-suggestions">
                <option value="KG" />
                <option value="EG" />
                <option value="OG" />
                <option value="OG2" />
                <option value="OG3" />
                <option value="DG" />
              </datalist>
              {!runMeta.etage && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                  ⚠ Etage noch nicht gesetzt — vor LV-Transfer setzen.
                </div>
              )}
            </div>

            <div className="border-t pt-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-700">
                  Wohneinheiten ({runMeta.wohneinheiten.length})
                </span>
                <button
                  onClick={addWohneinheit}
                  className="text-[11px] px-2 py-0.5 rounded bg-cyan-600 text-white hover:bg-cyan-700"
                  title="Neue Wohneinheit anlegen"
                >
                  + WE
                </button>
              </div>

              {runMeta.wohneinheiten.length === 0 && (
                <div className="text-[11px] text-slate-500 italic">
                  Keine Wohneinheiten — Räume bleiben ungruppiert.
                </div>
              )}

              {runMeta.wohneinheiten.map((we) => {
                const color = weColorFor(we.id);
                const isAssigning = weAssignTarget === we.id;
                return (
                  <div
                    key={we.id}
                    className={`mb-2 border rounded p-2 ${
                      isAssigning ? "bg-cyan-50 border-cyan-300" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                        style={{ background: color }}
                        title={`Farbe für ${we.id}`}
                      />
                      {weEditingId === we.id ? (
                        <input
                          autoFocus
                          defaultValue={we.label}
                          onBlur={(e) => {
                            const v = e.currentTarget.value.trim();
                            if (v && v !== we.label) renameWohneinheit(we.id, v);
                            setWeEditingId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                            if (e.key === "Escape") setWeEditingId(null);
                          }}
                          className="flex-1 px-1 py-0.5 border rounded text-sm"
                        />
                      ) : (
                        <span
                          className="flex-1 text-sm font-medium cursor-pointer hover:underline truncate"
                          onClick={() => setWeEditingId(we.id)}
                          title="Klick zum Umbenennen"
                        >
                          {we.label}{" "}
                          <span className="text-[11px] text-slate-500">
                            ({we.raumIds.length}
                            {(weAreaSum.get(we.id) ?? 0) > 0
                              ? ` · ${(weAreaSum.get(we.id) ?? 0).toFixed(2)} m²`
                              : ""}
                            )
                          </span>
                        </span>
                      )}
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={() =>
                            setWeAssignTarget(isAssigning ? null : we.id)
                          }
                          className={`text-[11px] px-1.5 py-0.5 rounded border ${
                            isAssigning
                              ? "bg-cyan-600 text-white border-cyan-700"
                              : "bg-white hover:bg-slate-100 border-slate-300"
                          }`}
                          title={
                            isAssigning
                              ? "Zuordnung beenden"
                              : "Räume zuordnen — dann auf Räume in der Liste klicken"
                          }
                        >
                          {isAssigning ? "✓ fertig" : "Räume"}
                        </button>
                        <button
                          onClick={() => deleteWohneinheit(we.id)}
                          className="text-[11px] px-1.5 py-0.5 rounded border border-slate-300 hover:bg-red-50 hover:border-red-300"
                          title="WE löschen"
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                    {we.raumIds.length > 0 && (
                      <div className="text-[11px] text-slate-600 mt-1.5 break-words">
                        {we.raumIds.join(", ")}
                      </div>
                    )}
                    {isAssigning && (
                      <div className="text-[11px] text-cyan-700 mt-1.5 italic">
                        Klick auf Räume in der Räume-Liste, um zuzuordnen /
                        zu entfernen. Ein Raum gehört nur einer WE.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {metaError && (
              <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {metaError}
              </div>
            )}

            {/* Sprint 0.22 — LV-Transfer-Button */}
            {lvEnabled && (
              <div className="mt-3 pt-3 border-t">
                <button
                  onClick={triggerLvTransfer}
                  disabled={lvTransferring || !runMeta.etage}
                  className="w-full text-sm px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
                  title={
                    !runMeta.etage
                      ? "Etage zuerst setzen"
                      : "Erzeugt einen neuen LV-Run aus den aktuellen Mengen"
                  }
                >
                  {lvTransferring
                    ? "📋 LV wird erzeugt …"
                    : "📋 LV erstellen"}
                </button>
                {lvTransferError && (
                  <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                    {lvTransferError}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Diagnostik */}
        {activeTab === "run" && bundle.dimensions && (
          <div className="p-4 border-b">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">
              Pro-Raum-NGF
            </h3>
            <dl className="text-xs space-y-1">
              <div className="flex justify-between">
                <dt className="text-slate-500">Stempel gefunden:</dt>
                <dd className="font-mono">
                  {bundle.dimensions.roomNgf.stamps.length}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">1:1-Matches:</dt>
                <dd className="font-mono">
                  {bundle.dimensions.roomNgf.oneToOneCount}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-red-600">Split-Candidates:</dt>
                <dd className="font-mono text-red-700">
                  {bundle.dimensions.roomNgf.splitCandidateCount}
                </dd>
              </div>
              {bundle.dimensions.roomNgf.splitCandidateCount > 0 && (
                <div className="pt-2">
                  <button
                    onClick={runBulkAutoSplit}
                    disabled={bulkSplitting}
                    className="w-full text-xs px-2 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded disabled:opacity-50"
                    title="Fuer jedes splitCandidate-Polygon mit 2 NGF-Stempeln den Auto-Vorschlag berechnen und automatisch bestaetigen"
                  >
                    {bulkSplitting
                      ? "Splitte sequenziell…"
                      : `🪄 Alle ${bundle.dimensions.roomNgf.splitCandidateCount} Splits automatisch ausfuehren`}
                  </button>
                  {bulkSplitReport && (
                    <div
                      className={`mt-1 text-xs text-center ${
                        bulkSplitReport.failed > 0
                          ? "text-amber-700"
                          : "text-emerald-700"
                      }`}
                    >
                      {bulkSplitReport.success} erfolgreich
                      {bulkSplitReport.failed > 0 &&
                        `, ${bulkSplitReport.failed} fehlgeschlagen`}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-slate-500">noStamp:</dt>
                <dd className="font-mono">
                  {bundle.dimensions.roomNgf.noStampCount}
                </dd>
              </div>
            </dl>
          </div>
        )}

        {/* Räume-Summary */}
        {activeTab === "run" && bundle.rooms && (
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-slate-700">
                Räume ({roomCounts.total})
              </h3>
              <div className="flex gap-1">
                <button
                  onClick={() => startDrawPolygon(false)}
                  disabled={editMode !== "view"}
                  className="text-[11px] px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Neuen Raum als n-Klick-Polygon zeichnen (Doppelklick / Klick auf 1. Punkt schließt)"
                >
                  📐 + Raum
                </button>
                <button
                  onClick={() => startDrawPolygon(true)}
                  disabled={editMode !== "view" || roomCounts.building > 0}
                  className="text-[11px] px-2 py-0.5 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={
                    roomCounts.building > 0
                      ? "HAUS-Polygon existiert bereits — bitte erst löschen"
                      : "HAUS-Außenkontur zeichnen (Container für Außenwände wie Klinker, Dämmung, Außenputz)"
                  }
                >
                  🏠 + HAUS
                </button>
                <button
                  onClick={async () => {
                    const keepHaus = window.confirm(
                      "Alle Polygone löschen?\n\n" +
                        "OK = HAUS behalten (komplett-manueller Modus innerhalb der Außenkontur)\n" +
                        "Abbrechen = Nichts tun\n\n" +
                        "(Über ↶ Verlauf rückgängig)"
                    );
                    if (!keepHaus) return;
                    const wirklichAlles = window.confirm(
                      "Auch das HAUS-Polygon löschen? OK = ALLES weg, leere Karte. Abbrechen = HAUS bleibt."
                    );
                    try {
                      await api.multiagentClearAllPolygons(
                        runId,
                        !wirklichAlles
                      );
                      const fresh = await loadBundle(runId);
                      setBundle(fresh);
                      setSelectedId(null);
                    } catch (err) {
                      window.alert(
                        "Bulk-Delete fehlgeschlagen: " +
                          (err instanceof Error ? err.message : String(err))
                      );
                    }
                  }}
                  disabled={editMode !== "view"}
                  className="text-[11px] px-2 py-0.5 rounded border border-red-300 bg-red-50 hover:bg-red-100 text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Alle Polygone löschen (Auto-Erkennungen + manuelle). Für komplett-manuellen Workflow auf einer leeren Karte."
                >
                  🧹 Alle löschen
                </button>
              </div>
            </div>
            <dl className="text-xs space-y-1">
              <div className="flex justify-between">
                <dt className="text-emerald-700">Innen (zählen):</dt>
                <dd className="font-mono">{roomCounts.inside}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Außen (gefiltert):</dt>
                <dd className="font-mono text-slate-500">
                  {roomCounts.outside}
                </dd>
              </div>
              {roomCounts.building > 0 && (() => {
                const haus = features.find((f) => f.properties.isBuilding);
                const hausId = haus?.properties.id;
                const isHausSelected = !!hausId && selectedId === hausId;
                return (
                  <div
                    onClick={() => {
                      if (!hausId) return;
                      setSelectedId(isHausSelected ? null : hausId);
                    }}
                    className={`flex justify-between cursor-pointer rounded px-1 -mx-1 transition-colors ${
                      isHausSelected
                        ? "bg-slate-200 ring-1 ring-slate-400"
                        : "hover:bg-slate-100"
                    }`}
                    title="Klick: HAUS-Polygon selektieren (z.B. um Außenwände zu zeichnen)"
                  >
                    <dt className="text-slate-700">🏠 Haus (Außenkontur):</dt>
                    <dd className="font-mono">{roomCounts.building}</dd>
                  </div>
                );
              })()}
              {roomCounts.gala > 0 && (() => {
                // Sprint #6 (2026-05-05) — GALA-Container-Polygon
                // (Außenanlage). Klickbar wie HAUS, hat eigenes Detail-Panel
                // mit GA-Code-Picker (Pflaster, Rasen, Mutterboden, ...).
                const gala = features.find((f) => f.properties.isGaLaArea);
                const galaId = gala?.properties.id;
                const isGalaSelected = !!galaId && selectedId === galaId;
                return (
                  <div
                    onClick={() => {
                      if (!galaId) return;
                      setSelectedId(isGalaSelected ? null : galaId);
                    }}
                    className={`flex justify-between cursor-pointer rounded px-1 -mx-1 transition-colors ${
                      isGalaSelected
                        ? "bg-emerald-100 ring-1 ring-emerald-500"
                        : "hover:bg-emerald-50"
                    }`}
                    title="Klick: GALA-Polygon selektieren (Außenanlage — Pflaster, Rasen, Mutterboden, Hecke, …)"
                  >
                    <dt className="text-emerald-800">🌿 Außenanlage (GaLa):</dt>
                    <dd className="font-mono">{roomCounts.gala}</dd>
                  </div>
                );
              })()}
              <div className="border-t my-1" />
              {bundle.rooms.summary.byDin &&
                Object.entries(bundle.rooms.summary.byDin).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <dt className="text-slate-500">{k}:</dt>
                    <dd className="font-mono">{v}</dd>
                  </div>
                ))}
              {bundle.rooms.summary.unclassified > 0 && (
                <div className="flex justify-between">
                  <dt className="text-amber-600">Unclassified:</dt>
                  <dd className="font-mono">
                    {bundle.rooms.summary.unclassified}
                  </dd>
                </div>
              )}
            </dl>
            {/* Status-Legende */}
            <div className="mt-3 pt-2 border-t text-[11px] space-y-1 text-slate-600">
              <div className="font-semibold text-slate-700 mb-0.5">
                Polygon-Status
              </div>
              <div
                className="flex items-center gap-2"
                title="Raum hat genau einen NGF-Stempel zugeordnet — Mengen sind verlässlich"
              >
                <span className="text-emerald-600">✓</span>
                <span>Stempel zugeordnet</span>
              </div>
              <div
                className="flex items-center gap-2"
                title="Polygon enthält 2 oder mehr NGF-Stempel — Polygon-Builder hat zwei Räume verschmolzen. Aufteilen empfohlen."
              >
                <span className="text-yellow-700">⚠</span>
                <span>2 Räume in einem · aufteilen</span>
              </div>
              <div
                className="flex items-center gap-2"
                title="Innenraum ohne erkannten NGF-Stempel — bitte prüfen oder Polygon korrigieren"
              >
                <span className="text-orange-600">✏</span>
                <span>kein Stempel · prüfen</span>
              </div>
              <div
                className="flex items-center gap-2"
                title="Polygon liegt außerhalb des Gebäudes — wird aus Mengen ausgeschlossen"
              >
                <span className="text-slate-500">🚫</span>
                <span>außen · nicht in Mengen</span>
              </div>
              <div
                className="flex items-center gap-2"
                title="Haus-Container für Außenwand-Materialien wie Klinker, Fassadendämmung, Außenputz"
              >
                <span className="text-slate-700">🏠</span>
                <span>Haus-Hülle</span>
              </div>
            </div>
          </div>
        )}

        {/* Selected Room */}
        {activeTab === "auswahl" && !selectedFeature && (
          <div className="p-6 text-center text-sm text-slate-500">
            <div className="text-3xl mb-2">🎯</div>
            <p>Kein Polygon ausgewählt.</p>
            <p className="text-xs text-slate-400 mt-1">
              Klick auf einen Eintrag links oder ein Polygon im Plan.
            </p>
          </div>
        )}
        {activeTab === "auswahl" && selectedFeature && (
          <div
            style={{
              padding: "14px 16px 12px",
              borderBottom: "1px solid var(--bd-line)",
              background: "var(--bd-surface)",
            }}
          >
            {/* Atelier-Hero: ID-Pill + DIN + Action-Icons rechts */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--bd-mono)",
                  fontSize: 11,
                  color: "var(--bd-ember-strong)",
                  fontWeight: 600,
                  padding: "2px 7px",
                  background:
                    "color-mix(in oklab, var(--bd-ember) 12%, transparent)",
                  borderRadius: 4,
                }}
              >
                {selectedFeature.properties.id}
              </span>
              {selectedClassifiedRoom?.din277?.sub && (
                <span
                  style={{
                    fontSize: 10,
                    color: "var(--bd-text-3)",
                    fontFamily: "var(--bd-mono)",
                  }}
                >
                  · {selectedClassifiedRoom.din277.sub}
                </span>
              )}
              {editMode === "view" && (
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <button
                    onClick={() => startSplit(selectedFeature.properties.id)}
                    style={atelierIconBtn("amber")}
                    title="Polygon mit einer Trennlinie in zwei teilen"
                  >
                    ✂
                  </button>
                  <button
                    onClick={() => startMerge(selectedFeature.properties.id)}
                    style={atelierIconBtn("violet")}
                    title="Mit einem benachbarten Polygon zusammenfuehren"
                  >
                    ⊕
                  </button>
                  <button
                    onClick={() =>
                      startVertexEdit(selectedFeature.properties.id)
                    }
                    style={atelierIconBtn("blue")}
                    title="Vertices an die echte Wand-Position ziehen"
                  >
                    ✥
                  </button>
                  <button
                    onClick={() => deletePolygon(selectedFeature.properties.id)}
                    style={atelierIconBtn("red")}
                    title="Polygon komplett löschen (rückgängig über ↶ Verlauf)"
                  >
                    🗑
                  </button>
                </div>
              )}
            </div>
            {/* Atelier-Hero: Raum-Name + große Mono-Fläche */}
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "var(--bd-text)",
                letterSpacing: -0.2,
                marginBottom: 4,
              }}
            >
              {selectedClassifiedRoom?.label ?? "—"}
              {runMeta?.etage && (
                <span
                  style={{
                    color: "var(--bd-text-3)",
                    fontWeight: 400,
                    marginLeft: 6,
                  }}
                >
                  · {runMeta.etage}
                </span>
              )}
            </div>
            {(() => {
              // Sprint AA.2 (2026-05-05) — Stempel-siegt-Header: bevorzuge
              // valueSet.effective.A (kommt aus Stempel-Override wenn gesetzt;
              // sonst Geometrie). Bei Drift `📋` als Pille neben dem Wert.
              const props = selectedFeature.properties as typeof selectedFeature.properties & {
                valueSet?: {
                  effective?: { L?: number; A?: number };
                  effectiveSource?: "stempel" | "geometrie";
                  drift?: { aDeltaPct?: number; lDeltaPct?: number };
                };
              };
              const effectiveA =
                props.valueSet?.effective?.A ?? selectedFeature.properties.areaM2;
              const effectiveL =
                props.valueSet?.effective?.L ?? selectedFeature.properties.perimeterM;
              const isStempel = props.valueSet?.effectiveSource === "stempel";
              const drift = props.valueSet?.drift?.aDeltaPct;
              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 6,
                      fontFamily: "var(--bd-mono)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 28,
                        fontWeight: 600,
                        color: "var(--bd-text)",
                        letterSpacing: -0.5,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {effectiveA.toFixed(2).replace(".", ",")}
                    </span>
                    <span style={{ fontSize: 13, color: "var(--bd-text-3)" }}>
                      m²
                    </span>
                    {isStempel && (
                      <span
                        style={{
                          fontSize: 11,
                          padding: "1px 6px",
                          borderRadius: 999,
                          background: "var(--bd-stempel-bg, #f0f9ff)",
                          color: "var(--bd-stempel-text, #075985)",
                          marginLeft: 6,
                        }}
                        title="Wert aus Plan-Stempel (siegt bei Drift)"
                      >
                        📋 Stempel
                      </span>
                    )}
                    {drift !== undefined && Math.abs(drift) > 1 && (
                      <span
                        style={{
                          fontSize: 10,
                          color: "var(--bd-text-3)",
                          marginLeft: 4,
                        }}
                        title={`Drift Stempel↔Geometrie: ${drift.toFixed(1)} %`}
                      >
                        ±{Math.abs(drift).toFixed(1)} %
                      </span>
                    )}
                  </div>
                  {effectiveL > 0 && (
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 11,
                        color: "var(--bd-text-3)",
                        fontFamily: "var(--bd-mono)",
                      }}
                    >
                      L {effectiveL.toFixed(2)} m · A {effectiveA.toFixed(2)} m²
                    </div>
                  )}
                </>
              );
            })()}

            {/* Sprint P (2026-05-03) — Cross-Plan-Hint: lichte Höhe aus
             *  Schnitt-Run derselben Mappe + Σ Verblenderfläche aus
             *  Ansicht-Runs. Zeigt nur wenn Daten vorhanden. */}
            {(() => {
              if (!crossPlanData) return null;
              const etage = runMeta?.etage ?? null;
              const rhEntry = etage
                ? crossPlanData.etagenHoehen[etage]
                : undefined;
              const sigmaVerblender = crossPlanData.sigmaVerblenderM2;
              if (!rhEntry && sigmaVerblender <= 0) return null;
              return (
                <div
                  style={{
                    marginTop: 6,
                    marginBottom: 8,
                    padding: "6px 10px",
                    background: "color-mix(in oklab, var(--bd-ember) 8%, transparent)",
                    border: "1px solid color-mix(in oklab, var(--bd-ember) 30%, var(--bd-line))",
                    borderRadius: 6,
                    fontSize: 11,
                    color: "var(--bd-text-2)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                  title="Aggregierte Daten aus den anderen Runs der Mappe (Schnitte + Ansichten)"
                >
                  {rhEntry && (
                    <div>
                      📐 Lichte Höhe aus Schnitt:{" "}
                      <span
                        style={{
                          fontFamily: "var(--bd-mono)",
                          fontWeight: 600,
                          color: "var(--bd-text)",
                        }}
                      >
                        {rhEntry.lichteHoeheM.toFixed(2).replace(".", ",")} m
                      </span>{" "}
                      <span style={{ color: "var(--bd-text-3)", fontSize: 10 }}>
                        · {rhEntry.sourceRunId.slice(-6)}
                      </span>
                    </div>
                  )}
                  {sigmaVerblender > 0 && (
                    <div>
                      🧱 Σ Verblender Mappe:{" "}
                      <span
                        style={{
                          fontFamily: "var(--bd-mono)",
                          fontWeight: 600,
                          color: "var(--bd-text)",
                        }}
                      >
                        {sigmaVerblender.toFixed(2).replace(".", ",")} m²
                      </span>{" "}
                      <span style={{ color: "var(--bd-text-3)", fontSize: 10 }}>
                        · {crossPlanData.ansichtRunIds.length} Ansicht
                        {crossPlanData.ansichtRunIds.length === 1 ? "" : "en"}
                      </span>
                    </div>
                  )}
                  {/* Sprint P-Iteration (2026-05-04) — Decken-/Estrich-/Boden-
                   *  platten-Aufbau pro Mappe; Quelle = Schnitt-Run. Mehr-Werte
                   *  in einer Zeile, damit der Banner kompakt bleibt. */}
                  {(crossPlanData.deckenStaerkeMm != null ||
                    crossPlanData.estrichStaerkeMm != null ||
                    crossPlanData.bodenplatteStaerkeMm != null) && (
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      {crossPlanData.deckenStaerkeMm != null && (
                        <span>
                          🧊 Decke{" "}
                          <span
                            style={{
                              fontFamily: "var(--bd-mono)",
                              fontWeight: 600,
                              color: "var(--bd-text)",
                            }}
                          >
                            {crossPlanData.deckenStaerkeMm} mm
                          </span>
                        </span>
                      )}
                      {crossPlanData.estrichStaerkeMm != null && (
                        <span>
                          ◾ Estrich{" "}
                          <span
                            style={{
                              fontFamily: "var(--bd-mono)",
                              fontWeight: 600,
                              color: "var(--bd-text)",
                            }}
                          >
                            {crossPlanData.estrichStaerkeMm} mm
                          </span>
                        </span>
                      )}
                      {crossPlanData.bodenplatteStaerkeMm != null && (
                        <span>
                          ⬛ Bodenplatte{" "}
                          <span
                            style={{
                              fontFamily: "var(--bd-mono)",
                              fontWeight: 600,
                              color: "var(--bd-text)",
                            }}
                          >
                            {crossPlanData.bodenplatteStaerkeMm} mm
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Sprint A.5/A.6 — valueSet-Pille mit Drei-Quellen-Hierarchie. */}
            {selectedFeature.properties.valueSet && (() => {
              const vs = selectedFeature.properties.valueSet!;
              const isStempel = vs.effectiveSource === "stempel";
              const driftL = vs.drift?.lDeltaPct;
              const driftA = vs.drift?.aDeltaPct;
              const hasDrift =
                (typeof driftL === "number" && Math.abs(driftL) > 1) ||
                (typeof driftA === "number" && Math.abs(driftA) > 1);
              const symbol = isStempel ? "📋" : "📐";
              const sourceLabel = isStempel ? "Stempel" : "Geometrie";
              const tooltipParts: string[] = [
                `Quelle: ${sourceLabel}`,
                `Geometrie: L ${vs.geometrie.L.toFixed(2)} m · A ${vs.geometrie.A.toFixed(2)} m²`,
              ];
              if (vs.stempel) {
                const sl = typeof vs.stempel.L === "number" ? `${vs.stempel.L.toFixed(2)} m` : "—";
                const sa = typeof vs.stempel.A === "number" ? `${vs.stempel.A.toFixed(2)} m²` : "—";
                tooltipParts.push(`Stempel: L ${sl} · A ${sa}`);
              }
              if (vs.drift) {
                tooltipParts.push(
                  `Drift L ${driftL?.toFixed(1) ?? "—"}% · A ${driftA?.toFixed(1) ?? "—"}%`
                );
              }
              return (
                <div
                  className={`text-[11px] mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded border ${
                    hasDrift
                      ? "bg-amber-50 border-amber-300 text-amber-900"
                      : isStempel
                        ? "bg-blue-50 border-blue-200 text-blue-900"
                        : "bg-slate-50 border-slate-200 text-slate-700"
                  }`}
                  title={tooltipParts.join("\n")}
                >
                  <span className="font-mono">{symbol}</span>
                  <span className="font-mono">
                    L {vs.effective.L.toFixed(2)} m · A {vs.effective.A.toFixed(2)} m²
                    {vs.effective.rh && (
                      <>
                        {" "}· RH {vs.effective.rh.min.toFixed(2)}
                        {vs.effective.rh.max
                          ? `–${vs.effective.rh.max.toFixed(2)}`
                          : ""}
                        {" m"}
                      </>
                    )}
                  </span>
                  {hasDrift && (
                    <span className="text-[10px] font-semibold">
                      ⚠ Drift{" "}
                      {typeof driftL === "number" && Math.abs(driftL) > 1
                        ? `L ${driftL > 0 ? "+" : ""}${driftL.toFixed(1)}%`
                        : ""}
                      {typeof driftL === "number" &&
                      Math.abs(driftL) > 1 &&
                      typeof driftA === "number" &&
                      Math.abs(driftA) > 1
                        ? " · "
                        : ""}
                      {typeof driftA === "number" && Math.abs(driftA) > 1
                        ? `A ${driftA > 0 ? "+" : ""}${driftA.toFixed(1)}%`
                        : ""}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Sprint Inline-OCR (2026-05-06) — perRoomInlineText pro Polygon.
             *  Premium-Feature für Bestands-/Sanierungspläne mit handgeschriebenen
             *  Maßen (RH/BRH/DFF/VK Decke/Vorwand/Stützen). Wird via Header-Toggle
             *  „🔍 Inline-Notizen lesen" aktiviert; Idempotent. */}
            {(() => {
              const polygonId = selectedFeature.properties.id;
              const inline: PerRoomInlineTextLite | undefined =
                bundle?.annotations?.perRoomInlineText?.[polygonId];
              if (!inline) return null;
              const p = inline.parsed;
              const hasParsed =
                (p.brh && p.brh.length > 0) ||
                p.rhInLichten ||
                (p.dff && p.dff.length > 0) ||
                p.vkDeckeH != null ||
                p.vorwandH != null ||
                (p.stuetzen && p.stuetzen.length > 0) ||
                (p.sonstiges && p.sonstiges.length > 0);
              const hasRaw = inline.rawText && inline.rawText.trim().length > 0;
              if (!hasRaw && !hasParsed) return null;
              return (
                <div
                  style={{
                    marginBottom: 10,
                    padding: "8px 10px",
                    border: "1px solid var(--bd-line)",
                    borderRadius: 6,
                    background:
                      "color-mix(in oklab, var(--bd-ember) 4%, transparent)",
                  }}
                  title="Aus dem Plan via Vision-API gelesene Inline-Notizen rund um das Polygon."
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--bd-text-3)",
                      fontFamily: "var(--bd-mono)",
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                      marginBottom: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>🔍 Inline-Notizen</span>
                  </div>
                  {hasParsed && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: hasRaw ? 6 : 0,
                      }}
                    >
                      {p.rhInLichten && (
                        <span style={inlineNotePill}>
                          RH{" "}
                          {p.rhInLichten.min.toFixed(2)}
                          {p.rhInLichten.max
                            ? `–${p.rhInLichten.max.toFixed(2)}`
                            : ""}{" "}
                          m
                        </span>
                      )}
                      {p.brh && p.brh.length > 0 && (
                        <span style={inlineNotePill}>
                          BRH {p.brh.map((v) => `${v.toFixed(2)} m`).join(" / ")}
                        </span>
                      )}
                      {p.vkDeckeH != null && (
                        <span style={inlineNotePill}>
                          VK Decke {p.vkDeckeH.toFixed(2)} m
                        </span>
                      )}
                      {p.vorwandH != null && (
                        <span style={inlineNotePill}>
                          Vorwand {p.vorwandH.toFixed(2)} m
                        </span>
                      )}
                      {p.dff && p.dff.length > 0 && (
                        <span style={inlineNotePill}>
                          DFF {p.dff.join(" / ")}
                        </span>
                      )}
                      {p.stuetzen && p.stuetzen.length > 0 && (
                        <span style={inlineNotePill}>
                          Stützen {p.stuetzen.join(" / ")}
                        </span>
                      )}
                      {p.sonstiges?.map((s, i) => (
                        <span key={i} style={inlineNotePill}>
                          {s.key}: {s.value}
                        </span>
                      ))}
                    </div>
                  )}
                  {hasRaw && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--bd-text-2)",
                        fontFamily: "var(--bd-mono)",
                        whiteSpace: "pre-wrap",
                        maxHeight: 80,
                        overflow: "auto",
                        lineHeight: 1.35,
                      }}
                    >
                      {inline.rawText}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Sprint G2.1 (2026-05-02) — Pro-Raum-Übersicht: Bauteil-Sektionen
             *  + LV-Position-Liste aus aggregateRoomQuantities.
             *  Sprint G (2026-05-03): isOutdoor durchreichen, damit der Boden-
             *  Picker bei Außenbereichen GA-Codes zeigt. */}
            <ProRaumUebersichtSection
              polygon={selectedFeature}
              runId={runId}
              raumhoeheM={raumhoeheM}
              verschnittPct={verschnittBoden / 100}
              isOutdoor={
                polyStatus.get(String(selectedFeature.properties.id)) === "outdoor" ||
                polyStatus.get(String(selectedFeature.properties.id)) === "gala"
              }
              onChange={() => loadBundle(runId).then(setBundle)}
            />

            {/* Sprint D (2026-05-03) — Label + DIN 277 manuell editierbar.
             *  Auto-Vorschlag aus room-classifier-Agent bleibt; User kann
             *  überschreiben. Gewerke kommen aus den Bauteil-Codes.
             *  Sprint UX (2026-05-06): einklappbar, default zu. */}
            <CollapsibleSection
              icon="🏷"
              title="Klassifikation"
              hint="Label + DIN 277 + abgeleitete Gewerke"
            >
              <RoomClassificationEditor
                key={String(selectedFeature.properties.id)}
                runId={runId}
                feat={selectedFeature}
                classifiedRoom={
                  selectedClassifiedRoom
                    ? {
                        label: selectedClassifiedRoom.label,
                        din277: selectedClassifiedRoom.din277
                          ? {
                              main: selectedClassifiedRoom.din277.main as Din277Main,
                              sub: selectedClassifiedRoom.din277.sub,
                              name: selectedClassifiedRoom.din277.name,
                            }
                          : undefined,
                      }
                    : null
                }
                derivedGewerke={deriveGewerkeFromCodes(selectedFeature)}
                onChange={() => loadBundle(runId).then(setBundle)}
              />
            </CollapsibleSection>

            {(() => {
              // Sprint 0.19 — Umfang aus NGF-Stempel (RU-Wert) wenn matched.
              // Polygon-Umfang ist Floodfill-Geometrie und enthält oft Tuer-
              // Bezier-Zickzack → ist systematisch zu hoch. Der vom Architekt
              // gestempelte RU-Wert ist die echte Quelle.
              // Sprint UX (2026-05-06): einklappbar, default zu.
              const polyPerim = selectedFeature.properties.perimeterM;
              const stampRu =
                selectedNgfMatch?.status === "matched" &&
                selectedNgfMatch.ngfStamps[0]?.ruM != null
                  ? selectedNgfMatch.ngfStamps[0].ruM
                  : null;
              const umfang = stampRu ?? polyPerim;
              const showBoth = stampRu !== null && Math.abs(polyPerim - stampRu) > 0.05;
              const wallsN = selectedFeature.properties.walls?.length ?? 0;
              const opensN = selectedFeature.properties.openings.length;
              return (
                <CollapsibleSection
                  icon="📐"
                  title={`Geometrie · U ${umfang.toFixed(2)} m · ${wallsN} W · ${opensN} Ö`}
                  hint="Umfang + Wand-/Öffnungs-Anzahl"
                >
                  <dl className="text-xs space-y-1 pt-2">
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Umfang:</dt>
                      <dd className="font-mono">
                        {umfang.toFixed(2)} m
                        {stampRu !== null && (
                          <span className="text-emerald-600 ml-1 text-[10px]">
                            aus RU-Stempel
                          </span>
                        )}
                      </dd>
                    </div>
                    {showBoth && (
                      <div className="flex justify-between">
                        <dt className="text-slate-400 text-[11px]">
                          Polygon-Umfang:
                        </dt>
                        <dd className="font-mono text-slate-400 text-[11px]">
                          {polyPerim.toFixed(2)} m
                        </dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Wände:</dt>
                      <dd className="font-mono">{wallsN}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Öffnungen:</dt>
                      <dd className="font-mono">{opensN}</dd>
                    </div>
                  </dl>
                </CollapsibleSection>
              );
            })()}

            {/* NGF-Match-Status — Sprint UX (2026-05-06) einklappbar, default zu.
             *  Bei Drift/SplitCandidate sehen Frank's Augen schon im Hero
             *  (📋-Pille mit ⚠ Drift) — Detail nur on-demand. */}
            {selectedNgfMatch && (() => {
              const headerIcon =
                selectedNgfMatch.status === "matched"
                  ? "✓"
                  : selectedNgfMatch.status === "splitCandidate"
                    ? "⚠"
                    : "·";
              const headerTitle =
                selectedNgfMatch.status === "matched"
                  ? "Stempel passt"
                  : selectedNgfMatch.status === "splitCandidate"
                    ? "2 Räume in einem"
                    : `Status: ${selectedNgfMatch.status}`;
              const isAlert = selectedNgfMatch.status === "splitCandidate";
              return (
                <CollapsibleSection
                  icon={headerIcon}
                  title={`Stempel · ${headerTitle}`}
                  defaultOpen={isAlert}
                  hint="NGF-Match-Status + Drift + Pro-Raum-Stempel-Liste"
                  style={
                    isAlert
                      ? { borderColor: "var(--bd-rose, #be123c)" }
                      : undefined
                  }
                >
                  <div
                    className={`text-xs ${
                      selectedNgfMatch.status === "splitCandidate"
                        ? "text-red-800"
                        : selectedNgfMatch.status === "matched"
                          ? "text-emerald-800"
                          : "text-slate-600"
                    } pt-2`}
                  >
                    {selectedNgfMatch.status === "splitCandidate" && (
                      <div className="mb-1">
                        {selectedNgfMatch.ngfStamps.length} Stempel im Polygon —
                        der Polygon-Builder hat zwei Räume zu einem verschmolzen.
                        Aufteilen empfohlen.
                      </div>
                    )}
                    {selectedNgfMatch.status === "matched" &&
                      selectedNgfMatch.linearDrift !== null && (
                        <div
                          className="mb-1 text-[10px] text-slate-500"
                          title="Maßstabs-Genauigkeit: 1.0 wäre exakt. Zwischen 0.95 und 1.05 ist normal."
                        >
                          Maßstab-Genauigkeit:{" "}
                          {selectedNgfMatch.linearDrift.toFixed(2)}
                        </div>
                      )}
                    {selectedNgfMatch.ngfStamps.length > 0 && (
                      <ul className="space-y-0.5 mt-1">
                        {selectedNgfMatch.ngfStamps.map((s, i) => (
                          <li key={i} className="font-mono">
                            · {s.roomName}: {s.ngfM2} m²
                            {s.ruM != null && (
                              <span> · RU {s.ruM.toFixed(2)} m</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </CollapsibleSection>
              );
            })()}

            {/* Türen — Auto-Erkennung (doorWalls) + User-openings[type="door"] */}
            {(() => {
              const polygonId = selectedFeature.properties.id;
              const allAutoDoors = doorsByPolyId.get(polygonId) ?? [];
              const suppressedAuto =
                ((selectedFeature.properties as {
                  suppressedAutoDoors?: number[];
                }).suppressedAutoDoors) ?? [];
              const autoDoors = allAutoDoors.filter(
                (d) =>
                  d._origIdx === undefined ||
                  !suppressedAuto.includes(d._origIdx)
              );
              const userOpenings = (selectedFeature.properties.openings ?? []) as Array<{
                type: "door" | "window";
                widthM: number;
                heightM?: number;
                bruestungM?: number;
                addedBy?: "auto" | "user";
                label?: string;
                id?: string;
              }>;
              const userDoors = userOpenings
                .map((o, i) => ({ ...o, _idx: i }))
                .filter((o) => o.type === "door");
              const total = autoDoors.length + userDoors.length;
              return (
                <CollapsibleSection
                  icon="🚪"
                  title="Türen"
                  counter={total}
                  defaultOpen
                  hint="Auto-erkannte + manuell hinzugefügte Türen"
                  rightSlot={
                    <>
                      <button
                        onClick={() => startDrawOpening(polygonId, "door")}
                        className="text-[10px] px-1.5 py-0.5 bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-300 rounded"
                        title="2 Punkte auf der Wand klicken — Tür anlegen"
                      >
                        ✏ + Tür
                      </button>
                      <button
                        onClick={() =>
                          openOpeningEditorForCreate(polygonId, "door")
                        }
                        className="text-[10px] px-1.5 py-0.5 bg-white hover:bg-slate-50 text-amber-800 border border-amber-300 rounded"
                        title="Tür ohne Plan-Position anlegen (nur Maße)"
                      >
                        ⌨ + Tür
                      </button>
                    </>
                  }
                >
                  {total === 0 && (
                    <p className="text-[11px] text-slate-400 italic pt-2">
                      Keine Türen — Klick auf + Tür oben.
                    </p>
                  )}
                  <ul className="text-xs space-y-0.5 pt-2">
                    {autoDoors.map((d) => {
                      const origIdx = d._origIdx ?? 0;
                      const key = `auto-door-${origIdx}`;
                      const isActive =
                        highlightedOpening?.polyId === polygonId &&
                        highlightedOpening.key === key;
                      return (
                      <li
                        key={`auto-${origIdx}`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button")) return;
                          setHighlightedOpening(
                            isActive
                              ? null
                              : {
                                  polyId: polygonId,
                                  key,
                                  type: "door",
                                  source: "auto",
                                  idx: origIdx,
                                }
                          );
                        }}
                        className={`flex justify-between gap-2 font-mono cursor-pointer rounded px-1 ${
                          isActive
                            ? "bg-amber-200 ring-1 ring-amber-500"
                            : "hover:bg-slate-100"
                        }`}
                        title="Klick: im Plan markieren"
                      >
                        <span className="flex items-center gap-1">
                          <span className="text-[9px] uppercase text-slate-400">
                            auto
                          </span>
                          Tür {origIdx + 1}
                          {d.sharedWith.length > 0 && (
                            <span
                              className="text-slate-400 text-[10px]"
                              title={`Tür liegt zwischen diesem Raum und ${d.sharedWith.join(", ")}. Mengen-Zählung: nur hier (kleinerer Raum gewinnt).`}
                            >
                              ↔ {d.sharedWith.join(",")}
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2">
                          <span>{d.widthCm.toFixed(0)} × ~200 cm</span>
                          <button
                            onClick={async () => {
                              // F7 — AUTO-Tür übernehmen: als USER-opening
                              // anlegen mit AUTO-Daten + suppress AUTO. Danach
                              // OpeningEditor öffnen mit dem neuen User-Opening.
                              try {
                                const widthM = d.widthCm / 100;
                                const heightM = 2.0;
                                const newOp = await api.multiagentAddOpening(
                                  runId,
                                  polygonId,
                                  {
                                    type: "door",
                                    widthM,
                                    heightM,
                                    startPt: d.from,
                                    endPt: d.to,
                                    label: `Tür ${origIdx + 1}`,
                                  }
                                );
                                await api.multiagentSuppressAutoDoor(
                                  runId,
                                  polygonId,
                                  origIdx
                                );
                                const fresh = await loadBundle(runId);
                                setBundle(fresh);
                                openOpeningEditorForEdit(
                                  polygonId,
                                  newOp.openingIdx,
                                  {
                                    type: "door",
                                    widthM,
                                    heightM,
                                  }
                                );
                              } catch (err) {
                                window.alert(
                                  "Übernehmen fehlgeschlagen: " +
                                    (err instanceof Error ? err.message : String(err))
                                );
                              }
                            }}
                            className="text-[10px] text-blue-700 hover:text-blue-900"
                            title="In User-Tür umwandeln und editieren (Maße korrigieren)"
                          >
                            ✎
                          </button>
                          <button
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `AUTO-Tür ${origIdx + 1} ausblenden? (kann über Verlauf rückgängig gemacht werden)`
                                )
                              )
                                return;
                              try {
                                await api.multiagentSuppressAutoDoor(
                                  runId,
                                  polygonId,
                                  origIdx
                                );
                                const fresh = await loadBundle(runId);
                                setBundle(fresh);
                              } catch (err) {
                                window.alert(
                                  "Löschen fehlgeschlagen: " +
                                    (err instanceof Error ? err.message : String(err))
                                );
                              }
                            }}
                            className="text-[10px] text-red-600 hover:text-red-800"
                            title="AUTO-Tür ausblenden (z.B. falsche Erkennung)"
                          >
                            🗑
                          </button>
                        </span>
                      </li>
                      );
                    })}
                    {userDoors.map((d) => {
                      const key = `user-${d._idx}`;
                      const isActive =
                        highlightedOpening?.polyId === polygonId &&
                        highlightedOpening.key === key;
                      return (
                      <li
                        key={`user-${d._idx}`}
                        onClick={(e) => {
                          // Nicht highlighten wenn Edit/Delete-Button geklickt
                          if ((e.target as HTMLElement).closest("button")) return;
                          setHighlightedOpening(
                            isActive
                              ? null
                              : {
                                  polyId: polygonId,
                                  key,
                                  type: "door",
                                  source: "user",
                                  idx: d._idx,
                                }
                          );
                        }}
                        className={`flex justify-between gap-2 font-mono p-1 border rounded cursor-pointer ${
                          isActive
                            ? "bg-amber-200 border-amber-500 ring-1 ring-amber-500"
                            : "bg-amber-50 border-amber-200 hover:bg-amber-100"
                        }`}
                        title="Klick: im Plan markieren"
                      >
                        <span className="flex items-center gap-1">
                          <span className="text-[9px] uppercase text-amber-700 font-semibold">
                            user
                          </span>
                          {d.label || `Tür ${d._idx + 1}`}
                        </span>
                        <span className="flex items-center gap-2">
                          <span>
                            {(d.widthM * 100).toFixed(0)}
                            {d.heightM ? ` × ${(d.heightM * 100).toFixed(0)}` : ""} cm
                          </span>
                          <button
                            onClick={() =>
                              openOpeningEditorForEdit(polygonId, d._idx, d)
                            }
                            className="text-[10px] text-blue-700 hover:text-blue-900"
                            title="Editieren"
                          >
                            ✎
                          </button>
                          <button
                            onClick={() => deleteOpening(polygonId, d._idx)}
                            className="text-[10px] text-red-600 hover:text-red-800"
                            title="Löschen"
                          >
                            🗑
                          </button>
                        </span>
                      </li>
                      );
                    })}
                  </ul>
                </CollapsibleSection>
              );
            })()}

            {/* Fenster — Auto (OCR) + User-openings[type="window"] */}
            {(() => {
              const polygonId = selectedFeature.properties.id;
              const allAutoWindows = windowsByPolyId.get(polygonId) ?? [];
              const suppressedAutoW =
                ((selectedFeature.properties as {
                  suppressedAutoWindows?: number[];
                }).suppressedAutoWindows) ?? [];
              const autoWindows = allAutoWindows
                .map((w, i) => ({ ...w, _origIdx: i }))
                .filter((w) => !suppressedAutoW.includes(w._origIdx));
              const userOpenings = (selectedFeature.properties.openings ?? []) as Array<{
                type: "door" | "window";
                widthM: number;
                heightM?: number;
                bruestungM?: number;
                addedBy?: "auto" | "user";
                label?: string;
                id?: string;
              }>;
              const userWindows = userOpenings
                .map((o, i) => ({ ...o, _idx: i }))
                .filter((o) => o.type === "window");
              const total = autoWindows.length + userWindows.length;
              return (
                <CollapsibleSection
                  icon="🪟"
                  title="Fenster"
                  counter={total}
                  defaultOpen
                  hint="Auto-erkannte + manuell hinzugefügte Fenster"
                  rightSlot={
                    <>
                      <button
                        onClick={() => startDrawOpening(polygonId, "window")}
                        className="text-[10px] px-1.5 py-0.5 bg-cyan-100 hover:bg-cyan-200 text-cyan-800 border border-cyan-300 rounded"
                        title="2 Punkte auf der Wand klicken — Fenster anlegen"
                      >
                        ✏ + Fenster
                      </button>
                      <button
                        onClick={() =>
                          openOpeningEditorForCreate(polygonId, "window")
                        }
                        className="text-[10px] px-1.5 py-0.5 bg-white hover:bg-slate-50 text-cyan-800 border border-cyan-300 rounded"
                        title="Fenster ohne Plan-Position anlegen (nur Maße)"
                      >
                        ⌨ + Fenster
                      </button>
                    </>
                  }
                >
                  {total === 0 && (
                    <p className="text-[11px] text-slate-400 italic pt-2">
                      Keine Fenster — Klick auf + Fenster oben.
                    </p>
                  )}
                  <ul className="text-xs space-y-0.5 pt-2">
                    {autoWindows.map((w) => {
                      const origIdx = w._origIdx;
                      const key = `auto-window-${origIdx}`;
                      const isActive =
                        highlightedOpening?.polyId === polygonId &&
                        highlightedOpening.key === key;
                      return (
                      <li
                        key={`auto-${origIdx}`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button")) return;
                          setHighlightedOpening(
                            isActive
                              ? null
                              : {
                                  polyId: polygonId,
                                  key,
                                  type: "window",
                                  source: "auto",
                                  idx: origIdx,
                                }
                          );
                        }}
                        className={`flex justify-between font-mono cursor-pointer rounded px-1 ${
                          isActive
                            ? "bg-cyan-200 ring-1 ring-cyan-500"
                            : "hover:bg-slate-100"
                        }`}
                        title="Klick: im Plan markieren"
                      >
                        <span className="flex items-center gap-1">
                          <span className="text-[9px] uppercase text-slate-400">
                            auto
                          </span>
                          {w.rawText}
                        </span>
                        <span className="flex items-center gap-2">
                          <span>
                            {w.widthCm} × {w.heightCm} cm
                            {w.brhCm != null && ` · BRH ${w.brhCm}`}
                          </span>
                          <button
                            onClick={async () => {
                              try {
                                const widthM = w.widthCm / 100;
                                const heightM = w.heightCm / 100;
                                const bruestungM =
                                  w.brhCm != null ? w.brhCm / 100 : undefined;
                                const newOp = await api.multiagentAddOpening(
                                  runId,
                                  polygonId,
                                  {
                                    type: "window",
                                    widthM,
                                    heightM,
                                    ...(bruestungM != null
                                      ? { bruestungM }
                                      : {}),
                                    label: w.rawText,
                                  }
                                );
                                await api.multiagentSuppressAutoWindow(
                                  runId,
                                  polygonId,
                                  origIdx
                                );
                                const fresh = await loadBundle(runId);
                                setBundle(fresh);
                                openOpeningEditorForEdit(
                                  polygonId,
                                  newOp.openingIdx,
                                  {
                                    type: "window",
                                    widthM,
                                    heightM,
                                    ...(bruestungM != null
                                      ? { bruestungM }
                                      : {}),
                                  }
                                );
                              } catch (err) {
                                window.alert(
                                  "Übernehmen fehlgeschlagen: " +
                                    (err instanceof Error ? err.message : String(err))
                                );
                              }
                            }}
                            className="text-[10px] text-blue-700 hover:text-blue-900"
                            title="In User-Fenster umwandeln und editieren"
                          >
                            ✎
                          </button>
                          <button
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  `AUTO-Fenster „${w.rawText}" ausblenden?`
                                )
                              )
                                return;
                              try {
                                await api.multiagentSuppressAutoWindow(
                                  runId,
                                  polygonId,
                                  origIdx
                                );
                                const fresh = await loadBundle(runId);
                                setBundle(fresh);
                              } catch (err) {
                                window.alert(
                                  "Löschen fehlgeschlagen: " +
                                    (err instanceof Error ? err.message : String(err))
                                );
                              }
                            }}
                            className="text-[10px] text-red-600 hover:text-red-800"
                            title="AUTO-Fenster ausblenden"
                          >
                            🗑
                          </button>
                        </span>
                      </li>
                      );
                    })}
                    {userWindows.map((w) => {
                      const key = `user-${w._idx}`;
                      const isActive =
                        highlightedOpening?.polyId === polygonId &&
                        highlightedOpening.key === key;
                      return (
                      <li
                        key={`user-${w._idx}`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("button")) return;
                          setHighlightedOpening(
                            isActive
                              ? null
                              : {
                                  polyId: polygonId,
                                  key,
                                  type: "window",
                                  source: "user",
                                  idx: w._idx,
                                }
                          );
                        }}
                        className={`flex justify-between gap-2 font-mono p-1 border rounded cursor-pointer ${
                          isActive
                            ? "bg-cyan-200 border-cyan-500 ring-1 ring-cyan-500"
                            : "bg-cyan-50 border-cyan-200 hover:bg-cyan-100"
                        }`}
                        title="Klick: im Plan markieren"
                      >
                        <span className="flex items-center gap-1">
                          <span className="text-[9px] uppercase text-cyan-700 font-semibold">
                            user
                          </span>
                          {w.label || `Fenster ${w._idx + 1}`}
                        </span>
                        <span className="flex items-center gap-2">
                          <span>
                            {(w.widthM * 100).toFixed(0)}
                            {w.heightM ? ` × ${(w.heightM * 100).toFixed(0)}` : ""} cm
                            {w.bruestungM != null
                              ? ` · BRH ${(w.bruestungM * 100).toFixed(0)}`
                              : ""}
                          </span>
                          <button
                            onClick={() =>
                              openOpeningEditorForEdit(polygonId, w._idx, w)
                            }
                            className="text-[10px] text-blue-700 hover:text-blue-900"
                            title="Editieren"
                          >
                            ✎
                          </button>
                          <button
                            onClick={() => deleteOpening(polygonId, w._idx)}
                            className="text-[10px] text-red-600 hover:text-red-800"
                            title="Löschen"
                          >
                            🗑
                          </button>
                        </span>
                      </li>
                      );
                    })}
                  </ul>
                </CollapsibleSection>
              );
            })()}

            {/* Wand-Material-Stack — auch wenn 0 Wände gerendert, damit
                der „+ Wand"-Button immer verfügbar ist (Frank's Wunsch:
                Haus-Polygon hat initial 0 Wände, soll trotzdem editierbar sein).
                Sprint UX (2026-05-06): einklappbar, default offen. */}
            {(() => {
              const wallsCount =
                selectedFeature.properties.walls?.length ?? 0;
              return (
                <CollapsibleSection
                  icon="🧱"
                  title="Wände"
                  counter={wallsCount}
                  defaultOpen
                  hint="Wand-Liste mit Material-Stack pro Wand"
                  rightSlot={
                    editMode === "view" ? (
                      <>
                        <button
                          onClick={() =>
                            rematchWallsForPolygon(
                              selectedFeature.properties.id
                            )
                          }
                          disabled={wallRematchSaving}
                          className="text-[10px] px-1.5 py-0.5 bg-amber-100 hover:bg-amber-200 disabled:bg-slate-100 disabled:text-slate-400 text-amber-900 border border-amber-300 rounded"
                          title="Wände aus den Sprint-W-Cluster-Mittellinien für dieses Polygon neu zuordnen. codeAssignments + Öffnungen bleiben. Nach Vertex-Drag oder Code-Edits sinnvoll."
                        >
                          {wallRematchSaving ? "…" : "🔄 Neu berechnen"}
                        </button>
                        <button
                          onClick={() =>
                            startDrawWallSegment(
                              selectedFeature.properties.id
                            )
                          }
                          className="text-[10px] px-1.5 py-0.5 bg-violet-100 hover:bg-violet-200 text-violet-800 border border-violet-300 rounded"
                          title={
                            selectedFeature.properties.isBuilding
                              ? "Außenwand für das Haus zeichnen (Klinker / Dämmung / Mauerwerk per Material-Editor)"
                              : "Neue Wand für dieses Polygon zeichnen (2-Klick)"
                          }
                        >
                          + Wand
                        </button>
                      </>
                    ) : null
                  }
                >
                  {wallRematchInfo && (
                    <div className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-1.5 py-1 mb-1 mt-2">
                      {wallRematchInfo}
                    </div>
                  )}
                  {wallsCount === 0 && (
                    <p className="text-[11px] text-slate-400 italic mb-1 pt-2">
                      {selectedFeature.properties.isBuilding
                        ? "Noch keine Außenwände. Klick auf + Wand oben — danach im Material-Editor das Plan-Maß (z.B. 490 mm) eintragen für Systemwand-Vorschlag."
                        : "Noch keine Wände erkannt/gezeichnet. Klick auf + Wand oben."}
                    </p>
                  )}
                  <ul className="text-xs space-y-1 pt-2">
                    {(selectedFeature.properties.walls ?? []).map((w, i) => {
                      const isActive = highlightedWallIdx === i;
                      const sharers = w.wallId
                        ? [...(wallSharers.get(w.wallId) ?? new Set<string>())].filter(
                            (id) => id !== selectedFeature.properties.id
                          )
                        : [];
                      const isShared = sharers.length > 0;
                      const rohbauLayers = w.materials.filter(
                        (m) => m.gewerk === "rohbau"
                      );
                      const surfaceLayers = w.materials.filter(
                        (m) => m.gewerk !== "rohbau"
                      );
                      return (
                        <li
                          key={i}
                          onClick={() =>
                            setHighlightedWallIdx(isActive ? null : i)
                          }
                          className={`border rounded p-1.5 cursor-pointer transition-colors ${
                            isActive
                              ? "border-blue-500 bg-blue-50 ring-1 ring-blue-300"
                              : "bg-white hover:bg-slate-50"
                          }`}
                          title={
                            isActive
                              ? "Nochmal klicken zum Aufheben"
                              : "Klicken um die Wand im Plan zu markieren"
                          }
                        >
                          <div className="flex justify-between font-mono">
                            <span>
                              {(w.lengthMm / 1000).toFixed(2)} m
                              {isShared && (
                                <span
                                  className="ml-1 text-[10px] text-violet-700 bg-violet-100 px-1 rounded font-sans"
                                  title={`Geteilte Wand mit ${sharers.join(", ")} — Rohbau wird in der Mengen-Tabelle dedupliziert (jeweils 1/${sharers.length + 1} dem aktuellen Raum zugerechnet)`}
                                >
                                  ↔ {sharers.join(",")}
                                </span>
                              )}
                              {w.userAdded && (
                                <span
                                  className="ml-1 text-[10px] text-emerald-700 bg-emerald-100 px-1 rounded font-sans"
                                  title="Vom User manuell hinzugefügt"
                                >
                                  ✏ user
                                </span>
                              )}
                              {(() => {
                                const wAny = w as unknown as Record<
                                  string,
                                  unknown
                                >;
                                const code =
                                  typeof wAny.wallTypeCode === "string"
                                    ? wAny.wallTypeCode
                                    : undefined;
                                const facade =
                                  typeof wAny.facadeCode === "string"
                                    ? wAny.facadeCode
                                    : undefined;
                                if (!code) return null;
                                const wt = wallCatalog?.walls.find(
                                  (x) => x.code === code
                                );
                                return (
                                  <span
                                    className="ml-1 text-[10px] text-sky-800 bg-sky-100 border border-sky-200 px-1 rounded font-sans"
                                    title={
                                      wt
                                        ? `${wt.fullCode} — ${wt.shortLabel}\n${wt.normReferenz.join(", ")}${facade ? `\nFassade: ${facade}` : ""}`
                                        : `Wandtyp ${code}${facade ? ` · ${facade}` : ""}`
                                    }
                                  >
                                    {code}
                                    {facade ? ` · ${facade}` : ""}
                                  </span>
                                );
                              })()}
                              {(() => {
                                // Sprint A.8 — Code-Assignments-Badge: Anzahl
                                // + aggregierter Status. Klick öffnet
                                // Material-Editor (über bestehenden Edit-Button).
                                const cas = (
                                  w as unknown as {
                                    codeAssignments?: CodeAssignment[];
                                  }
                                ).codeAssignments;
                                if (!cas || cas.length === 0) return null;
                                const agg =
                                  aggregateCodeAssignmentStatus(cas);
                                const cls = agg
                                  ? CODE_ASSIGNMENT_STATUS_CLASSES[agg]
                                  : "bg-slate-100 text-slate-700 border-slate-200";
                                const tip = cas
                                  .map(
                                    (ca) =>
                                      `${ca.code} (${CODE_ASSIGNMENT_STATUS_LABELS[ca.status]})`
                                  )
                                  .join(", ");
                                return (
                                  <span
                                    className={`ml-1 text-[10px] px-1 rounded border font-sans ${cls}`}
                                    title={`${cas.length} Code-Zuweisung(en): ${tip}`}
                                  >
                                    📋 {cas.length}
                                  </span>
                                );
                              })()}
                            </span>
                            <span className="text-slate-500">
                              {w.totalThicknessMm.toFixed(0)} mm
                            </span>
                          </div>
                          {rohbauLayers.length > 0 && (
                            <div className="text-[10px] mt-0.5">
                              <span className="text-orange-700 font-semibold">
                                Rohbau:
                              </span>{" "}
                              <span className="text-slate-600">
                                {rohbauLayers
                                  .map(
                                    (m) =>
                                      `${m.preset} ${m.dickeMm.toFixed(0)}mm`
                                  )
                                  .join(" + ")}
                              </span>
                              {isShared && (
                                <span
                                  className="ml-1 text-violet-700"
                                  title="Geteilte Wand: Rohbau-Mengen werden in der Mengen-Tabelle nicht doppelt gezählt"
                                >
                                  (geteilt)
                                </span>
                              )}
                            </div>
                          )}
                          {surfaceLayers.length > 0 && (
                            <div className="text-[10px] mt-0.5">
                              <span className="text-blue-700 font-semibold">
                                Surface:
                              </span>{" "}
                              <span className="text-slate-600">
                                {surfaceLayers
                                  .map(
                                    (m) =>
                                      `${m.preset} ${m.dickeMm.toFixed(0)}mm`
                                  )
                                  .join(" + ")}
                              </span>
                            </div>
                          )}
                          {w.verifiedStatus && (
                            <div
                              className={`text-[10px] mt-0.5 ${
                                w.verifiedStatus === "verified"
                                  ? "text-emerald-700"
                                  : w.verifiedStatus === "warning"
                                    ? "text-amber-700"
                                    : "text-red-700"
                              }`}
                            >
                              {w.verifiedLabel} → {w.verifiedValueM} m (Δ
                              {w.verifiedDeltaPct?.toFixed(1)}%)
                            </div>
                          )}
                          {/* Sprint 0.19 — Edit-Buttons pro Wand (sichtbar wenn highlighted) */}
                          {isActive && editMode === "view" && (
                            <div className="flex flex-wrap gap-1 mt-1.5 pt-1 border-t border-slate-200">
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  startEditWallEndpoints(
                                    selectedFeature.properties.id,
                                    i
                                  );
                                }}
                                disabled={wallEditSaving}
                                className="text-[10px] px-1.5 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300 rounded disabled:opacity-50"
                                title="Endpunkte ziehen / verschieben"
                              >
                                ✥ Endpunkte
                              </button>
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  openMaterialEditor(
                                    selectedFeature.properties.id,
                                    i
                                  );
                                }}
                                disabled={wallEditSaving}
                                className="text-[10px] px-1.5 py-0.5 bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 rounded disabled:opacity-50"
                                title="Material-Schichten der Wand bearbeiten (Rohbau, Putz, Tapete)"
                              >
                                🎨 Material
                              </button>
                              <button
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  if (
                                    window.confirm(
                                      `Wand ${i} (${(w.lengthMm / 1000).toFixed(2)} m) löschen?`
                                    )
                                  ) {
                                    deleteWall(
                                      selectedFeature.properties.id,
                                      i
                                    );
                                  }
                                }}
                                disabled={wallEditSaving}
                                className="text-[10px] px-1.5 py-0.5 bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 rounded disabled:opacity-50"
                                title="Wand komplett entfernen"
                              >
                                🗑 Löschen
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </CollapsibleSection>
              );
            })()}
          </div>
        )}

        {/* Sprint 0.19 — Verlauf / Undo */}
        {activeTab === "verlauf" && (
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">
            ↶ Verlauf
            <span className="ml-1 text-slate-400 font-normal">
              ({history.length})
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mb-2">
            Vor jedem Edit (Split/Merge/Wand/Vertex/Reset) wird der vorherige
            Stand gesichert. Klick „↩ Wiederherstellen" um zurückzuspringen — der
            aktuelle Stand wird dabei als neuer Undo-Snapshot abgelegt.
          </p>
          {historyError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5 mb-2">
              {historyError}
            </div>
          )}
          {history.length === 0 ? (
            <p className="text-xs text-slate-400 italic">
              Noch keine Edits gemacht — Verlauf ist leer.
            </p>
          ) : (
            <ul className="text-xs space-y-1 mb-2 max-h-48 overflow-y-auto">
              {history.map((h) => {
                const ts = new Date(h.timestamp);
                const tsStr = ts.toLocaleTimeString("de-DE", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                });
                return (
                  <li
                    key={h.id}
                    className="flex items-center justify-between gap-2 border rounded p-1.5 bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[11px] text-slate-700 truncate">
                        {h.label}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {tsStr}
                        {h.polygonCount != null && (
                          <span> · {h.polygonCount} Pol.</span>
                        )}
                        {h.userWallCount != null && h.userWallCount > 0 && (
                          <span> · {h.userWallCount} Wand</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => restoreSnapshot(h.id)}
                      disabled={restoring || resetting}
                      className="text-[10px] px-1.5 py-0.5 bg-blue-100 hover:bg-blue-200 text-blue-700 border border-blue-300 rounded disabled:opacity-50 whitespace-nowrap"
                      title="Auf diesen Stand zurückspringen"
                    >
                      ↩ Wiederherstellen
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            onClick={resetToPipeline}
            disabled={resetting || restoring}
            className="w-full text-xs px-2 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded disabled:opacity-50"
            title="Verwirft alle User-Edits (Wände + Splits + Vertex-Drags) und rechnet die Pipeline komplett neu. Vorheriger Stand wird in der History gesichert."
          >
            {resetting ? "Pipeline läuft…" : "🔄 Auf Pipeline-Default zurücksetzen"}
          </button>
        </div>
        )}

        {/* Virtuelle Schnitte aus Auswahl-Tab entfernt (2026-05-06) — Tool ist
            über die Tool-Rail-Hotkey "W" erreichbar, dedupliziert die UI. */}

        {/* Mengen */}
        {activeTab === "mengen" && (
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowQuantities((s) => !s)}
              className="flex-1 flex items-center justify-between text-sm font-semibold text-slate-700"
            >
              <span>
                {quantitiesView === "legacy" ? "Mengen pro Gewerk" : "LV-Positionen (v2)"}
              </span>
              <span className="text-slate-400 mr-2">{showQuantities ? "▾" : "▸"}</span>
            </button>
            <div className="inline-flex rounded border border-slate-300 overflow-hidden text-[11px]">
              <button
                onClick={() => setQuantitiesView("legacy")}
                className={`px-2 py-0.5 ${
                  quantitiesView === "legacy"
                    ? "bg-slate-700 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                title="Legacy: Raum × Gewerk-Tabelle (heute)"
              >
                Legacy
              </button>
              <button
                onClick={() => setQuantitiesView("v2")}
                className={`px-2 py-0.5 border-l border-slate-300 ${
                  quantitiesView === "v2"
                    ? "bg-emerald-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50"
                }`}
                title="v2: LV-Position-Liste aus codeAssignments + lvMapping (Sprint A.10)"
              >
                v2
              </button>
            </div>
          </div>
          {showQuantities && (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs">
                Raumhöhe
                <input
                  type="range"
                  min={2.2}
                  max={3.0}
                  step={0.05}
                  value={raumhoeheM}
                  onChange={(e) => setRaumhoeheM(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="font-mono w-12 text-right">
                  {raumhoeheM.toFixed(2)} m
                </span>
              </label>
              <label className="flex items-center gap-2 text-xs">
                Putz-Abzug
                <input
                  type="range"
                  min={0}
                  max={30}
                  step={1}
                  value={putzAbzugPercent}
                  onChange={(e) => setPutzAbzugPercent(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="font-mono w-10 text-right">
                  {putzAbzugPercent}%
                </span>
              </label>
              <div className="border-t pt-2 mt-1">
                <div className="text-[11px] text-slate-500 mb-1">
                  Verschnitt-Aufschläge (Bestellmenge)
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <span className="w-14 text-slate-600">Boden</span>
                  <input
                    type="range"
                    min={0}
                    max={20}
                    step={1}
                    value={verschnittBoden}
                    onChange={(e) =>
                      setVerschnittBoden(Number(e.target.value))
                    }
                    className="flex-1"
                    title="Estrich, Parkett, Trockenbau-Wand"
                  />
                  <span className="font-mono w-10 text-right">
                    {verschnittBoden}%
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <span className="w-14 text-slate-600">Fliesen</span>
                  <input
                    type="range"
                    min={0}
                    max={25}
                    step={1}
                    value={verschnittFliesen}
                    onChange={(e) =>
                      setVerschnittFliesen(Number(e.target.value))
                    }
                    className="flex-1"
                    title="Verschnitt für Fliesenschnitt am Rand"
                  />
                  <span className="font-mono w-10 text-right">
                    {verschnittFliesen}%
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <span className="w-14 text-slate-600">Rohbau</span>
                  <input
                    type="range"
                    min={0}
                    max={15}
                    step={1}
                    value={verschnittRohbau}
                    onChange={(e) =>
                      setVerschnittRohbau(Number(e.target.value))
                    }
                    className="flex-1"
                    title="Mauerwerk, Sicht-Mauerwerk"
                  />
                  <span className="font-mono w-10 text-right">
                    {verschnittRohbau}%
                  </span>
                </label>
              </div>
              {quantitiesView === "legacy" && (
              <>
              <a
                href={api.multiagentQuantitiesXlsxUrl(runId, {
                  raumhoehe: raumhoeheM,
                  putzAbzug: putzAbzugPercent,
                  verschnittBoden,
                  verschnittFliesen,
                  verschnittRohbau,
                })}
                className="block text-center text-xs px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                download
              >
                ⬇ Excel-Export (Mengen-Tabelle)
              </a>
              {quantitiesError && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
                  {quantitiesError}
                </div>
              )}
              {quantities && quantities.warnings.length > 0 && (
                <details className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
                  <summary>{quantities.warnings.length} Warnung(en)</summary>
                  <ul className="mt-1 space-y-0.5">
                    {quantities.warnings.map((w, i) => (
                      <li key={i}>· {w}</li>
                    ))}
                  </ul>
                </details>
              )}
              {quantities && (
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left py-1">Gewerk</th>
                      <th className="text-right">Räume</th>
                      <th className="text-right">Summe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(quantities.totals).map(([g, t]) => (
                      <>
                        <tr
                          key={g}
                          className="border-t cursor-pointer hover:bg-slate-50"
                          onClick={() =>
                            setExpandedGewerk(expandedGewerk === g ? null : g)
                          }
                        >
                          <td className="py-1.5">
                            {expandedGewerk === g ? "▾ " : "▸ "}
                            {g}
                          </td>
                          <td className="text-right text-slate-600">
                            {t.rooms}
                          </td>
                          <td className="text-right font-mono">
                            {t.menge.toFixed(2)} {t.einheit}
                          </td>
                        </tr>
                        {expandedGewerk === g && quantities.byGewerk[g] && (
                          <tr key={`${g}-detail`}>
                            <td colSpan={3} className="bg-slate-50 px-2 py-1">
                              {(() => {
                                const rows = quantities.byGewerk[g];
                                const weList = runMeta?.wohneinheiten ?? [];
                                const hasWe = weList.length >= 1;
                                // Wenn keine WEs angelegt: flache Liste wie bisher
                                if (!hasWe) {
                                  return (
                                    <table className="w-full text-[11px]">
                                      <tbody>
                                        {rows.map((row) => (
                                          <tr key={row.roomId}>
                                            <td className="font-mono">
                                              {row.roomId}
                                            </td>
                                            <td>{row.roomLabel}</td>
                                            <td className="text-right font-mono">
                                              {row.menge.toFixed(2)} {row.einheit}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  );
                                }
                                // WE-Gruppierung: pro WE Subtotal + Räume
                                type WeBucket = {
                                  id: string;
                                  label: string;
                                  rows: typeof rows;
                                  sum: number;
                                };
                                const buckets = new Map<string, WeBucket>();
                                for (const we of weList) {
                                  buckets.set(we.id, {
                                    id: we.id,
                                    label: we.label,
                                    rows: [],
                                    sum: 0,
                                  });
                                }
                                buckets.set("__nowe__", {
                                  id: "__nowe__",
                                  label: "ohne WE",
                                  rows: [],
                                  sum: 0,
                                });
                                for (const r of rows) {
                                  const we = roomToWe.get(r.roomId);
                                  const key = we ? we.id : "__nowe__";
                                  const b = buckets.get(key);
                                  if (b) {
                                    b.rows.push(r);
                                    b.sum += r.menge;
                                  }
                                }
                                const einheit = rows[0]?.einheit ?? "m²";
                                const visibleBuckets = Array.from(buckets.values())
                                  .filter((b) => b.rows.length > 0);
                                return (
                                  <div className="space-y-2">
                                    {visibleBuckets.map((b) => (
                                      <div key={b.id}>
                                        <div className="flex justify-between items-center text-[11px] font-semibold text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5 mb-0.5">
                                          <span>
                                            {b.id === "__nowe__"
                                              ? "Räume ohne Wohneinheit"
                                              : b.label}
                                            <span className="ml-1 font-normal text-cyan-600 text-[10px]">
                                              ({b.rows.length})
                                            </span>
                                          </span>
                                          <span className="font-mono">
                                            {b.sum.toFixed(2)} {einheit}
                                          </span>
                                        </div>
                                        <table className="w-full text-[11px]">
                                          <tbody>
                                            {b.rows.map((row) => (
                                              <tr key={row.roomId}>
                                                <td className="font-mono pl-2">
                                                  {row.roomId}
                                                </td>
                                                <td>{row.roomLabel}</td>
                                                <td className="text-right font-mono">
                                                  {row.menge.toFixed(2)}{" "}
                                                  {row.einheit}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Sprint #6 (2026-05-05) — Außenanlage (GALA-Container) */}
              {quantities?.gala && (
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <div className="text-xs font-semibold text-emerald-800 mb-1.5 flex items-center gap-1">
                    <span>🌿 Außenanlage</span>
                    <span className="text-[10px] font-normal text-slate-500">
                      ({quantities.gala.perimeterM.toFixed(1)} m Umriss · {quantities.gala.areaM2Total.toFixed(0)} m² gesamt)
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-t border-emerald-100">
                        <td className="py-1 text-slate-700">Container-Fläche (GALA)</td>
                        <td className="text-right font-mono text-[11px]">
                          {quantities.gala.areaM2Total.toFixed(2)} m²
                        </td>
                      </tr>
                      {quantities.gala.areaM2Haus > 0 && (
                        <tr className="border-t border-emerald-100 text-slate-500">
                          <td className="py-1 pl-3">− Gebäude-Schatten (HAUS)</td>
                          <td className="text-right font-mono text-[11px]">
                            −{quantities.gala.areaM2Haus.toFixed(2)} m²
                          </td>
                        </tr>
                      )}
                      {quantities.gala.subtractedPolygons.map((s) => (
                        <tr
                          key={s.polygonId}
                          className="border-t border-emerald-100 text-slate-500"
                        >
                          <td className="py-1 pl-3">
                            − {s.label}{" "}
                            <span className="text-[10px] text-slate-400">
                              ({s.polygonId})
                            </span>
                          </td>
                          <td className="text-right font-mono text-[11px]">
                            −{s.areaM2.toFixed(2)} m²
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-emerald-300 font-semibold text-emerald-900 bg-emerald-50">
                        <td className="py-1.5">
                          = Freie GaLa-Fläche
                          <div className="text-[10px] font-normal text-emerald-700 italic">
                            LV-Scope für Mutterboden, Rasen, Pflanzungen, Hecke
                          </div>
                        </td>
                        <td className="text-right font-mono text-[12px]">
                          {quantities.gala.areaM2Freie.toFixed(2)} m²
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {/* Sprint 0.19 / Punkt 4 — Gebäude-Hülle (Haus-Polygon-Wände) */}
              {quantities?.haus && quantities.haus.materials.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
                    <span>🏠 Gebäude-Hülle</span>
                    <span className="text-[10px] font-normal text-slate-500">
                      ({quantities.haus.perimeterM.toFixed(2)} m Außenkontur ×
                      {" "}
                      {quantities.haus.raumhoeheM.toFixed(2)} m)
                    </span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="text-left py-1">Material</th>
                        <th className="text-right">Dicke</th>
                        <th className="text-right">Fläche</th>
                        <th className="text-right">Volumen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quantities.haus.materials.map((m, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-1">
                            <div className="font-mono text-[11px]">
                              {m.materialName ?? m.preset}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {m.gewerk} · {m.preset}
                            </div>
                          </td>
                          <td className="text-right font-mono text-[11px]">
                            {m.dickeMm.toFixed(0)} mm
                          </td>
                          <td className="text-right font-mono text-[11px]">
                            {m.areaM2.toFixed(2)} m²
                          </td>
                          <td className="text-right font-mono text-[11px]">
                            {m.volumeM3.toFixed(2)} m³
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </>
              )}
              {quantitiesView === "v2" && (
              <>
                {quantitiesV2Error && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5">
                    {quantitiesV2Error}
                  </div>
                )}
                {!quantitiesV2 && !quantitiesV2Error && (
                  <div className="text-xs text-slate-500 italic">Lade v2-Daten…</div>
                )}
                {quantitiesV2 && (
                  <>
                    <div className="text-[11px] text-slate-600 bg-emerald-50 border border-emerald-200 rounded p-1.5">
                      <strong>{quantitiesV2.positions.length}</strong> LV-Positionen aus{" "}
                      <strong>{quantitiesV2.config.polygonsProcessed}</strong> Polygon(en).
                      {quantitiesV2.config.skippedCodes > 0 && (
                        <>
                          {" "}
                          {quantitiesV2.config.skippedCodes} Code(s) ohne LV-Mapping
                          übersprungen.
                        </>
                      )}
                    </div>
                    {(quantitiesV2.unmappedCodes.length > 0 ||
                      quantitiesV2.unknownCodes.length > 0) && (
                      <details className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-1.5">
                        <summary>
                          {quantitiesV2.unmappedCodes.length} ohne LV-Mapping ·{" "}
                          {quantitiesV2.unknownCodes.length} unbekannt
                        </summary>
                        <div className="mt-1 space-y-1">
                          {quantitiesV2.unmappedCodes.length > 0 && (
                            <div>
                              <div className="font-semibold">
                                Codes ohne lvMapping (im Katalog, aber kein LV-Mapping
                                befüllt):
                              </div>
                              <ul className="ml-2">
                                {quantitiesV2.unmappedCodes.slice(0, 20).map((c, i) => (
                                  <li key={i}>
                                    · {c.code} <span className="text-amber-600">({c.scope}, {c.polygonId})</span>
                                  </li>
                                ))}
                                {quantitiesV2.unmappedCodes.length > 20 && (
                                  <li>… und {quantitiesV2.unmappedCodes.length - 20} weitere</li>
                                )}
                              </ul>
                            </div>
                          )}
                          {quantitiesV2.unknownCodes.length > 0 && (
                            <div>
                              <div className="font-semibold">
                                Unbekannte Codes (typo / Legacy):
                              </div>
                              <ul className="ml-2">
                                {quantitiesV2.unknownCodes.slice(0, 20).map((c, i) => (
                                  <li key={i}>
                                    · {c.code} <span className="text-amber-600">({c.scope}, {c.polygonId})</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                    {/* Filter-Pillen */}
                    <div className="flex flex-wrap gap-1.5 text-[11px]">
                      <select
                        value={v2HauptgruppeFilter}
                        onChange={(e) => setV2HauptgruppeFilter(e.target.value)}
                        className="border border-slate-300 rounded px-1 py-0.5"
                      >
                        <option value="alle">alle Hauptgruppen</option>
                        {quantitiesV2.gesamtAggregat.map((g) => (
                          <option key={g.hauptgruppe} value={g.hauptgruppe}>
                            {g.hauptgruppe} ({g.positionsCount})
                          </option>
                        ))}
                      </select>
                      <select
                        value={v2RaumFilter}
                        onChange={(e) => setV2RaumFilter(e.target.value)}
                        className="border border-slate-300 rounded px-1 py-0.5"
                      >
                        <option value="alle">alle Räume</option>
                        {quantitiesV2.roomAggregates.map((r) => (
                          <option key={r.polygonId} value={r.polygonId}>
                            {r.polygonId} {r.polygonLabel}
                          </option>
                        ))}
                      </select>
                      <select
                        value={v2QuelleFilter}
                        onChange={(e) => setV2QuelleFilter(e.target.value)}
                        className="border border-slate-300 rounded px-1 py-0.5"
                      >
                        <option value="alle">alle Quellen</option>
                        <option value="user-manual">user-manual</option>
                        <option value="auto-thickness">auto-thickness</option>
                        <option value="auto-stempel">auto-stempel</option>
                        <option value="auto-roomtype">auto-roomtype</option>
                        <option value="auto-inline-text">auto-inline-text</option>
                      </select>
                    </div>
                    {/* Aggregat pro Hauptgruppe */}
                    <div className="border-t pt-2">
                      <div className="text-[11px] font-semibold text-slate-700 mb-1">
                        Σ pro Hauptgruppe
                      </div>
                      <table className="w-full text-[11px]">
                        <tbody>
                          {quantitiesV2.gesamtAggregat.map((g) => (
                            <tr
                              key={g.hauptgruppe}
                              className={`border-t cursor-pointer ${
                                v2HauptgruppeFilter === g.hauptgruppe
                                  ? "bg-emerald-50"
                                  : "hover:bg-slate-50"
                              }`}
                              onClick={() =>
                                setV2HauptgruppeFilter(
                                  v2HauptgruppeFilter === g.hauptgruppe
                                    ? "alle"
                                    : g.hauptgruppe
                                )
                              }
                            >
                              <td className="py-0.5 font-mono">{g.hauptgruppe}</td>
                              <td>{g.hauptgruppeName}</td>
                              <td className="text-right text-slate-500">
                                {g.positionsCount}
                              </td>
                              <td className="text-right font-mono">
                                {g.menge.toFixed(2)} {g.einheit}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Detail-Position-Liste */}
                    <div className="border-t pt-2">
                      <div className="text-[11px] font-semibold text-slate-700 mb-1">
                        LV-Positionen
                      </div>
                      <div className="space-y-0.5 max-h-[420px] overflow-y-auto">
                        {quantitiesV2.positions
                          .filter((p) => {
                            if (
                              v2HauptgruppeFilter !== "alle" &&
                              p.hauptgruppe !== v2HauptgruppeFilter
                            )
                              return false;
                            if (
                              v2RaumFilter !== "alle" &&
                              p.polygonId !== v2RaumFilter
                            )
                              return false;
                            if (
                              v2QuelleFilter !== "alle" &&
                              p.source !== v2QuelleFilter
                            )
                              return false;
                            return true;
                          })
                          .map((p, i) => (
                            <div
                              key={`${p.polygonId}-${p.code}-${p.hauptgruppe}-${p.bezeichnung}-${i}`}
                              className="text-[11px] border-l-2 border-emerald-300 pl-1.5 py-0.5 hover:bg-emerald-50"
                              title={`Formel: ${p.mengenFormel}\nAusdruck: ${p.mengenAusdruck}\nLangtext: ${p.langtextTemplate}`}
                            >
                              <div className="flex justify-between items-baseline gap-2">
                                <span className="font-mono text-slate-700">
                                  {p.ozPattern.replace("{N}", "??")}
                                </span>
                                <span className="font-mono text-emerald-700">
                                  {p.menge.toFixed(2)} {p.einheit}
                                </span>
                              </div>
                              <div className="text-slate-600 truncate">
                                {p.bezeichnung}
                              </div>
                              <div className="text-[10px] text-slate-400">
                                {p.polygonId} · {p.code} · {p.source} · {p.scope}
                                {p.wallId ? ` · ${p.wallId.slice(0, 16)}…` : ""}
                              </div>
                            </div>
                          ))}
                        {quantitiesV2.positions.length === 0 && (
                          <div className="text-xs italic text-slate-500">
                            Keine LV-Positionen — Polygone haben noch keine
                            codeAssignments mit lvMapping. Wand-Codes (AW/IW/FAS)
                            werden automatisch vom code-assigner zugewiesen, sobald
                            die Pipeline einen Run mit Wänden produziert. Boden-/
                            Wand-/Decken-/Sanitär-Codes (B-Reduced) müssen über den
                            Material-Editor manuell zugewiesen werden.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Toggles */}
        {activeTab === "anzeige" && (
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Anzeige</h3>
          <div className="space-y-1.5 text-xs">
            <div className="space-y-1">
              <div className="text-slate-600 font-medium">Plan-Hintergrund</div>
              <div className="flex gap-1">
                {(["all", "walls", "helpers"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setPlanBitmapLayer(mode)}
                    className={`flex-1 px-2 py-1 text-[11px] rounded border transition-colors ${
                      planBitmapLayer === mode
                        ? "bg-brand-600 text-white border-brand-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                    title={
                      mode === "all"
                        ? "Voll-Plan (alles wie original)"
                        : mode === "walls"
                          ? "Nur als Wand klassifizierte Linien"
                          : "Nur Hilfslinien (Achsen, Bemaßung, Möbel)"
                    }
                  >
                    {mode === "all"
                      ? "Voll"
                      : mode === "walls"
                        ? "Wände"
                        : "Helfer"}
                  </button>
                ))}
              </div>
              <label
                className={`flex items-center gap-2 mt-1 ${
                  planBitmapLayer === "all" ? "" : "opacity-50"
                }`}
                title="Blendet im Voll-Modus den OCG-Layer A-Möblierung aus (Betten, Stühle, Sanitär-Symbole). Erleichtert das Wände-Zeichnen. Wirkt nur bei Vektor-PDFs mit OCG-Layern."
              >
                <input
                  type="checkbox"
                  checked={hideFurniture}
                  onChange={(e) => setHideFurniture(e.target.checked)}
                  disabled={planBitmapLayer !== "all"}
                />
                Möblierung ausblenden
              </label>
            </div>
            <div className="border-t my-1" />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showFill}
                onChange={(e) => setShowFill(e.target.checked)}
              />
              Polygon-Füllung
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showLabels}
                onChange={(e) => setShowLabels(e.target.checked)}
              />
              Raum-Labels (id · m²)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showNgfAnchors}
                onChange={(e) => setShowNgfAnchors(e.target.checked)}
              />
              NGF-Anchor-Punkte
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showSplitsHighlight}
                onChange={(e) => setShowSplitsHighlight(e.target.checked)}
              />
              Split-Candidates rot markieren
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showDoorWalls}
                onChange={(e) => setShowDoorWalls(e.target.checked)}
              />
              Tür-Wand-Linien (gestrichelt rot)
              {bundle.doorWalls && (
                <span className="text-slate-500 text-[11px] ml-auto">
                  {bundle.doorWalls.doorWalls.length}
                </span>
              )}
            </label>
            <label
              className="flex items-center gap-2"
              title="Auto-T-Stoss-Trennlinien (orange-gestrichelt). Klick auf eine Linie deaktiviert sie und triggert einen Pipeline-Re-Run."
            >
              <input
                type="checkbox"
                checked={showAutoTJunctions}
                onChange={(e) => setShowAutoTJunctions(e.target.checked)}
              />
              Auto-T-Stoss-Trennlinien (gestrichelt orange)
              {bundle.polygons.autoTJunctions && (
                <span className="text-slate-500 text-[11px] ml-auto">
                  {bundle.polygons.autoTJunctions.filter((t) => !t.disabled).length}
                  {bundle.polygons.autoTJunctions.some((t) => t.disabled)
                    ? ` / ${bundle.polygons.autoTJunctions.length}`
                    : ""}
                </span>
              )}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={showOutdoor}
                onChange={(e) => setShowOutdoor(e.target.checked)}
              />
              Außen-Polygone anzeigen (grau)
              <span className="text-slate-500 text-[11px] ml-auto">
                {roomCounts.outside}
              </span>
            </label>
            <label className="flex items-center gap-2">
              Opazität:
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(opacity * 100)}
                onChange={(e) => setOpacity(Number(e.target.value) / 100)}
                className="flex-1"
              />
              <span className="font-mono w-8 text-right">
                {Math.round(opacity * 100)}%
              </span>
            </label>
          </div>
        </div>
        )}
      </aside>
      </div>{/* /UX2 3-Spalten-Body */}

      {/* Sprint 0.19 / Punkt 4 — Material-Editor-Modal: Schichten einer Wand
          editieren (Rohbau-Mauerwerk, Putz, Trockenbau). Auch für vom CAD
          erkannte Wände, falls die Material-Klassifikation falsch lag. */}
      {materialEditorOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={closeMaterialEditor}
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-[560px] max-w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-base font-semibold">
                  🎨 Wand-Material — {materialEditorPolygonId} #
                  {materialEditorWallIdx}
                </h3>
                <div className="text-[11px] text-slate-500">
                  Schritt {materialWizardStep} von 4
                </div>
              </div>
              {/* Sprint #5 — Stepper-Header, 4 Schritte mit Connector-Lines.
                  Nicht-anklickbar (User navigiert mit Zurück/Weiter), aber
                  current-Step ist visuell hervorgehoben + done-Steps bekommen
                  Häkchen. */}
              <div className="flex items-center">
                {([1, 2, 3, 4] as const).map((s, idx) => {
                  const labels: Record<number, string> = {
                    1: "Wandtyp",
                    2: "Aufbau",
                    3: "Fachplaner",
                    4: "Oberflächen",
                  };
                  const done = s < materialWizardStep;
                  const active = s === materialWizardStep;
                  return (
                    <div
                      key={s}
                      className="flex items-center"
                      style={{ flex: idx < 3 ? 1 : 0 }}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                            active
                              ? "bg-emerald-600 text-white"
                              : done
                                ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                                : "bg-slate-100 text-slate-400 border border-slate-200"
                          }`}
                        >
                          {done ? "✓" : s}
                        </div>
                        <div
                          className={`text-[10px] ${
                            active
                              ? "text-emerald-700 font-semibold"
                              : done
                                ? "text-slate-600"
                                : "text-slate-400"
                          }`}
                        >
                          {labels[s]}
                        </div>
                      </div>
                      {idx < 3 && (
                        <div
                          className={`flex-1 h-px mx-1 mb-3 ${
                            done ? "bg-emerald-300" : "bg-slate-200"
                          }`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stufe 1 (Wand-Katalog) — Wandtyp aus typisierter Bibliothek
                wählen. Setzt Material-Stack + Code für LV-Nachweise.
                Sprint #5 — nur sichtbar im Schritt 1. */}
            {materialWizardStep === 1 && wallCatalog && (
              <div className="p-4 border-b bg-sky-50/40">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-700">
                    Wandtyp aus Katalog
                    {materialEditorWallTypeCode && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-sky-100 text-sky-800 border border-sky-300 rounded text-[10px] font-mono">
                        {materialEditorWallTypeCode}
                        <button
                          onClick={() => setMaterialEditorWallTypeCode(null)}
                          className="text-sky-700 hover:text-sky-900"
                          title="Zuordnung entfernen"
                        >
                          ✕
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    {(["inner", "outer"] as const).map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setMaterialEditorCatalogTab(cat)}
                        className={`px-2 py-0.5 text-[11px] rounded border transition-colors ${
                          materialEditorCatalogTab === cat
                            ? "bg-sky-600 text-white border-sky-600"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {cat === "inner" ? "Innenwand" : "Außenwand"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {wallCatalog.walls
                    .filter((w) => w.category === materialEditorCatalogTab)
                    .map((w) => {
                      const isActive = materialEditorWallTypeCode === w.code;
                      return (
                        <button
                          key={w.code}
                          onClick={() => applyWallTypeFromCatalog(w.code)}
                          className={`w-full text-left p-1.5 rounded border text-[11px] transition-colors ${
                            isActive
                              ? "bg-sky-100 border-sky-400 ring-1 ring-sky-300"
                              : "bg-white border-slate-200 hover:bg-sky-50 hover:border-sky-300"
                          }`}
                          title={w.description}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-sky-900">
                              {w.code}
                            </span>
                            <span className="text-slate-700">{w.shortLabel}</span>
                            <span className="ml-auto text-slate-500 text-[10px] font-mono">
                              {w.totalThicknessMmRange[0]}–
                              {w.totalThicknessMmRange[1]}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {w.bauweise} · {w.tragend === "ja" ? "tragend" : w.tragend === "nein" ? "nicht-tragend" : w.tragend}
                            {" · "}
                            {w.normReferenz.slice(0, 2).join(", ")}
                          </div>
                        </button>
                      );
                    })}
                </div>
                {materialEditorCatalogTab === "outer" &&
                  materialEditorWallTypeCode && (
                    <div className="mt-2 pt-2 border-t border-sky-200">
                      <div className="text-[10px] font-semibold text-slate-700 mb-1">
                        Fassade
                        {materialEditorFacadeCode && (
                          <span className="ml-2 px-1 py-0 bg-sky-100 text-sky-800 border border-sky-300 rounded font-mono">
                            {materialEditorFacadeCode}
                          </span>
                        )}
                      </div>
                      <select
                        value={materialEditorFacadeCode ?? ""}
                        onChange={(e) =>
                          setMaterialEditorFacadeCode(e.target.value || null)
                        }
                        className="w-full text-[11px] border border-slate-300 rounded px-1.5 py-1"
                      >
                        <option value="">— Fassade wählen —</option>
                        {wallCatalog.facades.map((f) => (
                          <option key={f.code} value={f.code}>
                            {f.code} · {f.shortLabel}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
              </div>
            )}

            {/* Sprint A.8 — Stufe 2: Code-Zuweisungen mit Status + Fachplaner-
                Nachweisen. Sprint #5 — als Schritt 3 (Fachplaner) gerendert,
                weil die Status- und Nachweis-Workflows hier liegen.
                Pro Wand können mehrere Codes mit eigener Quelle,
                Status und Nachweis-Liste gepflegt werden. Stufe-1-Wahl oben
                (wallTypeCode/facadeCode) bleibt für den Material-Stack-Init
                unabhängig erhalten. */}
            {materialWizardStep === 3 && (
            <div className="p-4 border-b bg-emerald-50/30">
              <div className="text-[11px] text-slate-600 italic mb-2 leading-relaxed">
                Bestätige die Fachplaner-Nachweise für die zugewiesenen Codes.
                Vom Wandtyp gesetzte Codes erscheinen automatisch — du kannst
                Status und Nachweise pro Code-Zeile aufklappen.
              </div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-700">
                  Code-Zuweisungen
                  <span className="ml-2 text-[10px] text-slate-500 font-normal">
                    ({materialEditorCodeAssignments.length})
                  </span>
                </div>
                <button
                  onClick={() => setShowCodePicker((v) => !v)}
                  className="px-2 py-0.5 text-[11px] rounded border bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                >
                  {showCodePicker ? "Picker schließen" : "+ Code zuweisen"}
                </button>
              </div>

              {showCodePicker && wallCatalog && (
                <div className="mb-2 p-1.5 bg-white rounded border border-slate-300 max-h-40 overflow-y-auto space-y-0.5">
                  {[
                    ...wallCatalog.walls.map((w) => ({
                      code: w.code,
                      shortLabel: w.shortLabel,
                    })),
                    ...wallCatalog.facades.map((f) => ({
                      code: f.code,
                      shortLabel: f.shortLabel,
                    })),
                  ]
                    .filter(
                      (c) =>
                        !materialEditorCodeAssignments.some(
                          (ca) => ca.code === c.code
                        )
                    )
                    .map((c) => (
                      <button
                        key={c.code}
                        onClick={() => addCodeAssignment(c.code)}
                        className="w-full text-left px-1.5 py-0.5 rounded hover:bg-emerald-50 text-[11px]"
                      >
                        <span className="font-mono font-semibold text-emerald-900">
                          {c.code}
                        </span>
                        <span className="ml-1.5 text-slate-700">
                          {c.shortLabel}
                        </span>
                      </button>
                    ))}
                </div>
              )}

              {materialEditorCodeAssignments.length === 0 ? (
                <p className="text-[11px] text-slate-500 italic leading-relaxed">
                  Noch keine Codes zugewiesen. Stufe-1-Wandtyp oben definiert
                  den Material-Stack; Stufe-2-Codes hier ergänzen Quelle,
                  Status und Fachplaner-Nachweise für die LV-Freigabe.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {materialEditorCodeAssignments.map((ca) => {
                    const isExpanded = expandedCodeAssignmentId === ca.id;
                    const nachweisCount = Object.keys(ca.nachweise).length;
                    return (
                      <div
                        key={ca.id}
                        className="bg-white rounded border border-slate-300"
                      >
                        <div className="flex items-center gap-1.5 p-1.5">
                          <span className="font-mono font-semibold text-slate-900 text-[11px]">
                            {ca.code}
                          </span>
                          <span
                            className={`px-1.5 py-0 text-[10px] rounded border ${CODE_ASSIGNMENT_STATUS_CLASSES[ca.status]}`}
                          >
                            {CODE_ASSIGNMENT_STATUS_LABELS[ca.status]}
                          </span>
                          <span
                            className={`px-1.5 py-0 text-[10px] rounded border ${CODE_ASSIGNMENT_SOURCE_CLASSES[ca.source]}`}
                            title={`Quelle: ${ca.source}`}
                          >
                            {CODE_ASSIGNMENT_SOURCE_LABELS[ca.source]}
                          </span>
                          <button
                            onClick={() =>
                              setExpandedCodeAssignmentId(
                                isExpanded ? null : ca.id
                              )
                            }
                            className="ml-auto text-[11px] text-slate-600 hover:text-slate-900"
                            title="Status + Fachplaner-Nachweise"
                          >
                            {isExpanded ? "▾" : "▸"} Nachweise ({nachweisCount})
                          </button>
                          <button
                            onClick={() => removeCodeAssignment(ca.id)}
                            className="text-rose-600 hover:text-rose-800 text-[11px] px-1"
                            title="Code-Zuweisung entfernen"
                          >
                            🗑
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="border-t bg-slate-50/50 p-2 space-y-2">
                            <div className="flex items-center flex-wrap gap-1.5 text-[11px]">
                              <span className="text-slate-600">Status:</span>
                              {CODE_ASSIGNMENT_STATUSES.map((st) => (
                                <button
                                  key={st}
                                  onClick={() =>
                                    setCodeAssignmentStatus(ca.id, st)
                                  }
                                  className={`px-1.5 py-0 text-[10px] rounded border ${
                                    ca.status === st
                                      ? CODE_ASSIGNMENT_STATUS_CLASSES[st] +
                                        " ring-1 ring-slate-400"
                                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                  }`}
                                >
                                  {CODE_ASSIGNMENT_STATUS_LABELS[st]}
                                </button>
                              ))}
                            </div>
                            <div>
                              <div className="text-[11px] font-semibold text-slate-700 mb-1">
                                Fachplaner-Nachweise
                              </div>
                              {nachweisCount === 0 ? (
                                <p className="text-[10px] text-slate-500 italic mb-1">
                                  Noch keine Nachweise erfasst.
                                </p>
                              ) : (
                                <div className="space-y-0.5 mb-1.5">
                                  {(
                                    Object.entries(ca.nachweise) as Array<
                                      [Fachplaner, NachweisStatus]
                                    >
                                  ).map(([fp, ns]) => (
                                    <div
                                      key={fp}
                                      className="flex items-center gap-1.5 text-[10px]"
                                    >
                                      <span className="text-slate-700 w-32 truncate">
                                        {FACHPLANER_LABELS[fp]}
                                      </span>
                                      <select
                                        value={ns.status}
                                        onChange={(e) =>
                                          setCodeAssignmentNachweis(
                                            ca.id,
                                            fp,
                                            e.target.value as NachweisStatus["status"]
                                          )
                                        }
                                        className={`text-[10px] border rounded px-1 py-0 ${NACHWEIS_CLASSES[ns.status]} border-slate-200`}
                                      >
                                        {NACHWEIS_STATES.map((st) => (
                                          <option key={st} value={st}>
                                            {NACHWEIS_LABELS[st]}
                                          </option>
                                        ))}
                                      </select>
                                      {/* Sprint Z++ (2026-05-05) — File-Upload pro Nachweis. */}
                                      {ns.fileRef ? (
                                        <>
                                          <a
                                            href={api.multiagentNachweisFileUrl(
                                              runId,
                                              ns.fileRef.pathInRun.replace(
                                                /^nachweise\//,
                                                ""
                                              )
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-700 hover:text-blue-900 truncate max-w-[8rem]"
                                            title={`${ns.fileRef.filename} (${Math.round(
                                              ns.fileRef.sizeBytes / 1024
                                            )} KB)`}
                                          >
                                            📎 {ns.fileRef.filename}
                                          </a>
                                          <button
                                            onClick={async () => {
                                              if (
                                                !window.confirm(
                                                  `Datei "${ns.fileRef!.filename}" löschen?`
                                                )
                                              )
                                                return;
                                              try {
                                                await api.multiagentNachweisDelete(
                                                  runId,
                                                  ca.id,
                                                  fp
                                                );
                                                // Lokal updaten — fileRef raus, status auf fehlt.
                                                setMaterialEditorCodeAssignments(
                                                  (prev) =>
                                                    prev.map((c) =>
                                                      c.id === ca.id
                                                        ? {
                                                            ...c,
                                                            nachweise: {
                                                              ...c.nachweise,
                                                              [fp]: {
                                                                ...(c.nachweise[fp] ?? {}),
                                                                status: "fehlt",
                                                                fileRef: undefined,
                                                              },
                                                            },
                                                          }
                                                        : c
                                                    )
                                                );
                                              } catch (err) {
                                                window.alert(
                                                  "Datei-Löschen fehlgeschlagen: " +
                                                    (err instanceof Error
                                                      ? err.message
                                                      : String(err))
                                                );
                                              }
                                            }}
                                            className="text-slate-400 hover:text-rose-600 px-0.5"
                                            title="Datei löschen"
                                          >
                                            🗑
                                          </button>
                                        </>
                                      ) : (
                                        <label
                                          className="text-slate-500 hover:text-blue-700 cursor-pointer px-0.5"
                                          title="PDF / Bild als Nachweis hochladen"
                                        >
                                          📎
                                          <input
                                            type="file"
                                            accept="application/pdf,image/*"
                                            className="hidden"
                                            onChange={async (e) => {
                                              const file =
                                                e.currentTarget.files?.[0];
                                              if (!file) return;
                                              try {
                                                const result =
                                                  await api.multiagentNachweisUpload(
                                                    runId,
                                                    {
                                                      caId: ca.id,
                                                      fachplaner: fp,
                                                      file,
                                                    }
                                                  );
                                                // Lokal updaten — fileRef + status setzen.
                                                setMaterialEditorCodeAssignments(
                                                  (prev) =>
                                                    prev.map((c) =>
                                                      c.id === ca.id
                                                        ? {
                                                            ...c,
                                                            nachweise: {
                                                              ...c.nachweise,
                                                              [fp]: {
                                                                ...(c.nachweise[fp] ?? {}),
                                                                status:
                                                                  result.status as NachweisStatus["status"],
                                                                fileRef:
                                                                  result.fileRef,
                                                              },
                                                            },
                                                          }
                                                        : c
                                                    )
                                                );
                                              } catch (err) {
                                                const msg =
                                                  err instanceof Error
                                                    ? err.message
                                                    : String(err);
                                                if (msg.includes("404")) {
                                                  window.alert(
                                                    "Bitte zuerst Speichern, dann Datei hochladen — die Code-Zuweisung muss im Backend existieren."
                                                  );
                                                } else {
                                                  window.alert(
                                                    "Upload fehlgeschlagen: " + msg
                                                  );
                                                }
                                              } finally {
                                                e.currentTarget.value = "";
                                              }
                                            }}
                                          />
                                        </label>
                                      )}
                                      <button
                                        onClick={() =>
                                          setCodeAssignmentNachweis(
                                            ca.id,
                                            fp,
                                            null
                                          )
                                        }
                                        className="text-slate-400 hover:text-rose-600 px-0.5"
                                        title="Nachweis entfernen"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              {ALL_FACHPLANER.filter(
                                (fp) => !ca.nachweise[fp]
                              ).length > 0 && (
                                <select
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      setCodeAssignmentNachweis(
                                        ca.id,
                                        e.target.value as Fachplaner,
                                        "fehlt"
                                      );
                                    }
                                  }}
                                  className="text-[10px] border border-slate-300 rounded px-1 py-0.5 bg-white w-full"
                                >
                                  <option value="">
                                    + Fachplaner-Nachweis hinzufügen…
                                  </option>
                                  {ALL_FACHPLANER.filter(
                                    (fp) => !ca.nachweise[fp]
                                  ).map((fp) => (
                                    <option key={fp} value={fp}>
                                      {FACHPLANER_LABELS[fp]}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Sprint 0.19 / Punkt 4 — System-Wand-Vorschlag (DIN 4172).
                Sprint #5 — Plan-Maß-Eingabe wandert in Schritt 1 nach oben
                (über die Wandtyp-Liste); System-Vorschlag wird mit dem
                Plan-Maß zusammen in Schritt 1 angezeigt. */}
            {materialWizardStep === 1 && (
            <div className="p-4 border-b bg-amber-50/50">
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs font-semibold text-slate-700">
                  Plan-Maß Wandstärke:
                </label>
                <input
                  type="number"
                  min={50}
                  max={800}
                  step={5}
                  value={materialEditorPlanThicknessMm}
                  onChange={(e) =>
                    setMaterialEditorPlanThicknessMm(Number(e.target.value))
                  }
                  className="w-24 border rounded px-2 py-1 text-xs font-mono bg-white"
                />
                <span className="text-xs text-slate-500">mm</span>
                <span className="text-[10px] text-slate-400 ml-auto">
                  aus Plan-Bemaßung übernehmen
                </span>
              </div>
              {(() => {
                const matches = findMatchingSystemPresets(
                  materialEditorPlanThicknessMm,
                  20
                );
                if (matches.length === 0) {
                  return (
                    <p className="text-[11px] text-slate-500 italic">
                      Kein DIN-4172-Standardmaß in der Nähe von{" "}
                      {materialEditorPlanThicknessMm} mm — Schichten manuell
                      hinzufügen.
                    </p>
                  );
                }
                return (
                  <div>
                    <p className="text-[11px] text-amber-800 mb-2">
                      <strong>Systemwand-Vorschlag</strong> nach DIN 4172
                      (oktametrisches System). Klick übernimmt den typischen
                      Aufbau — danach kannst du die Schichten anpassen.
                    </p>
                    <div className="space-y-1.5">
                      {matches.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => applySystemWallPreset(p)}
                          className="w-full text-left bg-white hover:bg-amber-100 border border-amber-300 rounded p-2 transition-colors"
                        >
                          <div className="text-xs font-semibold text-amber-900">
                            {p.label}
                          </div>
                          <div className="text-[11px] text-slate-600 mt-0.5">
                            {p.description}
                          </div>
                          {p.norm && (
                            <div className="text-[10px] text-slate-400 mt-0.5">
                              {p.norm}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            )}

            {/* Sprint #5 — Schritt 2: Aufbau prüfen.
                Read-only-Summary der Schichten ist der Default; das Akkordeon
                "individuell anpassen" zeigt die alte Editor-Liste mit Add-
                Buttons. Standard-User können einfach Weiter klicken; Power-
                User passen frei an. */}
            {materialWizardStep === 2 && (
            <>
              {/* Read-only Summary — kompakte Stack-Vorschau */}
              <div className="p-4 border-b bg-amber-50/30">
                <div className="text-xs font-semibold text-slate-700 mb-2">
                  Aufbau (Wand-Stack)
                </div>
                {materialEditorLayers.length === 0 ? (
                  <p className="text-[11px] text-slate-500 italic">
                    Noch keine Schichten — gehe zurück nach Schritt 1 und wähle
                    einen Wandtyp, oder klappe „individuell anpassen" auf.
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {materialEditorLayers.map((layer, i) => {
                      const icon =
                        layer.gewerk === "rohbau"
                          ? "🟧"
                          : layer.gewerk === "putz"
                            ? "🟦"
                            : layer.gewerk === "trockenbau"
                              ? "🟦"
                              : "🟪";
                      const presetLabel =
                        layer.preset === "mauerwerk"
                          ? "Mauerwerk"
                          : layer.preset === "beton"
                            ? "Beton"
                            : layer.preset === "trockenbau"
                              ? "Trockenbau"
                              : "Custom";
                      const sides =
                        layer.gewerk === "putz"
                          ? layer.putzSides === 2
                            ? " · beidseitig"
                            : " · 1-seitig"
                          : "";
                      return (
                        <li
                          key={i}
                          className="text-[11px] text-slate-700 flex items-baseline gap-1.5"
                        >
                          <span>{icon}</span>
                          <span className="capitalize font-semibold">
                            {layer.gewerk}
                          </span>
                          <span className="text-slate-500">·</span>
                          <span>
                            {layer.materialName ?? presetLabel}
                            {sides}
                          </span>
                          <span className="ml-auto font-mono text-slate-500">
                            {layer.dickeMm} mm
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {/* Akkordeon: individuell anpassen */}
              <div className="p-4 border-b">
                <button
                  onClick={() => setMaterialWizardLayersOpen((v) => !v)}
                  className="flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-slate-900"
                >
                  <span className="text-[10px]">
                    {materialWizardLayersOpen ? "▾" : "▸"}
                  </span>
                  Aufbau individuell anpassen
                </button>
                {!materialWizardLayersOpen && (
                  <p className="text-[10px] text-slate-400 italic mt-1">
                    Default-Aufbau aus dem Wandtyp wird übernommen. Nur
                    aufklappen wenn du Schichten hinzufügen oder Stärken ändern
                    willst.
                  </p>
                )}
              </div>
            </>
            )}

            {materialWizardStep === 2 && materialWizardLayersOpen && (
            <>
            <div className="p-4 space-y-2">
              {materialEditorLayers.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-4 text-center">
                  Keine Schichten — z.B. nur Sicht-Mauerwerk oder nur Putz hinzufügen.
                </p>
              ) : (
                materialEditorLayers.map((layer, i) => (
                  <div
                    key={i}
                    className={`grid grid-cols-12 gap-2 items-center p-2 rounded border ${
                      layer.gewerk === "rohbau"
                        ? "bg-orange-50 border-orange-200"
                        : "bg-blue-50 border-blue-200"
                    }`}
                  >
                    <select
                      value={layer.gewerk}
                      onChange={(e) =>
                        updateMaterialLayer(i, {
                          gewerk: e.target.value as LayerForm["gewerk"],
                        })
                      }
                      className="col-span-3 border rounded px-1 py-1 text-xs bg-white"
                    >
                      <option value="rohbau">Rohbau</option>
                      <option value="putz">Putz</option>
                      <option value="trockenbau">Trockenbau</option>
                    </select>
                    <select
                      value={layer.preset}
                      onChange={(e) =>
                        updateMaterialLayer(i, {
                          preset: e.target.value as LayerForm["preset"],
                        })
                      }
                      className="col-span-3 border rounded px-1 py-1 text-xs bg-white"
                    >
                      <option value="mauerwerk">Mauerwerk</option>
                      <option value="beton">Beton</option>
                      <option value="trockenbau">Trockenbau</option>
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Name (optional)"
                      value={layer.materialName ?? ""}
                      onChange={(e) =>
                        updateMaterialLayer(i, {
                          materialName: e.target.value || undefined,
                        })
                      }
                      className="col-span-3 border rounded px-1 py-1 text-xs bg-white"
                    />
                    <input
                      type="number"
                      min={1}
                      max={500}
                      value={layer.dickeMm}
                      onChange={(e) =>
                        updateMaterialLayer(i, {
                          dickeMm: Number(e.target.value),
                        })
                      }
                      className="col-span-2 border rounded px-1 py-1 text-xs bg-white text-right font-mono"
                    />
                    <button
                      onClick={() => removeMaterialLayer(i)}
                      className="col-span-1 text-red-600 hover:text-red-800 text-lg leading-none"
                      title="Schicht entfernen"
                    >
                      ×
                    </button>
                    {layer.gewerk === "putz" && (
                      <div className="col-span-12 flex items-center gap-2 text-[10px] text-slate-500 mt-0.5 pl-1">
                        <span>Putz-Seiten:</span>
                        <select
                          value={layer.putzSides ?? 1}
                          onChange={(e) =>
                            updateMaterialLayer(i, {
                              putzSides: Number(e.target.value) as 1 | 2,
                            })
                          }
                          className="border rounded px-1 py-0.5 text-[10px] bg-white"
                        >
                          <option value={1}>1 Seite (nur dieser Raum)</option>
                          <option value={2}>
                            2 Seiten (beide Raum-Innenseiten)
                          </option>
                        </select>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t bg-slate-50 flex flex-wrap gap-2 items-center">
              <span className="text-[11px] text-slate-500 mr-1">
                Schicht hinzufügen:
              </span>
              <button
                onClick={() => addMaterialLayer("rohbau")}
                className="text-xs px-2 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 rounded"
              >
                + Rohbau (Mauerwerk 175mm)
              </button>
              <button
                onClick={() => addMaterialLayer("putz")}
                className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300 rounded"
              >
                + Putz (15mm, 1 Seite)
              </button>
              <button
                onClick={() => addMaterialLayer("trockenbau")}
                className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300 rounded"
              >
                + Trockenbau (100mm)
              </button>
              <button
                onClick={() => addMaterialLayer("oberflaeche")}
                className="text-xs px-2 py-1 bg-purple-100 hover:bg-purple-200 text-purple-800 border border-purple-300 rounded"
                title="Tapete / Anstrich / Lasur — m²-basiert, ohne Volumen-Beitrag"
              >
                + Oberfläche (Tapete 0,3mm)
              </button>
            </div>
            </>
            )}

            {/* Sprint #5 — Schritt 4: Oberflächen.
                Tapete / Anstrich / Fliesen jeweils mit Ja/Nein-Toggle; bei
                "Ja" erscheint ein Code-Picker mit den passenden Codes aus
                dem Catalog (Filter über GewerkPrefix). User wählt 1 Code pro
                Slot — der wird beim Save als oberflaeche-Layer ans Material-
                Stack angehängt. */}
            {materialWizardStep === 4 && (
              <div className="p-4 border-b space-y-3">
                <div className="text-[11px] text-slate-600 italic mb-1 leading-relaxed">
                  Wandseiten-Oberflächen optional setzen. Wenn nichts
                  gewählt ist, bleibt die Wand „roh" + ggf. Putz aus dem
                  Aufbau (Schritt 2).
                </div>
                {(["tapete", "anstrich", "fliesen"] as const).map((slot) => {
                  const slotLabel: Record<typeof slot, string> = {
                    tapete: "Tapete",
                    anstrich: "Anstrich",
                    fliesen: "Fliesen",
                  };
                  const slotIcon: Record<typeof slot, string> = {
                    tapete: "🧱",
                    anstrich: "🎨",
                    fliesen: "🟦",
                  };
                  const slotPrefix: Record<typeof slot, RegExp> = {
                    tapete: /tapete|raufaser/i,
                    anstrich: /anstrich|dispersion|lasur/i,
                    fliesen: /fliese/i,
                  };
                  const isOn = materialWizardSurfaceOn[slot];
                  const code = materialWizardSurfaceCode[slot];
                  // Catalog-Codes für diesen Slot — gefiltert über shortLabel
                  // (Pattern-basiert; deckt verschiedene WA-/OB-Prefixes).
                  const candidates = (DEFAULT_CATALOG as CatalogEntry[]).filter(
                    (e) =>
                      slotPrefix[slot].test(
                        `${e.code} ${e.shortLabel}`
                      )
                  );
                  return (
                    <div
                      key={slot}
                      className={`rounded border ${
                        isOn
                          ? "bg-purple-50 border-purple-200"
                          : "bg-slate-50 border-slate-200"
                      } p-2.5`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{slotIcon[slot]}</span>
                        <span className="text-xs font-semibold text-slate-700">
                          {slotLabel[slot]}?
                        </span>
                        <div className="ml-auto inline-flex bg-white rounded-md p-0.5 border border-slate-300">
                          {(
                            [
                              { v: true, label: "Ja" },
                              { v: false, label: "Nein" },
                            ] as const
                          ).map((opt) => (
                            <button
                              key={String(opt.v)}
                              onClick={() => {
                                setMaterialWizardSurfaceOn((prev) => ({
                                  ...prev,
                                  [slot]: opt.v,
                                }));
                                if (!opt.v) {
                                  setMaterialWizardSurfaceCode((prev) => ({
                                    ...prev,
                                    [slot]: null,
                                  }));
                                }
                              }}
                              className={`px-3 py-1 text-[11px] rounded transition-colors ${
                                isOn === opt.v
                                  ? opt.v
                                    ? "bg-purple-600 text-white font-semibold"
                                    : "bg-slate-600 text-white font-semibold"
                                  : "text-slate-600 hover:bg-slate-100"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {isOn && (
                        <div className="mt-2 pl-6 border-l-2 border-purple-200">
                          <label className="text-[10px] text-slate-500 block mb-1">
                            Code aus Katalog wählen:
                          </label>
                          <select
                            value={code ?? ""}
                            onChange={(e) =>
                              setMaterialWizardSurfaceCode((prev) => ({
                                ...prev,
                                [slot]: e.target.value || null,
                              }))
                            }
                            className="w-full border rounded px-2 py-1 text-xs bg-white"
                          >
                            <option value="">— bitte wählen —</option>
                            {candidates.map((c) => (
                              <option key={c.code} value={c.code}>
                                {c.code} · {c.shortLabel}
                              </option>
                            ))}
                          </select>
                          {candidates.length === 0 && (
                            <p className="text-[10px] text-amber-700 mt-1">
                              Keine Codes für {slotLabel[slot]} im aktuellen
                              Catalog. Sprint G2/B-Reduced erweitert die Liste —
                              bei Bedarf manuell ergänzen.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sprint #5 — Wizard-Footer: Stepper-Navigation Zurück/Weiter
                bzw. Speichern auf S4. Primary-Button rechts (HIG: einer pro
                Screen), Abbrechen wandert in Header oder ggf. ganz weg
                (User klickt das Modal-Backdrop um abzubrechen). */}
            <div className="p-4 border-t bg-slate-50 flex items-center gap-2">
              <button
                onClick={closeMaterialEditor}
                className="text-[11px] px-2 py-1.5 text-slate-500 hover:text-slate-800"
              >
                Abbrechen
              </button>
              <div className="ml-auto flex items-center gap-2">
                {materialWizardStep > 1 && (
                  <button
                    onClick={() =>
                      setMaterialWizardStep(
                        (s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4) : s)
                      )
                    }
                    className="text-xs px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded"
                  >
                    ← Zurück
                  </button>
                )}
                {materialWizardStep < 4 ? (
                  <button
                    onClick={() =>
                      setMaterialWizardStep(
                        (s) => (s < 4 ? ((s + 1) as 1 | 2 | 3 | 4) : s)
                      )
                    }
                    className="text-xs px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-semibold"
                  >
                    Weiter →
                  </button>
                ) : (
                  <button
                    onClick={saveMaterialEditor}
                    disabled={wallEditSaving}
                    className="text-xs px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 font-semibold"
                  >
                    {wallEditSaving ? "Speichert…" : "✓ Speichern"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ScaleSetter
        open={scaleSetterOpen}
        runId={runId}
        currentRatio={
          bundle.classification.pages[0]?.scale?.ratio ?? 100
        }
        prefilledTwoPoint={scalePicked}
        onClose={() => {
          setScaleSetterOpen(false);
          setScalePicked(null);
          setScalePickStart(null);
        }}
        onRequestTwoPointPicking={() => {
          setScaleSetterOpen(false);
          setScalePicked(null);
          setScalePickStart(null);
          setEditMode("scalePicking");
        }}
        onSuccess={async () => {
          // Bundle neu laden — neuer ratio wirkt sich auf alle Mengen aus
          const fresh = await loadBundle(runId);
          setBundle(fresh);
        }}
      />

      {/* Sprint 0.20 — Tür/Fenster-Editor-Modal */}
      {openingEditorOpen && openingEditorTarget && (
        <div
          className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4"
          onClick={closeOpeningEditor}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="text-lg font-semibold">
                {openingEditorTarget.idx === null
                  ? `${openingEditorType === "door" ? "🚪 Tür" : "🪟 Fenster"} hinzufügen`
                  : `${openingEditorType === "door" ? "🚪 Tür" : "🪟 Fenster"} editieren`}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Polygon: {openingEditorTarget.polygonId}
              </p>
            </div>

            {openingEditorTarget.idx === null && (
              <div className="flex gap-2">
                {(["window", "door"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setOpeningEditorType(t);
                      setOpeningEditorFields((f) => ({
                        ...f,
                        widthM: t === "door" ? "0.885" : "1.26",
                        heightM: t === "door" ? "2.01" : "1.385",
                        bruestungM: t === "window" ? "0.90" : "",
                      }));
                    }}
                    className={`flex-1 px-3 py-2 text-sm rounded border ${
                      openingEditorType === t
                        ? "bg-brand-600 text-white border-brand-600"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    {t === "door" ? "🚪 Tür" : "🪟 Fenster"}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Breite (m)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={openingEditorFields.widthM}
                  onChange={(e) =>
                    setOpeningEditorFields((f) => ({
                      ...f,
                      widthM: e.target.value,
                    }))
                  }
                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={openingEditorType === "door" ? "0.885" : "1.26"}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Höhe (m)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={openingEditorFields.heightM}
                  onChange={(e) =>
                    setOpeningEditorFields((f) => ({
                      ...f,
                      heightM: e.target.value,
                    }))
                  }
                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={openingEditorType === "door" ? "2.01" : "1.385"}
                />
              </div>
              {openingEditorType === "window" && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Brüstung (m)
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={openingEditorFields.bruestungM}
                    onChange={(e) =>
                      setOpeningEditorFields((f) => ({
                        ...f,
                        bruestungM: e.target.value,
                      }))
                    }
                    className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="0.90"
                  />
                </div>
              )}
              <div className={openingEditorType === "window" ? "" : "col-span-2"}>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Label (optional)
                </label>
                <input
                  type="text"
                  value={openingEditorFields.label}
                  onChange={(e) =>
                    setOpeningEditorFields((f) => ({
                      ...f,
                      label: e.target.value,
                    }))
                  }
                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="z.B. F1, T-Schlafen"
                />
              </div>
            </div>

            {(() => {
              const w = Number(openingEditorFields.widthM.replace(",", "."));
              const h = Number(openingEditorFields.heightM.replace(",", "."));
              if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                return (
                  <div className="text-xs bg-slate-50 border rounded p-2 text-slate-700">
                    Fläche: {(w * h).toFixed(2)} m²
                  </div>
                );
              }
              return null;
            })()}

            {openingEditorError && (
              <div role="alert" className="text-sm bg-red-50 border border-red-200 text-red-800 rounded p-2">
                {openingEditorError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={closeOpeningEditor}
                disabled={openingEditorSaving}
                className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={saveOpeningEditor}
                disabled={openingEditorSaving}
                className="px-4 py-1.5 text-sm rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {openingEditorSaving
                  ? "Speichert…"
                  : openingEditorTarget.idx === null
                    ? "Anlegen"
                    : "Speichern"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
